import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import NotificationBell from '@/components/NotificationBell';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Search, FileText, Download, Trash2, Upload, File, 
  Image, Eye, FolderOpen, User, Settings, LogOut, Gavel,
  Clock, CheckCircle2, XCircle, Users, Calendar, ChevronRight, Filter,
  MoreVertical, Send, Award, BarChart3, Building2, Sparkles, X, Menu,
  Zap, FileCheck, DollarSign, Paperclip, HelpCircle, Loader2,
  ArrowRight, CheckSquare, UserPlus, FileSpreadsheet, Landmark, Wand2,
  Edit2, Phone, Mail, ExternalLink, ChevronDown, ChevronUp, Copy, Scale, Bell, Shield,
  ClipboardCheck, AlertCircle, LayoutDashboard, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import TourLauncher from '@/components/TourLauncher';
import { toast } from 'sonner';
import { apiService, api } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { PERMISSIONS, hasPermission } from '@/lib/permissions';
import InviteContractors from '@/components/InviteContractors';
import PipelineGrid from '@/components/PipelineGrid';
import BidSheetAIWizard from '@/components/BidSheetAIWizard';
import SignContractDialog from '@/components/SignContractDialog';
import NewBidProjectModal from '@/components/NewBidProjectModal';
import ProjectBidWorkspaceHeader from '@/components/ProjectBidWorkspaceHeader';
import ContractorBidPreviewModal from '@/components/ContractorBidPreviewModal';
import { isV255User } from '@/lib/featureFlags';

function ProjectHub({ user, onLogout, contractorsOnly = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const bidUploadRef = useRef(null);
  const docInputRef = useRef(null);
  
  // Handle tab from URL query param
  const tabFromUrl = searchParams.get('tab');
  const projectFromUrl = searchParams.get('project');
  const [activeTab, setActiveTab] = useState(contractorsOnly ? 'contractors' : (tabFromUrl || 'pipeline'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [activeTab]);
  
  // Role-based permissions state
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamRoles, setTeamRoles] = useState([]);
  
  // Permission check helper
  const can = (permission) => {
    if (!isTeamMember) return true; // Owner has all permissions
    return hasPermission(teamRoles, permission);
  };
  
  // Update active tab when URL param changes
  useEffect(() => {
    if (contractorsOnly) { setActiveTab('contractors'); return; }
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl, contractorsOnly]);

  // Pre-set the bid-requests project filter from ?project=... (used by Pipeline tile deep-links)
  useEffect(() => {
    if (projectFromUrl) {
      setBidFilterProject(projectFromUrl);
    }
  }, [projectFromUrl]);

  // Guided hand-off from the dashboard "not funded" path:
  // ?intro=bidding auto-opens the 9-step "how bidding works" popup; its CTA
  // then opens the create-project-for-bid modal. ?new=1 opens it directly.
  const [showBiddingModal, setShowBiddingModal] = useState(false);
  const openCreateBidFlow = () => {
    // v2.5.5 users no longer have a global "Bid Requests" tab — bids are
    // project-scoped and created via the "new project for bid" modal, launched
    // from the Pipeline. Older users still use the legacy Bid Requests tab.
    if (isV255User(user)) {
      setActiveTab('pipeline');
      setShowNewBidProjectModal(true);
    } else {
      setActiveTab('bids');
      setShowCreateBid(true);
    }
  };
  useEffect(() => {
    const intro = searchParams.get('intro');
    const openNew = searchParams.get('new');
    if (intro !== 'bidding' && openNew !== '1') return;
    if (intro === 'bidding') setShowBiddingModal(true);
    if (openNew === '1') openCreateBidFlow();
    const next = new URLSearchParams(searchParams);
    next.delete('intro');
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams]);
  
  // Data states
  const [documents, setDocuments] = useState([]);
  const [bidRequests, setBidRequests] = useState([]);
  const [bidSheets, setBidSheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [bankTemplates, setBankTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDocType, setFilterDocType] = useState('all');
  const [selectedTrade, setSelectedTrade] = useState('all');
  const [bidFilterProject, setBidFilterProject] = useState('all'); // Filter bid requests by project

  // v2.5.5 — contractor bid preview modal ("what your contractor sees")
  const [previewBidId, setPreviewBidId] = useState(null);
  // v2.5.5 — pipeline stage per project (funded projects get a read-only bid workspace)
  const [pipelineStages, setPipelineStages] = useState({});
  
  // Dialog states
  const [showCreateBid, setShowCreateBid] = useState(false);
  // v2.5: feature-flagged new bid-flow modal (Project Info → Scopes → Publish)
  const [showNewBidProjectModal, setShowNewBidProjectModal] = useState(false);
  const [showUploadBid, setShowUploadBid] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [showEditContractor, setShowEditContractor] = useState(false);
  const [showContractorPortal, setShowContractorPortal] = useState(false);
  const [showInviteContractors, setShowInviteContractors] = useState(false);
  const [showLogPayment, setShowLogPayment] = useState(false);
  const [show1099Dialog, setShow1099Dialog] = useState(false);
  const [selected1099Year, setSelected1099Year] = useState(new Date().getFullYear());
  const [isAIWizardOpen, setIsAIWizardOpen] = useState(false); // AI Bid Sheet Wizard
  const [available1099Years, setAvailable1099Years] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState(null);
  // Contractor W-9 / COI document upload (from compliance badges)
  const [showDocUploadDialog, setShowDocUploadDialog] = useState(false);
  const [docUploadType, setDocUploadType] = useState('w9'); // 'w9' | 'coi'
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docCoiExpiration, setDocCoiExpiration] = useState('');
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [expandedContractors, setExpandedContractors] = useState({});
  const [contractorPayments, setContractorPayments] = useState([]);
  const [availableTrades, setAvailableTrades] = useState([]);
  const [newTradeName, setNewTradeName] = useState('');
  
  // Import to Bid Sheet modal state
  const [showImportBidSheetModal, setShowImportBidSheetModal] = useState(false);
  const [importBidRequest, setImportBidRequest] = useState(null);
  const [existingBidSheets, setExistingBidSheets] = useState([]);
  const [selectedBidSheetId, setSelectedBidSheetId] = useState('new');
  const [importCategory, setImportCategory] = useState('');
  const [importSubcategory, setImportSubcategory] = useState('');
  const [importItemType, setImportItemType] = useState('labor');
  const [customCategory, setCustomCategory] = useState('');

  // Manual bid entry (owner records an offline bid on a contractor's behalf)
  const [showManualBidModal, setShowManualBidModal] = useState(false);
  const [manualBidRequest, setManualBidRequest] = useState(null);
  const [manualBidSaving, setManualBidSaving] = useState(false);
  const [manualBidForm, setManualBidForm] = useState({
    contractor_id: 'new', contractor_company: '', contractor_name: '',
    contractor_phone: '', total_amount: '', estimated_days: '', notes: '',
  });

  const handleOpenManualBid = (bid) => {
    setManualBidRequest(bid);
    setManualBidForm({
      contractor_id: 'new', contractor_company: '', contractor_name: '',
      contractor_phone: '', total_amount: '', estimated_days: '', notes: '',
    });
    setShowManualBidModal(true);
  };

  const handleSubmitManualBid = async () => {
    if (!user?.id || !manualBidRequest) return;
    const amount = parseFloat(manualBidForm.total_amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid bid amount');
      return;
    }
    const usingExisting = manualBidForm.contractor_id && manualBidForm.contractor_id !== 'new';
    if (!usingExisting && !manualBidForm.contractor_company.trim()) {
      toast.error('Select a contractor or enter a company name');
      return;
    }
    setManualBidSaving(true);
    try {
      await apiService.createManualBid(manualBidRequest.id, user.id, {
        contractor_id: usingExisting ? manualBidForm.contractor_id : null,
        contractor_company: usingExisting ? null : manualBidForm.contractor_company.trim(),
        contractor_name: usingExisting ? null : (manualBidForm.contractor_name.trim() || null),
        contractor_phone: usingExisting ? null : (manualBidForm.contractor_phone.trim() || null),
        total_amount: amount,
        estimated_days: manualBidForm.estimated_days ? parseInt(manualBidForm.estimated_days, 10) : null,
        notes: manualBidForm.notes.trim() || null,
      });
      toast.success('Bid recorded');
      setShowManualBidModal(false);
      // Refresh bid requests so the response count / bids update
      try {
        const res = await apiService.getBidRequests(user.id);
        setBidRequests(res.data || []);
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Manual bid failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to record bid');
    } finally {
      setManualBidSaving(false);
    }
  };

  const [showCustomCategoryInput, setShowCustomCategoryInput] = useState(false);
  const [importMethod, setImportMethod] = useState('combined'); // 'combined' or 'separate'
  const [importLineItems, setImportLineItems] = useState([]); // For separate import with editable types
  
  // Award Bid modal state
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [awardingBidRequest, setAwardingBidRequest] = useState(null);
  const [selectedWinningResponse, setSelectedWinningResponse] = useState(null);
  const [awardContract, setAwardContract] = useState(null);
  const [isAwarding, setIsAwarding] = useState(false);
  
  // Revoke/Transfer Award state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [revokingBidRequest, setRevokingBidRequest] = useState(null);

  // Issue / Reissue Contract modal state
  const [issueContractBid, setIssueContractBid] = useState(null);
  const [issueContractForm, setIssueContractForm] = useState({
    contract_amount: '',
    scope_of_work: '',
    start_date: '',
    end_date: '',
    send_email: true,
    include_rules: true,
  });
  const [issueContractLoading, setIssueContractLoading] = useState(false);
  const [projectContracts, setProjectContracts] = useState([]);
  const [loadingProjectContracts, setLoadingProjectContracts] = useState(false);
  const [countersignContract, setCountersignContract] = useState(null);

  const handleOpenIssueContract = (bid) => {
    setIssueContractBid(bid);
    const awarded = bid?.awarded_response || {};
    setIssueContractForm({
      contract_amount: awarded.total_amount || '',
      scope_of_work: bid?.scope_of_work || '',
      start_date: bid?.start_date || '',
      end_date: bid?.end_date || '',
      send_email: true,
      include_rules: true,
    });
    // Pull existing contracts for this bid's contractor + project
    if (bid?.project_id && bid?.awarded_contractor_id) {
      setLoadingProjectContracts(true);
      api
        .get(`/projects/${bid.project_id}/contracts?contractor_id=${bid.awarded_contractor_id}`)
        .then(r => setProjectContracts(r.data || []))
        .catch(() => setProjectContracts([]))
        .finally(() => setLoadingProjectContracts(false));
    }
  };

  const handleIssueContract = async () => {
    if (!issueContractBid) return;
    setIssueContractLoading(true);
    try {
      const body = {
        project_id: issueContractBid.project_id,
        contractor_id: issueContractBid.awarded_contractor_id,
        bid_request_id: issueContractBid.id,
        contract_amount: issueContractForm.contract_amount ? parseFloat(issueContractForm.contract_amount) : null,
        scope_of_work: issueContractForm.scope_of_work || null,
        start_date: issueContractForm.start_date || null,
        end_date: issueContractForm.end_date || null,
        include_rules: issueContractForm.include_rules,
        send_email: issueContractForm.send_email,
      };
      const res = await api.post(`/projects/${issueContractBid.project_id}/contracts/issue`, body);
      const status = res.data?.email_status;
      const summary = !issueContractForm.send_email
        ? 'Contract saved to portal (no email sent)'
        : status === 'sent'
          ? 'Contract generated, emailed, and stored in portal'
          : status === 'failed'
            ? 'Contract stored, but email failed to send — try again'
            : 'Contract stored';
      toast.success(summary);
      setIssueContractBid(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not issue contract');
    } finally {
      setIssueContractLoading(false);
    }
  };
  const [revokeReason, setRevokeReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  const [transferTargetResponse, setTransferTargetResponse] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  
  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messagingContractor, setMessagingContractor] = useState(null);
  const [messagingBidRequest, setMessagingBidRequest] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [messageThread, setMessageThread] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // Payment form state
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    notes: ''
  });
  
  // Alerts/Notifications state
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [showAlertsDropdown, setShowAlertsDropdown] = useState(false);
  
  // Reviews Tab state
  const [pendingReviews, setPendingReviews] = useState({ pending_invoices: [], pending_w9s: [], pending_cois: [], total_pending: 0 });
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingItem, setRejectingItem] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Messages Tab state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');
  const [isSendingNewMessage, setIsSendingNewMessage] = useState(false);
  const [contactableContractors, setContactableContractors] = useState([]);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Form states
  const [newBidRequest, setNewBidRequest] = useState({
    project_id: '',
    title: '',
    scope_of_work: '',
    due_date: '',
    trade_type: '',
    contractor_ids: [],
    document_ids: [],
    notes: ''
  });
  
  const [newContractor, setNewContractor] = useState({
    company: '', name: '', email: '', phone: '', trade: '', specialty: ''
  });
  
  const [uploadBidData, setUploadBidData] = useState({
    project_id: '', contractor_id: '', trade_type: '', file: null, total_amount: '', notes: '', line_items: []
  });
  // When set (to an existing bid_request id), an uploaded bid is filed UNDER
  // that prepared scope instead of creating a brand-new standalone bid.
  const [uploadTargetBidRequestId, setUploadTargetBidRequestId] = useState('');
  const [isExtractingBid, setIsExtractingBid] = useState(false);
  const [extractedBidData, setExtractedBidData] = useState(null);
  
  // Quick Add Contractor state (for bid upload)
  const [showQuickAddContractor, setShowQuickAddContractor] = useState(false);
  const [quickAddContractor, setQuickAddContractor] = useState({
    company: '', name: '', email: '', phone: '', trade: ''
  });
  const [isAddingContractor, setIsAddingContractor] = useState(false);
  
  // Multi-file bid combining states
  const [multiFileMode, setMultiFileMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedFilesData, setExtractedFilesData] = useState([]);
  const [combineFiles, setCombineFiles] = useState(true);
  
  // Multi-trade bid states
  const [multiTradeMode, setMultiTradeMode] = useState(false);
  const [bidUnits, setBidUnits] = useState(1);
  const [projectUnits, setProjectUnits] = useState(1);
  const [selectedTradeGroups, setSelectedTradeGroups] = useState([]);
  const [showTradeComparison, setShowTradeComparison] = useState(false);
  const [tradeComparisonData, setTradeComparisonData] = useState(null);
  const [selectedComparisonTrade, setSelectedComparisonTrade] = useState('');
  
  // Edit bid request states
  const [showEditBid, setShowEditBid] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [editBidData, setEditBidData] = useState({
    title: '',
    trade_type: '',
    contractor_id: '',
    project_id: '',
    notes: '',
    total_amount: '',
    line_items: []
  });
  
  // Quick Add Trade state
  const [showQuickAddTrade, setShowQuickAddTrade] = useState(false);
  const [quickAddTradeName, setQuickAddTradeName] = useState('');
  const [isAddingTrade, setIsAddingTrade] = useState(false);
  
  // Scope Templates state
  const [scopeTemplates, setScopeTemplates] = useState([]);
  const [showScopeTemplateManager, setShowScopeTemplateManager] = useState(false);
  const [editingScopeTemplate, setEditingScopeTemplate] = useState(null);
  const [newScopeTemplate, setNewScopeTemplate] = useState({ name: '', trade_type: '', scope_text: '', description: '' });
  const [isSavingScopeTemplate, setIsSavingScopeTemplate] = useState(false);
  
  const [uploadProject, setUploadProject] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadFile, setUploadFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // AI Takeoff state
  const [takeoffPlanFile, setTakeoffPlanFile] = useState(null);
  const [takeoffResult, setTakeoffResult] = useState(null);
  const [isAnalyzingTakeoff, setIsAnalyzingTakeoff] = useState(false);
  const [showTakeoffModal, setShowTakeoffModal] = useState(false);
  const [takeoffSpecs, setTakeoffSpecs] = useState({
    ceiling_height: '9',
    wall_thickness: '1/2"',
    ceiling_thickness: '5/8"',
    garage_thickness: '5/8" Type X',
    include_garage: true,
    include_stairs: true,
    exclude_basement: true,
    sheet_sizes: 'both' // 'standard', 'wide', 'both'
  });
  const [takeoffPrompt, setTakeoffPrompt] = useState(''); // Free-form user prompt
  const [showHelperFields, setShowHelperFields] = useState(false); // Toggle for optional fields
  
  // PDF Page Selector state - now supports multi-select
  const [pdfPages, setPdfPages] = useState([]); // Array of page thumbnails
  const [selectedPages, setSelectedPages] = useState([]); // Array of selected page numbers (1-indexed)
  const [isLoadingPdfPreview, setIsLoadingPdfPreview] = useState(false);
  const MAX_PAGES_TO_SELECT = 4; // Limit to prevent API overload

  // Send to More Contractors modal state
  const [showSendToMoreModal, setShowSendToMoreModal] = useState(false);
  const [selectedBidRequestForSend, setSelectedBidRequestForSend] = useState(null);
  const [selectedContractorsToSend, setSelectedContractorsToSend] = useState([]);
  const [isSendingToMore, setIsSendingToMore] = useState(false);

  // Quick Bid Invite state
  const [showQuickBidInvite, setShowQuickBidInvite] = useState(false);
  const [quickInviteData, setQuickInviteData] = useState({
    project_id: '',
    contractor_id: '',
    trade_type: '',
    message: ''
  });
  const [isSendingQuickInvite, setIsSendingQuickInvite] = useState(false);
  const [quickBidInvites, setQuickBidInvites] = useState([]);
  const [showQuickInviteHistory, setShowQuickInviteHistory] = useState(false);

  const defaultTrades = ['All Trades', 'General', 'Framing', 'Electrical', 'Plumbing', 'HVAC', 'Concrete', 'Roofing', 'Drywall', 'Painting', 'Flooring', 'Tile', 'Cabinetry', 'Landscaping', 'Windows & Doors', 'Insulation', 'Siding', 'Gutters', 'Garage Doors', 'Excavation', 'Foundation', 'Masonry'];
  const trades = availableTrades.length > 0 ? ['All Trades', ...availableTrades] : defaultTrades;

  // Category to Subcategory mapping for bid sheet import
  const categorySubcategories = {
    'Site Work': ['Survey & Staking', 'Site Preparation', 'Utilities'],
    'Foundation': ['Footings', 'Walls', 'Waterproofing'],
    'Concrete & Slab': ['Plumbing', 'Slab Work'],
    'Framing': ['Structure', 'Materials'],
    'Exterior': ['Windows & Doors', 'Roofing', 'Siding & Trim'],
    'Rough-Ins': ['Plumbing', 'HVAC', 'Electrical'],
    'Insulation & Drywall': ['Insulation', 'Drywall'],
    'Interior Finishes': ['Trim & Doors', 'Paint', 'Flooring', 'Cabinets & Counters'],
    'Final Fixtures': ['Plumbing Fixtures', 'Electrical Fixtures', 'Appliances'],
    'Final & Cleanup': ['Inspections', 'Cleanup'],
    'Flooring': ['Hardwood', 'Tile', 'Carpet', 'LVP'],
    'Painting': ['Interior Paint', 'Exterior Paint'],
    'Other': ['General'],
  };

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Check team membership for permissions
      try {
        const accessRes = await apiService.getTeamAccess(user.id);
        if (accessRes.data && accessRes.data.length > 0) {
          // User is a team member - get their roles
          const access = accessRes.data[0]; // Primary access
          const roles = access.roles && access.roles.length > 0 
            ? access.roles 
            : [access.role || 'accountant'];
          setTeamRoles(roles);
          setIsTeamMember(true);
        } else {
          setIsTeamMember(false);
          setTeamRoles([]);
        }
      } catch (e) {
        // Not a team member - user is owner
        setIsTeamMember(false);
        setTeamRoles([]);
      }
      
      const [projectsRes, contractorsRes, bidRequestsRes, bidSheetsRes, bankTemplatesRes, paymentsRes, tradesRes, quickInvitesRes, scopeTemplatesRes] = await Promise.all([
        apiService.getProjects(user.id),
        apiService.getContractorsByUser(user.id),
        apiService.getBidRequests(user.id),
        apiService.getBidSheets(user.id).catch(() => ({ data: [] })),
        apiService.getBankTemplates(user.id).catch(() => ({ data: [] })),
        apiService.getContractorPayments(user.id).catch(() => ({ data: [] })),
        apiService.getTrades(user.id).catch(() => ({ data: { trades: [] } })),
        apiService.getQuickBidInvites(user.id).catch(() => ({ data: [] })),
        apiService.getScopeTemplates(user.id).catch(() => ({ data: { templates: [] } }))
      ]);
      
      setProjects(projectsRes.data);
      setContractors(contractorsRes.data);
      setBidRequests(bidRequestsRes.data);
      setBidSheets(bidSheetsRes.data || []);
      setBankTemplates(bankTemplatesRes.data || []);
      setContractorPayments(paymentsRes.data || []);
      if (tradesRes.data?.trades) {
        setAvailableTrades(tradesRes.data.trades.filter(t => t !== 'All Trades'));
      }
      setQuickBidInvites(quickInvitesRes.data || []);
      setScopeTemplates(scopeTemplatesRes.data?.templates || []);

      // v2.5.5 — pull computed pipeline stages so we can lock the bid workspace
      // once a project is funded (bids move to the active build / Budget & Progress).
      try {
        const pipeRes = await api.get('/pipeline/projects', { params: { owner_id: user.id } });
        const stageMap = {};
        (pipeRes.data || []).forEach((p) => { stageMap[p.id] = p.stage; });
        setPipelineStages(stageMap);
      } catch (e) {
        // Non-blocking — workspace just won't show the funded lock.
      }
      
      // Load documents
      const allDocs = [];
      for (const project of projectsRes.data) {
        try {
          const docsRes = await apiService.getDocuments(project.id);
          allDocs.push(...docsRes.data.map(doc => ({ ...doc, project_name: project.name })));
        } catch (e) {
          // Ignore individual project document errors
        }
      }
      setDocuments(allDocs);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useResumeRefresh(loadData);

  // One-time cleanup: file any legacy standalone "Uploaded Bid" cards into their
  // matching prepared scope (same project + trade). Idempotent and conservative;
  // runs silently once per session and only surfaces a toast if something moved.
  useEffect(() => {
    if (!user?.id) return;
    const key = `bids_consolidated_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    (async () => {
      try {
        const res = await apiService.autoConsolidateUploadedBids(user.id);
        if (res.data?.total_shells_consolidated > 0) {
          toast.success(`Filed ${res.data.total_bids_moved} uploaded bid(s) into their prepared scopes`);
          loadData();
        }
      } catch (e) {
        // Non-blocking cleanup — ignore failures.
      }
    })();
  }, [user, loadData]);

  // Bid Request handlers
  const handleCreateBidRequest = async () => {
    if (!newBidRequest.project_id || !newBidRequest.title || !newBidRequest.scope_of_work || !newBidRequest.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newBidRequest.contractor_ids.length === 0) {
      toast.error('Please select at least one contractor');
      return;
    }
    
    try {
      setIsSubmitting(true);
      await apiService.createBidRequest(user.id, { ...newBidRequest, line_items: [] });
      toast.success('Bid request created and sent!');
      setShowCreateBid(false);
      setNewBidRequest({ project_id: '', title: '', scope_of_work: '', due_date: '', trade_type: '', contractor_ids: [], document_ids: [], notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to create bid request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBidRequest = async (id) => {
    if (!window.confirm('Delete this bid request?')) return;
    try {
      await apiService.deleteBidRequest(id, user.id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Handle opening "Send to More Contractors" modal
  const handleOpenSendToMore = (bidRequest) => {
    setSelectedBidRequestForSend(bidRequest);
    setSelectedContractorsToSend([]);
    setShowSendToMoreModal(true);
  };

  // Handle sending bid request to more contractors
  const handleSendToMoreContractors = async () => {
    if (!selectedBidRequestForSend || selectedContractorsToSend.length === 0) {
      toast.error('Please select at least one contractor');
      return;
    }
    
    try {
      setIsSendingToMore(true);
      const res = await apiService.sendBidRequestToMoreContractors(
        selectedBidRequestForSend.id,
        user.id,
        selectedContractorsToSend
      );
      
      toast.success(res.data.message);
      setShowSendToMoreModal(false);
      setSelectedBidRequestForSend(null);
      setSelectedContractorsToSend([]);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Failed to send to contractors:', error);
      toast.error(error.response?.data?.detail || 'Failed to send to contractors');
    } finally {
      setIsSendingToMore(false);
    }
  };

  // Handle Quick Bid Invite
  const handleSendQuickBidInvite = async () => {
    if (!quickInviteData.project_id || !quickInviteData.contractor_id) {
      toast.error('Please select both a project and a contractor');
      return;
    }
    
    try {
      setIsSendingQuickInvite(true);
      const res = await apiService.sendQuickBidInvite(user.id, {
        project_id: quickInviteData.project_id,
        contractor_id: quickInviteData.contractor_id,
        trade_type: quickInviteData.trade_type || null,
        message: quickInviteData.message || null
      });
      
      toast.success(res.data.message || 'Quick bid invite sent!');
      setShowQuickBidInvite(false);
      setQuickInviteData({ project_id: '', contractor_id: '', trade_type: '', message: '' });
    } catch (error) {
      console.error('Failed to send quick bid invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setIsSendingQuickInvite(false);
    }
  };

  // Get contractors not already on the bid request
  const getAvailableContractorsForBid = (bidRequest) => {
    if (!bidRequest) return [];
    const existingIds = new Set(bidRequest.contractor_ids || []);
    return contractors.filter(c => !existingIds.has(c.id));
  };

  // Handle PDF file selection - extract page previews
  const handleTakeoffFileSelect = async (file) => {
    if (!file) {
      setTakeoffPlanFile(null);
      setPdfPages([]);
      setSelectedPages([]);
      setTakeoffResult(null);
      return;
    }
    
    setTakeoffPlanFile(file);
    setTakeoffResult(null);
    
    // If it's a PDF, get page previews
    if (file.name.toLowerCase().endsWith('.pdf')) {
      setIsLoadingPdfPreview(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await apiService.getPdfPreview(formData);
        
        if (res.data?.success && res.data?.pages?.length > 0) {
          setPdfPages(res.data.pages);
          setSelectedPages([]); // Start with no pages selected
          if (res.data.pages.length > 1) {
            toast.info(`PDF has ${res.data.pages.length} pages. Select the floor plan pages to analyze (up to ${MAX_PAGES_TO_SELECT}).`, { duration: 5000 });
          } else {
            // Auto-select if only 1 page
            setSelectedPages([1]);
          }
        } else {
          setPdfPages([]);
        }
      } catch (error) {
        console.error('PDF preview error:', error);
        setPdfPages([]);
      } finally {
        setIsLoadingPdfPreview(false);
      }
    } else {
      // Not a PDF, clear page selector
      setPdfPages([]);
      setSelectedPages([]);
    }
  };

  // Toggle page selection for multi-select
  const togglePageSelection = (pageNumber) => {
    setSelectedPages(prev => {
      if (prev.includes(pageNumber)) {
        // Remove page
        return prev.filter(p => p !== pageNumber);
      } else {
        // Add page (if under limit)
        if (prev.length >= MAX_PAGES_TO_SELECT) {
          toast.warning(`Maximum ${MAX_PAGES_TO_SELECT} pages can be selected at once`);
          return prev;
        }
        return [...prev, pageNumber].sort((a, b) => a - b);
      }
    });
  };

  // AI Material Takeoff
  const handleAnalyzeTakeoff = async () => {
    if (!takeoffPlanFile) {
      toast.error('Please upload a construction plan image');
      return;
    }
    if (!newBidRequest.trade_type) {
      toast.error('Please select a trade type first');
      return;
    }
    if (!takeoffPrompt.trim()) {
      toast.error('Please describe what you need calculated from this plan');
      return;
    }
    
    // For PDFs with multiple pages, require at least one page selected
    if (pdfPages.length > 1 && selectedPages.length === 0) {
      toast.error('Please select at least one floor plan page to analyze');
      return;
    }
    
    try {
      setIsAnalyzingTakeoff(true);
      const formData = new FormData();
      formData.append('file', takeoffPlanFile);
      formData.append('trade_type', newBidRequest.trade_type);
      
      // Send the user's free-form prompt as the primary input
      formData.append('user_prompt', takeoffPrompt);
      
      // Include selected page numbers for PDFs (comma-separated for multiple)
      const pagesToAnalyze = selectedPages.length > 0 ? selectedPages : [1];
      formData.append('page_numbers', pagesToAnalyze.join(','));
      
      // Include optional helper specifications if user filled them
      const specsToSend = {
        ceiling_height: takeoffSpecs.ceiling_height ? takeoffSpecs.ceiling_height + "' ceilings" : null,
        wall_drywall: takeoffSpecs.wall_thickness ? takeoffSpecs.wall_thickness + " drywall for walls" : null,
        ceiling_drywall: takeoffSpecs.ceiling_thickness ? takeoffSpecs.ceiling_thickness + " drywall for ceilings" : null,
        garage: takeoffSpecs.include_garage ? `Garage: ${takeoffSpecs.garage_thickness} walls and ceiling` : "Garage: NOT included",
        stairs: takeoffSpecs.include_stairs ? "Include all stairwells" : "Stairs: NOT included",
        basement: takeoffSpecs.exclude_basement ? "Basement: NOT finished / no drywall" : "Include basement",
        sheet_preference: takeoffSpecs.sheet_sizes === 'both' ? "Use both 4'×12' and 54-1/2\"×12' sheets where efficient" : 
                         takeoffSpecs.sheet_sizes === 'wide' ? "Prefer 54-1/2\"×12' wide sheets" : "Use standard 4'×12' sheets only"
      };
      formData.append('specifications', JSON.stringify(specsToSend));
      
      const res = await apiService.analyzeForTakeoff(formData);
      
      if (res.data?.success && res.data?.takeoff_data) {
        // Check if it's a raw text response
        if (res.data.takeoff_data.format === 'text' && res.data.takeoff_data.raw_analysis) {
          // Still show the modal with raw text
          setTakeoffResult({
            ...res.data.takeoff_data,
            raw_text: res.data.takeoff_data.raw_analysis,
            isRawText: true
          });
          setShowTakeoffModal(true);
          toast.success('AI analysis complete!', {
            description: res.data.note || 'View the detailed takeoff results.'
          });
        } else {
          setTakeoffResult(res.data.takeoff_data);
          setShowTakeoffModal(true);
          toast.success('AI analysis complete!');
        }
      } else {
        const errorMsg = res.data?.error || 'Failed to analyze plan';
        const suggestion = res.data?.suggestion;
        
        // Show error with optional suggestion
        toast.error(errorMsg, {
          duration: 6000,
          description: suggestion || undefined,
        });
      }
    } catch (error) {
      console.error('Takeoff error:', error);
      
      // Handle different error types
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. The AI service may be slow right now.', {
          duration: 6000,
          description: 'Tip: Try a simpler floor plan or try again in a few seconds.',
        });
      } else {
        toast.error('Unable to analyze plan. Please try again.', {
          duration: 5000,
        });
      }
    } finally {
      setIsAnalyzingTakeoff(false);
    }
  };

  const applyTakeoffToScope = () => {
    if (!takeoffResult) return;
    
    // Build a formatted scope from the takeoff results
    let scopeText = newBidRequest.scope_of_work || '';
    if (scopeText) scopeText += '\n\n';
    
    scopeText += `--- AI Material Takeoff (${takeoffResult.trade}) ---\n`;
    scopeText += `Confidence: ${takeoffResult.confidence || 'N/A'}\n\n`;
    
    if (takeoffResult.measurements?.total_area_sqft) {
      scopeText += `Total Area: ${takeoffResult.measurements.total_area_sqft} sq ft\n`;
    }
    if (takeoffResult.measurements?.dimensions) {
      scopeText += `Dimensions: ${takeoffResult.measurements.dimensions}\n`;
    }
    
    scopeText += '\nMaterials Needed:\n';
    takeoffResult.materials?.forEach(m => {
      scopeText += `• ${m.item}: ${m.quantity} ${m.unit}${m.notes ? ` (${m.notes})` : ''}\n`;
    });
    
    if (takeoffResult.recommendations?.length > 0) {
      scopeText += '\nRecommendations:\n';
      takeoffResult.recommendations.forEach(r => {
        scopeText += `• ${r}\n`;
      });
    }
    
    if (takeoffResult.assumptions?.length > 0) {
      scopeText += '\nAssumptions:\n';
      takeoffResult.assumptions.forEach(a => {
        scopeText += `• ${a}\n`;
      });
    }
    
    setNewBidRequest(prev => ({ ...prev, scope_of_work: scopeText }));
    setShowTakeoffModal(false);
    toast.success('Takeoff added to scope of work');
  };

  const handleImportToBidSheet = async (bidRequest) => {
    try {
      // Load existing bid sheets for this user
      const sheetsRes = await apiService.getBidSheets(user.id);
      setExistingBidSheets(sheetsRes.data || []);
      
      // Fetch the bid responses if not already loaded
      let bidWithResponses = bidRequest;
      if (!bidRequest.responses || bidRequest.responses.length === 0) {
        try {
          const responsesRes = await apiService.getBidResponses(bidRequest.id, user.id);
          bidWithResponses = { ...bidRequest, responses: responsesRes.data || [] };
        } catch (err) {
          console.log('Could not fetch bid responses:', err);
        }
      }
      
      // Auto-suggest category based on trade type
      const tradeToCategory = {
        'electrical': 'Rough-Ins',
        'plumbing': 'Rough-Ins',
        'hvac': 'Rough-Ins',
        'framing': 'Framing',
        'roofing': 'Exterior',
        'siding': 'Exterior',
        'painting': 'Interior Finishes',
        'paint': 'Interior Finishes',
        'flooring': 'Flooring',
        'drywall': 'Insulation & Drywall',
        'insulation': 'Insulation & Drywall',
        'concrete': 'Concrete & Slab',
        'foundation': 'Foundation',
        'site work': 'Site Work',
        'landscaping': 'Final & Cleanup',
        'cabinets': 'Interior Finishes',
        'countertops': 'Interior Finishes',
      };
      
      const tradeLower = (bidRequest.trade_type || '').toLowerCase();
      const suggestedCategory = Object.keys(tradeToCategory).find(key => tradeLower.includes(key));
      const category = suggestedCategory ? tradeToCategory[suggestedCategory] : bidRequest.trade_type || 'Other';
      setImportCategory(category);
      
      // Auto-suggest subcategory based on trade type within the category
      const tradeToSubcategory = {
        'electrical': 'Electrical',
        'plumbing': 'Plumbing',
        'hvac': 'HVAC',
        'roofing': 'Roofing',
        'siding': 'Siding & Trim',
        'windows': 'Windows & Doors',
        'doors': 'Windows & Doors',
        'painting': 'Paint',
        'paint': 'Paint',
        'flooring': 'Flooring',
        'hardwood': 'Hardwood',
        'tile': 'Tile',
        'carpet': 'Carpet',
        'lvp': 'LVP',
        'drywall': 'Drywall',
        'insulation': 'Insulation',
        'concrete': 'Slab Work',
        'slab': 'Slab Work',
        'foundation': 'Footings',
        'framing': 'Structure',
        'cabinets': 'Cabinets & Counters',
        'countertops': 'Cabinets & Counters',
        'trim': 'Trim & Doors',
        'gutters': 'Siding & Trim',
        'soffit': 'Siding & Trim',
      };
      
      const suggestedSubcategory = Object.keys(tradeToSubcategory).find(key => tradeLower.includes(key));
      setImportSubcategory(suggestedSubcategory ? tradeToSubcategory[suggestedSubcategory] : '');
      
      // Auto-detect types for line items from the first response
      const response = bidWithResponses.responses?.[0];
      if (response?.line_items?.length > 0) {
        const detectedLineItems = response.line_items.map((item, idx) => {
          const descLower = (item.description || '').toLowerCase();
          let detectedType = 'labor'; // default
          
          if (descLower.includes('material') || descLower.includes('supply') || descLower.includes('supplies')) {
            detectedType = 'material';
          } else if (descLower.includes('labor') || descLower.includes('work') || descLower.includes('install')) {
            detectedType = 'labor';
          } else if (descLower.includes('permit') || descLower.includes('fee') || descLower.includes('insurance')) {
            detectedType = 'soft_costs';
          }
          
          return {
            id: idx,
            description: item.description,
            amount: item.total || item.unit_price || 0,
            quantity: item.quantity || 1,
            type: detectedType,
          };
        });
        setImportLineItems(detectedLineItems);
        
        // Check if there's a mix of labor and materials
        const hasLabor = detectedLineItems.some(i => i.type === 'labor');
        const hasMaterial = detectedLineItems.some(i => i.type === 'material');
        if (hasLabor && hasMaterial) {
          setImportItemType('labor_and_materials');
        } else if (hasMaterial) {
          setImportItemType('material');
        } else {
          setImportItemType('labor');
        }
      } else {
        setImportLineItems([]);
        setImportItemType('labor');
      }
      
      setImportBidRequest(bidWithResponses);
      setSelectedBidSheetId('new');
      setImportMethod('combined');
      setShowImportBidSheetModal(true);
    } catch (error) {
      toast.error('Failed to load bid sheets');
    }
  };
  
  const handleConfirmImport = async () => {
    if (!importBidRequest) return;
    
    try {
      const response = importBidRequest.responses?.[0];
      
      if (selectedBidSheetId === 'new') {
        // Navigate to create new bid sheet with pre-filled data
        const params = new URLSearchParams({
          from_bid: importBidRequest.id,
          project: importBidRequest.project_id,
          category: importCategory,
          subcategory: importSubcategory || importBidRequest.trade_type || 'General',
          title: importBidRequest.title || '',
          trade_type: importBidRequest.trade_type || '',
          import_method: importMethod,
        });
        
        if (response) {
          params.append('response_id', response.id);
          params.append('contractor_id', response.contractor_id || '');
          params.append('contractor_name', response.contractor_name || '');
          params.append('amount', response.total_amount || '0');
        }
        
        if (importMethod === 'combined') {
          params.append('item_type', importItemType);
        } else {
          // Pass line items with their types as JSON
          params.append('line_items', JSON.stringify(importLineItems));
        }
        
        navigate(`/bid-sheet/new?${params.toString()}`);
      } else {
        // Import to existing bid sheet
        const importData = {
          category: importCategory,
          subcategory: importSubcategory || importBidRequest.trade_type || 'General',
          import_method: importMethod,
          title: importBidRequest.title,
          trade_type: importBidRequest.trade_type
        };
        
        if (importMethod === 'combined') {
          importData.item_type = importItemType;
        } else {
          importData.line_items = importLineItems;
        }
        
        await apiService.importBidToBidSheet(
          selectedBidSheetId, 
          importBidRequest.id, 
          response?.id || null, 
          user.id, 
          importData
        );
        toast.success('Bid imported to bid sheet successfully!');
        navigate(`/bid-sheet/${selectedBidSheetId}`);
      }
      setShowImportBidSheetModal(false);
    } catch (error) {
      console.error('Import error:', error);
      const detail = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Unknown error';
      const status = error?.response?.status;
      toast.error(`Failed to import bid${status ? ` (${status})` : ''}: ${detail}`);
    }
  };

  // Award Bid handlers
  const handleOpenAwardModal = (bidRequest) => {
    setAwardingBidRequest(bidRequest);
    setSelectedWinningResponse(bidRequest.responses?.[0] || null);
    setAwardContract(null);
    setShowAwardModal(true);
  };
  
  const handleAwardBid = async () => {
    if (!awardingBidRequest || !selectedWinningResponse) {
      toast.error('Please select a winning bid');
      return;
    }
    
    setIsAwarding(true);
    try {
      // Create FormData if we have a contract file
      let contractFileId = null;
      if (awardContract) {
        const formData = new FormData();
        formData.append('file', awardContract);
        formData.append('user_id', user.id);
        formData.append('project_id', awardingBidRequest.project_id);
        formData.append('document_type', 'contract');
        formData.append('description', `Contract for ${awardingBidRequest.title}`);
        
        const uploadRes = await apiService.uploadDocument(formData);
        contractFileId = uploadRes.data?.id || uploadRes.data?.file_id;
      }
      
      // Award the bid
      await apiService.awardBid(awardingBidRequest.id, {
        winning_response_id: selectedWinningResponse.id,
        contractor_id: selectedWinningResponse.contractor_id,
        contract_file_id: contractFileId,
        user_id: user.id
      });
      
      toast.success(`🏆 Bid awarded to ${selectedWinningResponse.contractor_name || 'Contractor'}! Email notification sent.`);
      setShowAwardModal(false);
      loadData(); // Refresh to show updated status
    } catch (error) {
      console.error('Failed to award bid:', error);
      toast.error(error.response?.data?.detail || 'Failed to award bid');
    } finally {
      setIsAwarding(false);
    }
  };

  // Revoke Award handlers
  const handleOpenRevokeModal = (bidRequest) => {
    setRevokingBidRequest(bidRequest);
    setRevokeReason('');
    setShowRevokeModal(true);
  };

  const handleRevokeAward = async () => {
    if (!revokingBidRequest) return;
    
    setIsRevoking(true);
    try {
      await apiService.revokeAward(revokingBidRequest.id, user.id, revokeReason, true);
      toast.success('Award revoked. Contractor has been notified.');
      setShowRevokeModal(false);
      setRevokingBidRequest(null);
      loadData();
    } catch (error) {
      console.error('Failed to revoke award:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke award');
    } finally {
      setIsRevoking(false);
    }
  };

  // Transfer Award handlers
  const handleOpenTransferModal = async (bidRequest) => {
    setRevokingBidRequest(bidRequest);
    setTransferTargetResponse(null);
    
    // Load responses for this bid request
    try {
      const res = await apiService.getBidResponses(bidRequest.id, user.id);
      // Filter out the currently awarded response
      const otherResponses = (res.data || []).filter(r => r.id !== bidRequest.awarded_response_id);
      if (otherResponses.length === 0) {
        toast.error('No other bids available to transfer to');
        return;
      }
      setRevokingBidRequest({ ...bidRequest, responses: otherResponses });
      setShowTransferModal(true);
    } catch (error) {
      toast.error('Failed to load bid responses');
    }
  };

  const handleTransferAward = async () => {
    if (!revokingBidRequest || !transferTargetResponse) return;
    
    setIsTransferring(true);
    try {
      await apiService.transferAward(revokingBidRequest.id, user.id, transferTargetResponse.id, null, true);
      toast.success(`Award transferred to ${transferTargetResponse.contractor_name || 'new contractor'}!`);
      setShowTransferModal(false);
      setRevokingBidRequest(null);
      setTransferTargetResponse(null);
      loadData();
    } catch (error) {
      console.error('Failed to transfer award:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer award');
    } finally {
      setIsTransferring(false);
    }
  };

  // Messaging handlers
  const handleOpenMessageModal = async (contractor, bidRequest) => {
    setMessagingContractor(contractor);
    setMessagingBidRequest(bidRequest);
    setMessageText('');
    setShowMessageModal(true);
    
    // Load message thread
    try {
      const res = await apiService.getMessageThread(user.id, contractor.id, bidRequest?.id);
      setMessageThread(res.data || []);
      
      // Mark messages as read
      await apiService.markMessagesRead(user.id, contractor.id, bidRequest?.id);
      loadUnreadCount();
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !messagingContractor) return;
    
    setIsSendingMessage(true);
    try {
      await apiService.sendMessage(
        user.id, 
        messagingContractor.id, 
        messageText.trim(),
        messagingBidRequest?.project_id,
        messagingBidRequest?.id
      );
      
      // Refresh thread
      const res = await apiService.getMessageThread(user.id, messagingContractor.id, messagingBidRequest?.id);
      setMessageThread(res.data || []);
      setMessageText('');
      toast.success('Message sent!');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await apiService.getUnreadMessageCount(user.id);
      setUnreadMessageCount(res.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await apiService.getAllAlerts(user.id, false, 10);
      setAlerts(res.data?.alerts || []);
      setUnreadAlertCount(res.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await apiService.dismissAlert(alertId, user.id);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setUnreadAlertCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  // Reviews Tab handlers
  const loadPendingReviews = async () => {
    if (!user?.id) return;
    try {
      setIsLoadingReviews(true);
      const res = await apiService.getPendingReviews(user.id);
      setPendingReviews(res.data || { pending_invoices: [], pending_w9s: [], pending_cois: [], total_pending: 0 });
    } catch (error) {
      console.error('Failed to load pending reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const handleApproveInvoice = async (invoiceId) => {
    try {
      await apiService.reviewInvoice(invoiceId, user.id, 'approve');
      toast.success('Invoice approved successfully');
      loadPendingReviews();
    } catch (error) {
      console.error('Failed to approve invoice:', error);
      toast.error('Failed to approve invoice');
    }
  };

  const handleRejectInvoice = async () => {
    if (!rejectingItem || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await apiService.reviewInvoice(rejectingItem.id, user.id, 'reject', rejectionReason);
      toast.success('Invoice rejected');
      setShowRejectModal(false);
      setRejectingItem(null);
      setRejectionReason('');
      loadPendingReviews();
    } catch (error) {
      console.error('Failed to reject invoice:', error);
      toast.error('Failed to reject invoice');
    }
  };

  const handleApproveW9 = async (contractorId) => {
    try {
      await apiService.reviewContractorW9(contractorId, user.id, 'approve');
      toast.success('W-9 approved');
      loadPendingReviews();
    } catch (error) {
      console.error('Failed to approve W-9:', error);
      toast.error('Failed to approve W-9');
    }
  };

  const handleRejectW9 = async () => {
    if (!rejectingItem || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await apiService.reviewContractorW9(rejectingItem.contractor_id, user.id, 'reject', rejectionReason);
      toast.success('W-9 rejected');
      setShowRejectModal(false);
      setRejectingItem(null);
      setRejectionReason('');
      loadPendingReviews();
    } catch (error) {
      console.error('Failed to reject W-9:', error);
      toast.error('Failed to reject W-9');
    }
  };

  const handleApproveCOI = async (contractorId) => {
    try {
      await apiService.reviewContractorCOI(contractorId, user.id, 'approve');
      toast.success('COI approved');
      loadPendingReviews();
    } catch (error) {
      console.error('Failed to approve COI:', error);
      toast.error('Failed to approve COI');
    }
  };

  const handleRejectCOI = async () => {
    if (!rejectingItem || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await apiService.reviewContractorCOI(rejectingItem.contractor_id, user.id, 'reject', rejectionReason);
      toast.success('COI rejected');
      setShowRejectModal(false);
      setRejectingItem(null);
      setRejectionReason('');
      loadPendingReviews();
    } catch (error) {
      console.error('Failed to reject COI:', error);
      toast.error('Failed to reject COI');
    }
  };

  const openRejectModal = (item, type) => {
    setRejectingItem({ ...item, type });
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // Messages Tab handlers
  const loadConversations = async () => {
    try {
      const res = await apiService.getConversations(user.id);
      setConversations(res.data?.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadContactableContractors = async () => {
    try {
      const res = await apiService.getContactableContractors(user.id);
      setContactableContractors(res.data?.contractors || []);
    } catch (error) {
      console.error('Failed to load contactable contractors:', error);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    
    // Load full conversation
    try {
      const res = await apiService.getConversation(user.id, conversation.contractor_id);
      setConversationMessages(res.data?.messages || []);
      
      // Mark as read
      await apiService.markMessagesRead(user.id, conversation.contractor_id);
      loadUnreadCount();
      loadConversations();
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const handleSendNewMessage = async () => {
    if (!newMessageText.trim() || !selectedConversation) return;
    
    setIsSendingNewMessage(true);
    try {
      await apiService.sendMessage(
        user.id,
        selectedConversation.contractor_id,
        newMessageText.trim()
      );
      
      setNewMessageText('');
      
      // Reload conversation
      const res = await apiService.getConversation(user.id, selectedConversation.contractor_id);
      setConversationMessages(res.data?.messages || []);
      loadConversations();
      
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSendingNewMessage(false);
    }
  };

  const handleStartNewConversation = async (contractor) => {
    setSelectedConversation({
      contractor_id: contractor.id,
      contractor_name: contractor.company || contractor.name,
      contractor_email: contractor.email,
      contractor_trade: contractor.trade,
      messages: [],
      unread_count: 0
    });
    setConversationMessages([]);
    setShowNewConversationModal(false);
  };

  const handleAlertClick = (alert) => {
    setShowAlertsDropdown(false);
    
    // Navigate based on alert type
    if (alert.alert_type === 'bid_received' && alert.related_bid_request_id) {
      setActiveTab('bids');
      // Could expand/highlight the specific bid request
      toast.info(`Navigating to bid request...`);
    } else if (alert.alert_type === 'contractor_message' && alert.related_contractor_id) {
      setActiveTab('messages');
      // Find and select the conversation
      const conv = conversations.find(c => c.contractor_id === alert.related_contractor_id);
      if (conv) {
        handleSelectConversation(conv);
      } else {
        loadConversations();
      }
    }
  };

  // Load conversations when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages' && user?.id) {
      loadConversations();
      loadContactableContractors();
    }
  }, [activeTab, user?.id]);

  // Load pending reviews when reviews tab is active
  useEffect(() => {
    if (activeTab === 'reviews' && user?.id) {
      loadPendingReviews();
    }
  }, [activeTab, user?.id]);

  // Load unread message count and alerts on mount
  useEffect(() => {
    if (user?.id) {
      loadUnreadCount();
      loadAlerts();
    }
  }, [user?.id]);

  // Contractor handlers
  const handleAddContractor = async () => {
    if (!newContractor.company || !newContractor.email) {
      toast.error('Company and email required');
      return;
    }
    try {
      const res = await apiService.createContractor({
        ...newContractor,
        trade: newContractor.specialty || newContractor.trade, // Use specialty if provided
        user_id: user.id
      });
      toast.success('Contractor added!');
      setShowAddContractor(false);
      if (showCreateBid) {
        setNewBidRequest(prev => ({ ...prev, contractor_ids: [...prev.contractor_ids, res.data.id] }));
      }
      setNewContractor({ company: '', name: '', email: '', phone: '', trade: '', specialty: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add contractor');
    }
  };

  const handleUpdateContractor = async () => {
    if (!selectedContractor) return;
    try {
      // Set primary trade from trades array if available
      const primaryTrade = selectedContractor.trades?.length > 0 
        ? selectedContractor.trades[0] 
        : (selectedContractor.specialty || selectedContractor.trade);
      
      await apiService.updateContractor(selectedContractor.id, {
        ...selectedContractor,
        trade: primaryTrade,
        trades: selectedContractor.trades || []
      });
      toast.success('Contractor updated!');
      setShowEditContractor(false);
      setSelectedContractor(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteContractor = async (id) => {
    if (!window.confirm('Delete this contractor?')) return;
    try {
      await apiService.deleteContractor(id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Resend portal email to contractor
  const handleResendPortalEmail = async (contractorId, contractorEmail) => {
    if (!window.confirm(`Resend portal access email to ${contractorEmail}?`)) return;
    try {
      await apiService.resendContractorPortalEmail(contractorId, user.id);
      toast.success(`Portal link sent to ${contractorEmail}`);
    } catch (error) {
      console.error('Failed to resend portal email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send portal email');
    }
  };

  // Document handlers
  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadProject) {
      toast.error('Select file and project');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('project_id', uploadProject);
      formData.append('category', uploadCategory);
      await apiService.uploadDocument(formData);
      toast.success('Uploaded!');
      setShowUploadDialog(false);
      setUploadFile(null);
      loadData();
    } catch (error) {
      toast.error('Upload failed');
    }
  };

  const handleDownloadDocument = async (id, filename) => {
    try {
      const response = await apiService.downloadDocument(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Delete?')) return;
    try {
      await apiService.deleteDocument(id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  // AI Bid Extraction - Single file
  const handleExtractBidData = async (file) => {
    if (!file) return;
    
    setIsExtractingBid(true);
    setExtractedBidData(null);
    setMultiTradeMode(false);
    setMultiFileMode(false);
    setSelectedTradeGroups([]);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiService.extractBidData(formData);
      
      if (res.data?.success && res.data?.extracted_data) {
        const data = res.data.extracted_data;
        setExtractedBidData(data);
        
        // Check for multi-trade detection
        if (data.trade_groups && data.trade_groups.length > 1) {
          setMultiTradeMode(true);
          // AI often leaves trade-group subtotals at 0 (esp. lump-sum bids with no
          // per-line prices), which caused bids to save as $0. Compute a fallback
          // subtotal from line items, and if the whole split still comes to $0,
          // put the document's grand total on the first group so it isn't lost.
          const grandTotal = parseFloat(data.pricing?.total_amount) || 0;
          const groups = data.trade_groups.map((tg, idx) => ({
            ...tg,
            subtotal: computeTradeGroupSubtotal(tg),
            selected: true,
            id: idx,
          }));
          const sumSub = groups.reduce((s, g) => s + (parseFloat(g.subtotal) || 0), 0);
          if (sumSub === 0 && grandTotal > 0 && groups.length > 0) {
            groups[0].subtotal = grandTotal;
          }
          setSelectedTradeGroups(groups);
          
          // Detect units from bid
          const detectedUnits = data.bid_details?.detected_units || 1;
          setBidUnits(detectedUnits);
          
          toast.success(`Detected ${data.trade_groups.length} trades in this bid!`);
        } else {
          // Single trade - use existing flow
          setUploadBidData(prev => ({
            ...prev,
            file: file,
            total_amount: data.pricing?.total_amount?.toString() || prev.total_amount,
            notes: [
              data.bid_details?.scope_summary || '',
              ...(data.notes || [])
            ].filter(Boolean).join('\n'),
            line_items: data.line_items || []
          }));
          
          toast.success('Bid data extracted successfully!');
        }
        
        // Try to match contractor by name
        if (data.contractor?.company_name) {
          const matchedContractor = contractors.find(c => 
            (c.company || c.name || '').toLowerCase().includes(data.contractor.company_name.toLowerCase()) ||
            data.contractor.company_name.toLowerCase().includes((c.company || c.name || '').toLowerCase())
          );
          if (matchedContractor) {
            setUploadBidData(prev => ({ ...prev, contractor_id: matchedContractor.id }));
          }
        }
      } else {
        toast.error(res.data?.error || 'Failed to extract bid data');
      }
    } catch (error) {
      console.error('Bid extraction error:', error);
      const detail = error.response?.data?.detail || error.response?.data?.error;
      toast.error(detail ? `Bid extraction failed: ${detail}` : 'Failed to extract bid data');
    } finally {
      setIsExtractingBid(false);
    }
  };

  // Multi-File Bid Extraction - Extract from multiple files and combine
  // Track per-file extraction progress so the user knows we're still working
  // through their upload, not frozen. Reset each time a new batch is dropped.
  const [extractionProgress, setExtractionProgress] = useState({ current: 0, total: 0 });

  const handleMultiFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const fileArray = Array.from(files);
    setUploadedFiles(fileArray);
    setIsExtractingBid(true);
    setExtractedBidData(null);
    setExtractedFilesData([]);
    setMultiFileMode(true);
    setMultiTradeMode(false);
    setCombineFiles(true);
    setExtractionProgress({ current: 0, total: fileArray.length });
    
    try {
      const extractedResults = [];
      let totalUnits = 0;
      let detectedContractor = null;
      let skippedCount = 0;
      
      // Extract each file. EVERY file gets an entry in extractedResults — even
      // if it failed — so the user sees the full upload list and can re-try /
      // correct individual rows. Previously we only pushed on success which
      // meant 2-of-3 silent drops looked like "only 1 file uploaded".
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        setExtractionProgress({ current: i + 1, total: fileArray.length });
        const formData = new FormData();
        formData.append('file', file);
        
        let pushedEntry = null;
        try {
          const res = await apiService.extractBidData(formData);
          
          if (res.data?.success && res.data?.extracted_data) {
            const data = res.data.extracted_data;
            // unit_multiplier defaults to 1 (treat as total). If the AI flagged
            // pricing as per-unit OR the file's detected unit count >1, the
            // user can flip the toggle to multiply by detected_units.
            const detectedUnits = data.bid_details?.detected_units || 1;
            const pricingBasis = (data.pricing?.pricing_basis || '').toLowerCase();
            const isPerUnit = pricingBasis === 'per_unit' || pricingBasis === 'per-unit';
            pushedEntry = {
              filename: file.name,
              data: data,
              units: detectedUnits,
              total: data.pricing?.total_amount || 0,
              trade_groups: data.trade_groups || [],
              line_items: data.line_items || [],
              pricing_basis: isPerUnit ? 'per_unit' : 'total',
              // multiplier: 1 = "the total above is the full bid"; N = "multiply by N units"
              multiplier: isPerUnit && detectedUnits > 1 ? detectedUnits : 1,
              extraction_status: 'ok',
            };
            totalUnits += detectedUnits;
            
            if (!detectedContractor && data.contractor?.company_name) {
              detectedContractor = data.contractor;
            }
          } else {
            skippedCount += 1;
            pushedEntry = {
              filename: file.name,
              data: null,
              extraction_status: 'failed',
              error: res.data?.error || 'AI could not read this file. Edit manually below or re-upload a clearer copy.',
              total: 0,
              multiplier: 1,
            };
          }
        } catch (fileError) {
          console.error(`Error extracting ${file.name}:`, fileError);
          skippedCount += 1;
          pushedEntry = {
            filename: file.name,
            data: null,
            extraction_status: 'failed',
            error: fileError?.response?.data?.detail || 'Network or server error while extracting.',
            total: 0,
            multiplier: 1,
          };
        }
        extractedResults.push(pushedEntry);
        // Stream partial results into state so the user sees rows appear one-by-one
        setExtractedFilesData([...extractedResults]);
      }
      
      setBidUnits(totalUnits);
      
      // Try to match contractor
      if (detectedContractor?.company_name) {
        const matchedContractor = contractors.find(c => 
          (c.company || c.name || '').toLowerCase().includes(detectedContractor.company_name.toLowerCase()) ||
          detectedContractor.company_name.toLowerCase().includes((c.company || c.name || '').toLowerCase())
        );
        if (matchedContractor) {
          setUploadBidData(prev => ({ ...prev, contractor_id: matchedContractor.id }));
        }
        setExtractedBidData({ contractor: detectedContractor });
      }
      
      const okCount = extractedResults.filter(r => r.extraction_status === 'ok').length;
      if (skippedCount > 0) {
        toast.warning(`${okCount} of ${fileArray.length} extracted. ${skippedCount} failed — re-upload or enter manually.`);
      } else {
        toast.success(`Extracted data from all ${okCount} files`);
      }
      
    } catch (error) {
      console.error('Multi-file extraction error:', error);
      toast.error('Failed to extract bid data from files');
    } finally {
      setIsExtractingBid(false);
      setExtractionProgress({ current: 0, total: 0 });
    }
  };

  // Handle combined multi-file bid upload
  const handleUploadCombinedBid = async () => {
    if (!uploadBidData.project_id || !uploadBidData.contractor_id || !uploadBidData.trade_type) {
      toast.error('Please select project, contractor, and trade type');
      return;
    }
    
    if (extractedFilesData.length === 0) {
      toast.error('No extracted file data');
      return;
    }
    
    try {
      // Combine all line items from all files
      const combinedLineItems = [];
      const fileBreakdown = [];
      let combinedTotal = 0;
      let combinedUnits = 0;
      
      for (const fileData of extractedFilesData) {
        if (fileData.extraction_status !== 'ok' && !fileData.data) continue;
        
        const mult = fileData.multiplier || 1;
        const fileTotal = (fileData.total || 0) * mult;
        const fileUnits = (fileData.units || 1) * (mult > 1 ? 1 : 1); // units field is unchanged; multiplier represents per-unit roll-up
        // If user said "per unit × N", that's N units worth from THIS file.
        const effectiveUnits = mult > 1 ? mult : (fileData.units || 1);
        
        fileBreakdown.push({
          filename: fileData.filename,
          units: effectiveUnits,
          per_unit_price: mult > 1 ? (fileData.total || 0) : null,
          multiplier: mult,
          total: fileTotal,
          line_items: fileData.line_items,
        });
        
        combinedTotal += fileTotal;
        combinedUnits += effectiveUnits;
        
        // Add line items with file source. If multiplier > 1, scale each line.
        for (const item of fileData.line_items || []) {
          combinedLineItems.push({
            ...item,
            total: (item.total || 0) * mult,
            source_file: fileData.filename,
            applied_multiplier: mult,
          });
        }
      }
      
      const contractor = contractors.find(c => c.id === uploadBidData.contractor_id);
      const contractorName = extractedBidData?.contractor?.company_name || contractor?.company || contractor?.name || 'Unknown';
      
      // Attach to an existing prepared scope if one was chosen; otherwise create a new bid request.
      const attaching = uploadTargetBidRequestId && uploadTargetBidRequestId !== '__new__';

      if (attaching) {
        // File the combined bid under the chosen scope via the owner manual-bid path
        // (no invite requirement, no owner email).
        await apiService.createManualBid(uploadTargetBidRequestId, user.id, {
          contractor_id: uploadBidData.contractor_id,
          total_amount: combinedTotal,
          notes: uploadBidData.notes,
          line_items: combinedLineItems,
        });
      } else {
        const bidRequest = await apiService.createBidRequest(user.id, {
          project_id: uploadBidData.project_id,
          title: `Combined Bid - ${contractorName}`,
          scope_of_work: `Combined from ${extractedFilesData.length} files: ${uploadedFiles.map(f => f.name).join(', ')}`,
          due_date: new Date().toISOString(),
          trade_type: uploadBidData.trade_type,
          contractor_ids: [uploadBidData.contractor_id],
          notes: uploadBidData.notes,
          bid_units: bidUnits,
          project_units: projectUnits,
          source: 'multi_file_combined',
          file_breakdown: fileBreakdown
        });

        if (contractor) {
          const effectiveBidUnits = combinedUnits || bidUnits || 1;
          const scaleFactor = projectUnits / effectiveBidUnits;
          await apiService.submitBidResponse(contractor.email, {
            bid_request_id: bidRequest.data.id,
            line_items: combinedLineItems,
            total_amount: combinedTotal,
            original_total: combinedTotal,
            bid_units: effectiveBidUnits,
            project_units: projectUnits,
            scaled_total: combinedTotal * scaleFactor,
            notes: uploadBidData.notes,
            contractor_name: contractorName,
            source: 'multi_file_combined',
            file_breakdown: fileBreakdown
          });
        }
      }
      
      const okCount = extractedFilesData.filter(f => f.extraction_status === 'ok' || f.data).length;
      toast.success(`Combined ${okCount} file${okCount === 1 ? '' : 's'} into one bid ($${combinedTotal.toLocaleString()})`);
      setShowUploadBid(false);
      resetUploadBidState();
      loadData();
    } catch (error) {
      console.error('Combined bid upload error:', error);
      toast.error('Failed to upload combined bid');
    }
  };

  // Upload existing bid
  const handleUploadExistingBid = async () => {
    if (!uploadBidData.project_id || !uploadBidData.contractor_id || !uploadBidData.trade_type) {
      toast.error('Please select project, contractor, and trade type');
      return;
    }
    try {
      // Build scope from extracted data
      let scopeText = 'Imported from existing bid';
      if (extractedBidData?.bid_details?.scope_summary) {
        scopeText = extractedBidData.bid_details.scope_summary;
      }
      
      // Get detected units from extraction or default to projectUnits
      const detectedUnits = extractedBidData?.bid_details?.detected_units || projectUnits;
      
      // Attach to an existing prepared scope if one was chosen; otherwise create a new bid request.
      const attaching = uploadTargetBidRequestId && uploadTargetBidRequestId !== '__new__';

      const lineItems = uploadBidData.line_items?.length > 0
        ? uploadBidData.line_items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unit_price: item.unit_price,
            total: item.total || 0
          }))
        : [{ description: 'Total Bid', total: parseFloat(uploadBidData.total_amount) || 0 }];
      const totalAmount = parseFloat(uploadBidData.total_amount) || 0;

      if (attaching) {
        // Owner recording an emailed/offline bid under an existing scope. Uses the
        // manual-bid endpoint so it doesn't require the contractor to be pre-invited
        // and doesn't fire the "new bid received" owner email.
        await apiService.createManualBid(uploadTargetBidRequestId, user.id, {
          contractor_id: uploadBidData.contractor_id,
          total_amount: totalAmount,
          notes: uploadBidData.notes,
          line_items: lineItems,
        });
      } else {
        const bidRequest = await apiService.createBidRequest(user.id, {
          project_id: uploadBidData.project_id,
          title: `Uploaded Bid - ${extractedBidData?.contractor?.company_name || uploadBidData.file?.name || 'Manual Entry'}`,
          scope_of_work: scopeText,
          due_date: extractedBidData?.bid_details?.valid_until || new Date().toISOString(),
          trade_type: uploadBidData.trade_type,
          contractor_ids: [uploadBidData.contractor_id],
          notes: uploadBidData.notes,
          bid_units: detectedUnits,
          project_units: projectUnits,
          skip_email: true  // Don't send email for uploaded bids - we already have them
        });

        const contractor = contractors.find(c => c.id === uploadBidData.contractor_id);
        if (contractor) {
          const scaleFactor = projectUnits / detectedUnits;
          await apiService.submitBidResponse(contractor.email, {
            bid_request_id: bidRequest.data.id,
            line_items: lineItems,
            total_amount: totalAmount,
            original_total: totalAmount,
            bid_units: detectedUnits,
            project_units: projectUnits,
            scaled_total: totalAmount * scaleFactor,
            notes: uploadBidData.notes,
            contractor_info: extractedBidData?.contractor || null
          });
        }
      }
      
      toast.success(
        attaching
          ? 'Bid filed under the selected scope!'
          : 'Bid uploaded with extracted data!'
      );
      setShowUploadBid(false);
      resetUploadBidState();
      loadData();
    } catch (error) {
      toast.error('Failed to upload bid');
    }
  };

  // Multi-Trade Bid Functions
  // Compute a trade group's dollar subtotal, falling back to the sum of its line
  // items when the AI left `subtotal` at 0 (common for lump-sum bids).
  const computeTradeGroupSubtotal = (tg) => {
    const st = parseFloat(tg?.subtotal) || 0;
    if (st > 0) return st;
    return (tg?.line_items || []).reduce((s, it) => {
      const lineTotal = parseFloat(it?.total)
        || ((parseFloat(it?.quantity) || 0) * (parseFloat(it?.unit_price) || 0));
      return s + (lineTotal || 0);
    }, 0);
  };

  const handleCreateMultiTradeBids = async () => {
    if (!uploadBidData.project_id || !uploadBidData.contractor_id) {
      toast.error('Please select project and contractor');
      return;
    }
    
    const selectedTrades = selectedTradeGroups.filter(tg => tg.selected);
    if (selectedTrades.length === 0) {
      toast.error('Please select at least one trade');
      return;
    }

    // Final guard: never save $0 trade bids. Use each group's effective subtotal,
    // and if the whole set is still $0 but the document had a grand total, apply it
    // to the first selected trade so the amount is never lost.
    const grandTotal = parseFloat(extractedBidData?.pricing?.total_amount) || 0;
    const tradeGroupsPayload = selectedTrades.map((tg) => ({
      trade_type: tg.trade_type,
      line_items: tg.line_items || [],
      subtotal: computeTradeGroupSubtotal(tg),
      detected_units: bidUnits,
    }));
    const sumSub = tradeGroupsPayload.reduce((s, t) => s + (parseFloat(t.subtotal) || 0), 0);
    if (sumSub === 0 && grandTotal > 0 && tradeGroupsPayload.length > 0) {
      tradeGroupsPayload[0].subtotal = grandTotal;
    }
    
    try {
      const contractor = contractors.find(c => c.id === uploadBidData.contractor_id);
      const contractorName = extractedBidData?.contractor?.company_name || contractor?.company || contractor?.name || 'Unknown';
      
      const res = await apiService.createMultiTradeBids(user.id, {
        project_id: uploadBidData.project_id,
        contractor_id: uploadBidData.contractor_id,
        contractor_name: contractorName,
        trade_groups: tradeGroupsPayload,
        bid_units: bidUnits,
        project_units: projectUnits,
        notes: uploadBidData.notes,
        original_filename: uploadBidData.file?.name
      });
      
      if (res.data?.success) {
        toast.success(`Created ${res.data.bids.length} trade-specific bids for comparison!`);
        setShowUploadBid(false);
        resetUploadBidState();
        loadTradeComparison();
      }
    } catch (error) {
      console.error('Multi-trade bid creation error:', error);
      toast.error('Failed to create trade bids');
    }
  };
  
  const resetUploadBidState = () => {
    setUploadBidData({ project_id: '', contractor_id: '', trade_type: '', file: null, total_amount: '', notes: '', line_items: [] });
    setUploadTargetBidRequestId('');
    setExtractedBidData(null);
    setMultiTradeMode(false);
    setMultiFileMode(false);
    setUploadedFiles([]);
    setExtractedFilesData([]);
    setCombineFiles(true);
    setSelectedTradeGroups([]);
    setBidUnits(1);
    setShowQuickAddContractor(false);
    setQuickAddContractor({ company: '', name: '', email: '', phone: '', trade: '' });
  };
  
  // Quick Add Contractor during bid upload
  const handleQuickAddContractor = async () => {
    if (!quickAddContractor.company.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    try {
      setIsAddingContractor(true);
      const res = await apiService.createContractor(user.id, {
        company: quickAddContractor.company.trim(),
        name: quickAddContractor.name.trim() || quickAddContractor.company.trim(),
        email: quickAddContractor.email.trim() || null,
        phone: quickAddContractor.phone.trim() || null,
        trade: quickAddContractor.trade || uploadBidData.trade_type || null
      });
      
      const newContractor = res.data;
      
      // Add to contractors list and select it
      setContractors(prev => [...prev, newContractor]);
      setUploadBidData(prev => ({ ...prev, contractor_id: newContractor.id }));
      
      toast.success(`Contractor "${newContractor.company}" added!`);
      setShowQuickAddContractor(false);
      setQuickAddContractor({ company: '', name: '', email: '', phone: '', trade: '' });
    } catch (error) {
      console.error('Failed to add contractor:', error);
      toast.error(error.response?.data?.detail || 'Failed to add contractor');
    } finally {
      setIsAddingContractor(false);
    }
  };
  
  // Quick Add Trade handler
  const handleQuickAddTrade = async () => {
    if (!quickAddTradeName.trim()) {
      toast.error('Trade name is required');
      return;
    }
    
    // Check if trade already exists
    const normalizedName = quickAddTradeName.trim();
    if (trades.some(t => t.toLowerCase() === normalizedName.toLowerCase())) {
      toast.error('This trade already exists');
      return;
    }
    
    try {
      setIsAddingTrade(true);
      await apiService.createTrade(user.id, normalizedName);
      
      // Add to local trades list and select it
      setAvailableTrades(prev => [...prev, normalizedName]);
      setEditBidData(prev => ({ ...prev, trade_type: normalizedName }));
      
      toast.success(`Trade "${normalizedName}" added!`);
      setShowQuickAddTrade(false);
      setQuickAddTradeName('');
    } catch (error) {
      console.error('Failed to add trade:', error);
      toast.error(error.response?.data?.detail || 'Failed to add trade');
    } finally {
      setIsAddingTrade(false);
    }
  };
  
  const loadTradeComparison = async (projectId = undefined) => {
    try {
      // Scope the comparison to the currently-selected project when one is
      // active (bidFilterProject). Passing no id previously returned bids
      // across ALL projects, which is not what users expect when viewing a
      // single project's pipeline. An explicit projectId argument still wins;
      // pass null explicitly to force an all-projects comparison.
      const effectiveProjectId =
        projectId !== undefined
          ? projectId
          : (bidFilterProject && bidFilterProject !== 'all' ? bidFilterProject : null);
      const res = await apiService.getBidsByTrade(user.id, effectiveProjectId);
      if (res.data?.success) {
        setTradeComparisonData(res.data);
      }
    } catch (error) {
      console.error('Failed to load trade comparison:', error);
    }
  };
  
  const handleDeleteTradeBid = async (bidId) => {
    try {
      await apiService.deleteTradeBid(bidId, user.id);
      toast.success('Trade bid deleted');
      loadTradeComparison();
    } catch (error) {
      toast.error('Failed to delete trade bid');
    }
  };

  // Edit Bid Request Functions
  const handleEditBidRequest = async (bid) => {
    setEditingBid(bid);
    
    // Get line items - first try to get from bid response (contractor submitted data)
    let lineItems = [];
    let totalAmount = bid.total_amount || '';
    let notes = bid.notes || '';
    
    // First check if there's a bid response with the actual contractor data
    try {
      const responsesRes = await apiService.getBidResponses(bid.id, user.id);
      if (responsesRes.data && responsesRes.data.length > 0) {
        // Use the first response (typically there's only one per bid request for uploaded bids)
        const response = responsesRes.data[0];
        if (response.line_items && response.line_items.length > 0) {
          lineItems = response.line_items;
        }
        if (response.total_amount) {
          totalAmount = response.total_amount;
        }
        if (response.notes) {
          notes = response.notes;
        }
      }
    } catch (e) {
      console.log('No bid responses found, checking bid request');
    }
    
    // If no response data, try embedded line_items from bid request
    if (lineItems.length === 0) {
      lineItems = bid.line_items || [];
    }
    
    // If still no line items, try to fetch from bid_sheet_items collection
    if (lineItems.length === 0 && bid.id) {
      try {
        const itemsRes = await apiService.getBidSheetItems(bid.id);
        if (itemsRes.data && itemsRes.data.length > 0) {
          lineItems = itemsRes.data;
        }
      } catch (e) {
        // No items found, that's ok
        console.log('No bid sheet items found');
      }
    }
    
    setEditBidData({
      title: bid.title || '',
      trade_type: bid.trade_type || '',
      contractor_id: bid.contractor_ids?.[0] || '',
      project_id: bid.project_id || '',
      notes: notes,
      total_amount: totalAmount,
      line_items: lineItems.map(item => ({
        id: item.id,
        description: item.description || '',
        quantity: item.quantity || '',
        unit: item.unit || '',
        unit_price: item.unit_price || '',
        total: item.total || 0
      }))
    });
    setShowEditBid(true);
  };
  
  const handleSaveEditBid = async () => {
    if (!editingBid) return;
    
    try {
      // Prepare line items for saving
      const cleanedLineItems = (editBidData.line_items || [])
        .filter(item => item.description || item.total)
        .map(item => ({
          id: item.id || `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          description: item.description || '',
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit || 'ea',
          unit_type: item.unit_type || 'flat',
          unit_price: parseFloat(item.unit_price) || 0,
          total: parseFloat(item.total) || 0
        }));
      
      // Calculate total from line items if we have them
      const calculatedTotal = cleanedLineItems.length > 0 
        ? cleanedLineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0)
        : parseFloat(editBidData.total_amount) || 0;
      
      // Update bid request with all data including line items
      await apiService.updateBidRequest(editingBid.id, user.id, {
        title: editBidData.title,
        trade_type: editBidData.trade_type,
        contractor_ids: editBidData.contractor_id ? [editBidData.contractor_id] : editingBid.contractor_ids,
        project_id: editBidData.project_id,
        notes: editBidData.notes,
        total_amount: calculatedTotal,
        line_items: cleanedLineItems
      });
      
      toast.success('Bid updated successfully!');
      setShowEditBid(false);
      setEditingBid(null);
      loadData();
    } catch (error) {
      console.error('Edit bid error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update bid');
    }
  };
  
  const updateLineItem = (index, field, value) => {
    const newItems = [...editBidData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-calculate total if quantity or unit_price changed
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(field === 'quantity' ? value : newItems[index].quantity) || 0;
      const price = parseFloat(field === 'unit_price' ? value : newItems[index].unit_price) || 0;
      newItems[index].total = (qty * price).toFixed(2);
    }
    
    // Update grand total
    const grandTotal = newItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    
    setEditBidData({
      ...editBidData, 
      line_items: newItems,
      total_amount: grandTotal.toFixed(2)
    });
  };

  // Payment functions
  const getContractorPayments = (contractorId) => {
    return contractorPayments.filter(p => p.contractor_id === contractorId);
  };

  const contractorYTDTotals = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const totals = {};
    contractorPayments.forEach(p => {
      const paymentYear = new Date(p.payment_date).getFullYear();
      if (paymentYear === currentYear) {
        totals[p.contractor_id] = (totals[p.contractor_id] || 0) + (p.amount || 0);
      }
    });
    return totals;
  }, [contractorPayments]);

  const handleLogPayment = async () => {
    if (!selectedContractor || !newPayment.amount) {
      toast.error('Please enter an amount');
      return;
    }
    try {
      await apiService.createContractorPayment({
        contractor_id: selectedContractor.id,
        user_id: user.id,
        amount: parseFloat(newPayment.amount),
        payment_date: newPayment.payment_date,
        payment_method: newPayment.payment_method,
        notes: newPayment.notes
      });
      toast.success('Payment logged successfully');
      setShowLogPayment(false);
      setNewPayment({ amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'check', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to log payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await apiService.deleteContractorPayment(paymentId);
      toast.success('Payment deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handleRequestForms = async (contractor) => {
    try {
      await apiService.sendW9Request(contractor.id, user.id);
      toast.success(`W-9 request sent to ${contractor.name || contractor.company}`);
    } catch (error) {
      toast.error('Failed to send W-9 request');
    }
  };

  const handleRequestInsurance = async (contractor) => {
    try {
      await apiService.sendInsuranceRequest(contractor.id, user.id);
      toast.success(`Insurance certificate request sent to ${contractor.name || contractor.company}`);
    } catch (error) {
      toast.error('Failed to send insurance request');
    }
  };

  const handleGenerate1099 = (contractor) => {
    setSelectedContractor(contractor);
    setSelected1099Year(new Date().getFullYear());
    setShow1099Dialog(true);
  };

  const handleDownload1099 = () => {
    if (!selectedContractor) return;
    
    const year = selected1099Year;
    // Calculate payments for the selected year
    const contractorPaymentsForYear = contractorPayments.filter(p => {
      if (p.contractor_id !== selectedContractor.id) return false;
      const paymentYear = new Date(p.payment_date).getFullYear();
      return paymentYear === year;
    });
    const totalPaid = contractorPaymentsForYear.reduce((sum, p) => sum + (p.amount || 0), 0);
    
    if (totalPaid < 600) {
      toast.info(`Total paid to ${selectedContractor.company || selectedContractor.name} in ${year} is $${totalPaid.toFixed(2)}. 1099 is only required for payments >= $600.`);
      setShow1099Dialog(false);
      return;
    }
    
    // Generate CSV for accountant
    const csvContent = `1099-NEC Data for Tax Year ${year}

PAYER INFORMATION
Business Name,${user.business_name || `${user.first_name} ${user.last_name}`}
Address,${user.business_address || ''}
City,${user.business_city || ''}
State,${user.business_state || ''}
ZIP,${user.business_zip || ''}
TIN (EIN),${user.tax_id || ''}

RECIPIENT INFORMATION
Name,${selectedContractor.company || selectedContractor.name}
Contact,${selectedContractor.name || ''}
Address,${selectedContractor.street_address || ''}
City,${selectedContractor.city || ''}
State,${selectedContractor.state || ''}
ZIP,${selectedContractor.zip || ''}
TIN (SSN/EIN),${selectedContractor.ein_ssn || ''}

PAYMENT SUMMARY
Tax Year,${year}
Total Payments,$${totalPaid.toFixed(2)}
Box 1 - Nonemployee Compensation,$${totalPaid.toFixed(2)}

PAYMENT DETAILS
Date,Amount,Method,Notes
${contractorPaymentsForYear.map(p => `${formatDate(p.payment_date)},$${p.amount.toFixed(2)},${p.payment_method || 'check'},${(p.notes || '').replace(/,/g, ';')}`).join('\n')}
`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `1099_NEC_${(selectedContractor.company || selectedContractor.name).replace(/\s+/g, '_')}_${year}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    setShow1099Dialog(false);
    toast.success(`1099-NEC generated for ${selectedContractor.company || selectedContractor.name}`);
  };

  const handle1099Report = async () => {
    setSelected1099Year(new Date().getFullYear());
    setSelectedContractor(null); // null means bulk report
    setShow1099Dialog(true);
  };

  const handleDownloadBulk1099Report = async () => {
    try {
      const year = selected1099Year;
      const response = await apiService.get1099Report(user.id, year);
      const report = response.data;
      
      if (report.available_years) {
        setAvailable1099Years(report.available_years);
      }
      
      // Generate comprehensive CSV
      let csvContent = `1099-NEC Summary Report - Tax Year ${year}
Generated,${new Date().toLocaleString()}

PAYER INFORMATION
Business Name,${report.payer.name}
TIN (EIN),${report.payer.tin}
Address,${report.payer.address}
City,${report.payer.city}
State,${report.payer.state}
ZIP,${report.payer.zip}

SUMMARY
Total Contractors,${report.summary.total_contractors}
Contractors Requiring 1099,${report.summary.reportable_contractors}
Total 1099 Amount,$${report.summary.total_reportable_amount.toFixed(2)}

CONTRACTORS REQUIRING 1099 (>= $600)
Name,TIN,Address,City,State,ZIP,Total Paid
`;
      report.reportable_contractors.forEach(c => {
        csvContent += `"${c.name}",${c.tin},"${c.address}",${c.city},${c.state},${c.zip},$${c.total_paid.toFixed(2)}\n`;
      });

      csvContent += `
CONTRACTORS NOT REQUIRING 1099 (< $600)
Name,Total Paid
`;
      report.non_reportable_contractors.forEach(c => {
        csvContent += `"${c.name}",$${c.total_paid.toFixed(2)}\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `1099_Report_${year}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShow1099Dialog(false);
      toast.success(`1099 Report generated: ${report.summary.reportable_contractors} contractors require 1099 forms`);
    } catch (error) {
      toast.error('Failed to generate 1099 report');
    }
  };

  const handleAddCustomTrade = async () => {
    if (!newTradeName.trim()) return;
    try {
      await apiService.createTrade(user.id, newTradeName.trim());
      toast.success(`Trade "${newTradeName}" added`);
      setNewTradeName('');
      // Reload trades
      const tradesRes = await apiService.getTrades(user.id);
      if (tradesRes.data?.trades) {
        setAvailableTrades(tradesRes.data.trades.filter(t => t !== 'All Trades'));
      }
    } catch (error) {
      toast.error('Failed to add trade');
    }
  };

  const handleUploadW9 = async (contractorId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiService.uploadContractorW9(contractorId, formData);
      toast.success('W-9 uploaded successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to upload W-9');
    }
  };

  const handleUploadCOI = async (contractorId, file) => {
    try {
      // v2.4: COI expiration is now MANDATORY. Quick prompt before upload.
      const today = new Date().toISOString().split('T')[0];
      const expIso = window.prompt(
        'COI Expiration Date is required (YYYY-MM-DD)',
        today
      );
      if (!expIso) {
        toast.error('COI upload cancelled — expiration date is required.');
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(expIso.trim())) {
        toast.error('Invalid date format. Use YYYY-MM-DD.');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('expiration_date', expIso.trim());
      await apiService.uploadContractorCOI(contractorId, formData);
      toast.success('COI uploaded successfully');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to upload COI');
    }
  };

  // Upload handler for the compliance-badge document dialog (W-9 / COI)
  const handleDocUpload = async () => {
    if (!selectedContractor || !docUploadFile) return;
    if (docUploadType === 'coi' && !docCoiExpiration) {
      toast.error('COI expiration date is required.');
      return;
    }
    try {
      setIsUploadingDoc(true);
      const formData = new FormData();
      formData.append('file', docUploadFile);
      if (docUploadType === 'coi') {
        formData.append('expiration_date', docCoiExpiration);
        await apiService.uploadContractorCOI(selectedContractor.id, formData);
        toast.success('COI uploaded successfully');
      } else {
        await apiService.uploadContractorW9(selectedContractor.id, formData);
        toast.success('W-9 uploaded successfully');
      }
      setShowDocUploadDialog(false);
      setDocUploadFile(null);
      setDocCoiExpiration('');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Failed to upload ${docUploadType.toUpperCase()}`);
    } finally {
      setIsUploadingDoc(false);
    }
  };


  // Get unique trades from all contractors
  const contractorTrades = useMemo(() => {
    const tradesSet = new Set();
    contractors.forEach(c => {
      if (c.trade) tradesSet.add(c.trade);
      if (c.specialty) tradesSet.add(c.specialty);
      if (c.trades?.length > 0) {
        c.trades.forEach(t => tradesSet.add(t));
      }
    });
    // Sort alphabetically and add "All Trades" at the beginning
    const sortedTrades = Array.from(tradesSet).sort((a, b) => a.localeCompare(b));
    return ['All Trades', ...sortedTrades];
  }, [contractors]);

  // Filtered data
  const { matchingContractors, otherContractors } = useMemo(() => {
    if (selectedTrade === 'all' || selectedTrade === 'All Trades') {
      return { matchingContractors: contractors, otherContractors: [] };
    }
    const tradeLower = selectedTrade.toLowerCase();
    // Support multiple trades
    const matching = contractors.filter(c => {
      if (c.trades?.length > 0) {
        return c.trades.some(t => t.toLowerCase() === tradeLower);
      }
      return c.trade?.toLowerCase() === tradeLower || c.specialty?.toLowerCase() === tradeLower;
    });
    const others = contractors.filter(c => {
      if (c.trades?.length > 0) {
        return !c.trades.some(t => t.toLowerCase() === tradeLower);
      }
      return c.trade?.toLowerCase() !== tradeLower && c.specialty?.toLowerCase() !== tradeLower;
    });
    return { matchingContractors: matching, otherContractors: others };
  }, [contractors, selectedTrade]);

  const filteredContractors = useMemo(() => {
    return contractors.filter(c => {
      if (searchTerm && !c.company?.toLowerCase().includes(searchTerm.toLowerCase()) && !c.name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (selectedTrade !== 'all' && selectedTrade !== 'All Trades') {
        const tradeLower = selectedTrade.toLowerCase();
        if (c.trade?.toLowerCase() !== tradeLower && c.specialty?.toLowerCase() !== tradeLower) return false;
      }
      return true;
    });
  }, [contractors, searchTerm, selectedTrade]);

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      if (filterProject !== 'all' && doc.project_id !== filterProject) return false;
      if (filterDocType !== 'all' && doc.category !== filterDocType) return false;
      if (searchTerm && !doc.file_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    });
  }, [documents, filterProject, filterDocType, searchTerm]);

  // Filter bid requests by project
  const filteredBidRequests = useMemo(() => {
    return bidRequests.filter(bid => {
      if (bidFilterProject !== 'all' && bid.project_id !== bidFilterProject) return false;
      return true;
    });
  }, [bidRequests, bidFilterProject]);

  // Stats
  const stats = useMemo(() => ({
    openRequests: bidRequests.filter(b => b.status === 'open').length,
    bidsReceived: bidRequests.reduce((sum, b) => sum + (b.response_count || 0), 0),
    readyForImport: bidRequests.filter(b => (b.response_count || 0) > 0 && b.status === 'open').length,
    totalContractors: contractors.length
  }), [bidRequests, contractors]);

  // Helpers
  const getTradeBadgeColor = (trade) => {
    const colors = {
      framing: 'bg-orange-100 text-orange-700', electrical: 'bg-yellow-100 text-yellow-700',
      plumbing: 'bg-blue-100 text-blue-700', hvac: 'bg-purple-100 text-purple-700',
      concrete: 'bg-gray-100 text-gray-700', general: 'bg-green-100 text-green-700',
      roofing: 'bg-red-100 text-red-700', drywall: 'bg-indigo-100 text-indigo-700',
      painting: 'bg-pink-100 text-pink-700', flooring: 'bg-teal-100 text-teal-700',
      tile: 'bg-cyan-100 text-cyan-700', cabinetry: 'bg-amber-100 text-amber-700'
    };
    return colors[trade?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <Image className="w-5 h-5 text-purple-500" />;
    if (['pdf'].includes(ext)) return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const getCategoryLabel = (cat) => {
    const labels = { plans: 'Plans & Specs', w9: 'W-9', coi: 'Insurance', lien_waiver: 'Lien Waiver', contract: 'Contract', general: 'General' };
    return labels[cat] || cat;
  };

  // v2.5.5 Deal Workspace rollout flag — see /app/frontend/src/lib/featureFlags.js
  const v255 = isV255User(user);

  // v2.5.5 — when the drilled-in project is funded, its bid workspace becomes
  // read-only: bids are archived and everything is managed from the active
  // build (Budget & Progress). Owners can still preview bids, just not edit.
  const readOnlyWorkspace = v255 && bidFilterProject !== 'all' && pipelineStages[bidFilterProject] === 'funded';

  // Main tabs (always visible).
  // v2.5.5.3 — for v2.5.5 users, "Bid Requests" is moved to the More dropdown
  // (the per-project Deal Workspace replaces it as the primary entry point).
  // Other users continue to see the legacy primary tab.
  const mainTabs = v255
    ? [
        { id: 'pipeline', label: 'Pipeline', icon: Briefcase },
        { id: 'bidsheets', label: 'Bid Sheets', icon: FileSpreadsheet },
        { id: 'contractors', label: 'Contractors', icon: Users },
        { id: 'messages', label: 'Messages', icon: Mail },
        { id: 'documents', label: 'Documents', icon: FolderOpen },
        { id: 'reviews', label: 'Reviews', icon: ClipboardCheck }
      ]
    : [
        { id: 'pipeline', label: 'Pipeline', icon: Briefcase },
        { id: 'bids', label: 'Bid Requests', icon: Gavel },
        { id: 'bidsheets', label: 'Bid Sheets', icon: FileSpreadsheet },
        { id: 'contractors', label: 'Contractors', icon: Users },
        { id: 'messages', label: 'Messages', icon: Mail },
        { id: 'documents', label: 'Documents', icon: FolderOpen },
        { id: 'reviews', label: 'Reviews', icon: ClipboardCheck }
      ];
  
  const [showScheduleMenu, setShowScheduleMenu] = useState(false);

  // More tabs (in dropdown). v2.5.5 users no longer get a global "All Bid
  // Requests" escape hatch — bids are strictly project-scoped and reached by
  // drilling into a project from the Pipeline.
  const moreTabs = v255
    ? [
        { id: 'aiextract', label: 'AI Extract', icon: Sparkles },
        { id: 'compare', label: 'Compare', icon: BarChart3 },
        { id: 'howto', label: 'How it Works', icon: HelpCircle }
      ]
    : [
        { id: 'aiextract', label: 'AI Extract', icon: Sparkles },
        { id: 'compare', label: 'Compare', icon: BarChart3 },
        { id: 'howto', label: 'How it Works', icon: HelpCircle }
      ];

  const allTabs = [...mainTabs, ...moreTabs];
  const visibleMainTabs = contractorsOnly ? mainTabs.filter(t => t.id === 'contractors') : mainTabs;
  const visibleMoreTabs = contractorsOnly ? [] : moreTabs;
  const currentTab = allTabs.find(t => t.id === activeTab);
  const isMoreTabActive = visibleMoreTabs.some(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Mobile nav drawer overlay (below lg) */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileNavOpen(false)} data-testid="hub-nav-overlay" />
      )}
      {/* Left Sidebar Navigation — fixed on desktop, slide-in hamburger drawer on mobile */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-900 flex flex-col h-full z-50 transform transition-transform duration-300 lg:translate-x-0 lg:z-30 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Close (mobile drawer only) */}
        <button onClick={() => setMobileNavOpen(false)} className="lg:hidden absolute top-4 right-3 text-slate-400 hover:text-white z-10" data-testid="hub-nav-close" aria-label="Close menu">
          <X className="w-5 h-5" />
        </button>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-slate-700">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg text-white whitespace-nowrap">DrawReady</h1>
              <p className="text-xs text-slate-400 whitespace-nowrap">Construction Finance</p>
            </div>
          </button>
        </div>

        {/* Page Title Section */}
        <div className="px-4 py-3 border-b border-slate-700">
          <h2 className="font-bold text-white">Project Hub</h2>
          <p className="text-xs text-slate-400">Bids & scopes</p>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {/* Dashboard */}
            <li>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Dashboard</span>
              </button>
            </li>
            
            {/* Main Project Hub Tabs */}
            {visibleMainTabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => { setActiveTab(tab.id); setShowCreateBid(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{tab.label}</span>
                  {tab.id === 'messages' && unreadMessageCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </button>
              </li>
            ))}

            {/* Divider */}
            {visibleMoreTabs.length > 0 && (
              <li className="pt-2">
                <div className="border-t border-slate-700 my-2"></div>
              </li>
            )}
            
            {/* More tabs */}
            {visibleMoreTabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => { setActiveTab(tab.id); setShowCreateBid(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section - User & Logout */}
        <div className="border-t border-slate-700 py-4">
          <ul className="space-y-1 px-3">
            <li>
              <button
                onClick={() => navigate('/contact')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Help & Support</span>
              </button>
            </li>
          </ul>

          {/* User Info & Logout */}
          {user && (
            <div className="mt-4 px-3">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-medium">
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                    {v255 && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm leading-none flex-shrink-0"
                        title="You're on the v2.5.5 early-access build"
                        data-testid="beta-pill"
                      >
                        Beta
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
              </div>
              <div className="mt-2" data-testid="sidebar-notifications">
                <NotificationBell userId={user.id} variant="sidebar" expanded={true} />
              </div>
              <button
                onClick={onLogout}
                className="mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Mobile Header - Only visible on small screens */}
        <div className="lg:hidden bg-white border-b shadow-sm sticky top-0 z-40">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setMobileNavOpen(true)} data-testid="hub-mobile-menu-toggle" className="p-2 -ml-2 rounded-lg hover:bg-slate-100" aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </button>
              <span className="font-bold text-lg text-slate-900">Project Hub</span>
              <span className="w-6" />
            </div>
          </div>
        </div>

        {/* Guided tour launcher — auto-shows once per tab, re-openable anytime */}
        <div className="max-w-7xl mx-auto px-4 pt-3 flex justify-end">
          <TourLauncher tourKey={`hub-${activeTab}`} userId={user?.id} />
        </div>


      {/* ==================== PIPELINE TAB (Phase 2) ==================== */}
      {activeTab === 'pipeline' && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white py-6 px-6 rounded-2xl mb-6">
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Briefcase className="w-7 h-7" /> Deal Pipeline
            </h1>
            <p className="text-slate-200 mt-1 text-sm">
              Every project, every stage — from collecting bids through funded build.
            </p>
          </div>
          <PipelineGrid
            user={user}
            onOpenProject={(projectId) => {
              // Deterministically open the project's Deal Workspace via state
              // (a same-route navigate() was unreliable → "dead clicks"). Also
              // sync the URL for refresh/back-button + shareability.
              setShowCreateBid(false);
              setShowNewBidProjectModal(false);
              setBidFilterProject(projectId);
              setActiveTab('bids');
              setSearchParams({ tab: 'bids', project: projectId });
            }}
            onNewDeal={() => {
              // Match the existing Bid Requests page button logic so we don't bypass
              // the 3-step "Create Project for Bid" modal. v2.5 users get the new
              // modal; others get the legacy scope-only create flow.
              setActiveTab('bids');
              const isV25User = (user?.email || '').toLowerCase() === 'modteetsw@gmail.com';
              if (isV25User) {
                setShowNewBidProjectModal(true);
              } else {
                setShowCreateBid(true);
              }
            }}
          />
        </div>
      )}

      {/* ==================== BID REQUESTS TAB ==================== */}
      {/* v2.5.5 — no global bid list. When a v2.5.5 user lands on the bids tab
          without a project selected, show a picker gate that routes them into
          a project's Deal Workspace (bids are strictly project-scoped). */}
      {activeTab === 'bids' && !showCreateBid && v255 && bidFilterProject === 'all' && (
        <div className="max-w-3xl mx-auto px-4 py-16" data-testid="bids-project-gate">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Gavel className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Bids live inside each project</h2>
              <p className="text-slate-500 mb-6">
                Every bid request belongs to a property. Pick a project from your pipeline to open its Deal Workspace, or choose one below.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Select value="" onValueChange={(val) => { if (val) { setBidFilterProject(val); } }}>
                  <SelectTrigger className="w-72 bg-white" data-testid="bids-gate-project-select">
                    <SelectValue placeholder="Open a project's bids…" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={() => setActiveTab('pipeline')} variant="outline" data-testid="bids-gate-pipeline-btn">
                  <Briefcase className="w-4 h-4 mr-2" /> Go to Pipeline
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'bids' && !showCreateBid && !(v255 && bidFilterProject === 'all') && (
        <>
          {v255 && bidFilterProject !== 'all' ? (
            /* Project-scoped Deal Workspace header (drilled in from a Pipeline tile).
               Hides global Bid Requests chrome — no "Compare by Trade" / stats /
               quick-invites — so this feels like a dedicated per-project page. */
            <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white py-6">
              <div className="max-w-7xl mx-auto px-4">
                <button
                  onClick={() => { setBidFilterProject('all'); setActiveTab('pipeline'); }}
                  className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1 mb-2"
                  data-testid="deal-workspace-back-btn"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to Deal Pipeline
                </button>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                      <Briefcase className="w-7 h-7" />
                      Deal Workspace
                    </h1>
                    <p className="text-slate-300 mt-1 text-sm">
                      Everything you need for this property's bids — scopes, responses, and reminders in one place.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><Gavel className="w-7 h-7" /> Bid Requests</h1>
                  <p className="text-blue-100 mt-1">Send bid requests and receive contractor bids</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      loadTradeComparison();
                      setShowTradeComparison(true);
                    }} 
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  >
                    <Scale className="w-4 h-4 mr-2" /> Compare by Trade
                  </Button>
                  <Button variant="outline" onClick={() => setShowQuickBidInvite(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <Send className="w-4 h-4 mr-2" /> Quick Bid Invite
                  </Button>
                  <Button variant="outline" onClick={() => setShowUploadBid(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <Upload className="w-4 h-4 mr-2" /> Upload Existing Bid
                  </Button>
                  {can(PERMISSIONS.CREATE_BIDS) && (
                    <Button
                      onClick={() => {
                        // v2.5: route owner to new 2-step modal. Others keep legacy.
                        const isV25User = (user?.email || '').toLowerCase() === 'modteetsw@gmail.com';
                        if (isV25User) {
                          setShowNewBidProjectModal(true);
                        } else {
                          setShowCreateBid(true);
                        }
                      }}
                      className="bg-white text-blue-600 hover:bg-blue-50"
                      data-testid="new-bid-request-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> New Bid Request
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Stats — hidden when v2.5.5 user is drilled into a single project (already shown in workspace header) */}
          {!(v255 && bidFilterProject !== 'all') && (
          <div className="max-w-7xl mx-auto px-4 -mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Open Requests', value: stats.openRequests, icon: Send, color: 'blue' },
                { label: 'Bids Received', value: stats.bidsReceived, icon: FileCheck, color: 'green' },
                { label: 'Ready for Import', value: stats.readyForImport, icon: FileSpreadsheet, color: 'purple' },
                { label: 'Contractors', value: stats.totalContractors, icon: Users, color: 'orange' }
              ].map((stat, i) => (
                <Card key={i} className="bg-white shadow-sm">
                  <CardContent className="p-5 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{stat.label}</p>
                      <p className={`text-3xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 bg-${stat.color}-100 rounded-full flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          )}

          {/* Quick Bid Invite History - Collapsible — global, hidden inside a single-project workspace for v2.5.5 users */}
          {!(v255 && bidFilterProject !== 'all') && quickBidInvites.length > 0 && (
            <div className="max-w-7xl mx-auto px-4 mb-4">
              <Card className="bg-white border-green-200">
                <CardHeader 
                  className="py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setShowQuickInviteHistory(!showQuickInviteHistory)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Send className="w-5 h-5 text-green-600" />
                      <CardTitle className="text-base">Quick Bid Invites</CardTitle>
                      <Badge variant="outline" className="ml-2">
                        {quickBidInvites.filter(i => i.status === 'pending').length} pending
                      </Badge>
                      {quickBidInvites.filter(i => i.status === 'uploaded').length > 0 && (
                        <Badge className="bg-green-100 text-green-700 ml-1">
                          {quickBidInvites.filter(i => i.status === 'uploaded').length} uploaded
                        </Badge>
                      )}
                    </div>
                    {showQuickInviteHistory ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </CardHeader>
                {showQuickInviteHistory && (
                  <CardContent className="pt-0">
                    <div className="divide-y">
                      {quickBidInvites.map(invite => (
                        <div key={invite.id} className="py-3 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              invite.status === 'uploaded' ? 'bg-green-100' : 'bg-yellow-100'
                            }`}>
                              {invite.status === 'uploaded' ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                              ) : (
                                <Clock className="w-5 h-5 text-yellow-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {invite.contractor?.company || invite.contractor?.name || invite.contractor_email}
                              </p>
                              <p className="text-xs text-slate-500">
                                {invite.project?.name} {invite.trade_type && `• ${invite.trade_type}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <Badge className={
                                invite.status === 'uploaded' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-yellow-100 text-yellow-700'
                              }>
                                {invite.status === 'uploaded' ? 'Bid Received' : 'Pending'}
                              </Badge>
                              <p className="text-xs text-slate-400 mt-1">
                                Sent {formatDate(invite.created_at)}
                              </p>
                            </div>
                            {invite.status === 'pending' && (
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  type="button"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    
                                    if (!user?.id) {
                                      toast.error('Please log in to send reminders');
                                      return;
                                    }
                                    
                                    // Build the same URL as copy button
                                    const portalUrl = `${window.location.origin}/contractor-portal?email=${encodeURIComponent(invite.contractor_email)}&quick_upload=${invite.token}`;
                                    
                                    try {
                                      toast.loading('Sending reminder email...');
                                      
                                      // Direct fetch call to avoid any axios issues
                                      const apiUrl = process.env.REACT_APP_BACKEND_URL;
                                      const response = await fetch(`${apiUrl}/api/quick-bid-invites/${invite.id}/remind?user_id=${user.id}`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' }
                                      });
                                      
                                      toast.dismiss();
                                      
                                      if (response.ok) {
                                        toast.success(`Reminder email sent to ${invite.contractor_email}!`);
                                      } else {
                                        const errorData = await response.json();
                                        toast.error(errorData.detail || 'Failed to send reminder');
                                      }
                                    } catch (error) {
                                      toast.dismiss();
                                      console.error('Reminder error:', error);
                                      toast.error('Failed to send reminder. Please try again.');
                                    }
                                  }}
                                  title="Send reminder email"
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                >
                                  <Bell className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const url = `${window.location.origin}/contractor-portal?email=${encodeURIComponent(invite.contractor_email)}&quick_upload=${invite.token}`;
                                    navigator.clipboard.writeText(url);
                                    toast.success('Invite link copied to clipboard');
                                  }}
                                  title="Copy invite link"
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          )}

          {/* Bid Requests List */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Project-scoped workspace header — v2.5.5 only, when drilled in from a Pipeline tile */}
            {v255 && bidFilterProject !== 'all' && (() => {
              const projBase = projects.find(p => p.id === bidFilterProject);
              if (!projBase) return null;
              const proj = { ...projBase, stage: pipelineStages[projBase.id] || projBase.stage };
              const awaitingBids = filteredBidRequests.filter(b => (b.response_count || 0) === 0 && b.status !== 'closed' && b.status !== 'awarded');
              return (
                <>
                {readOnlyWorkspace && (
                  <div
                    className="mb-4 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 flex items-start justify-between gap-4 flex-wrap"
                    data-testid="funded-workspace-banner"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-emerald-900">This project is funded</p>
                        <p className="text-sm text-emerald-700">
                          Bids are locked and archived here. Manage budget, phases and draws from the active build.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/project/${proj.id}`)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="funded-go-to-build-btn"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" /> Go to Budget &amp; Progress
                    </Button>
                  </div>
                )}
                <ProjectBidWorkspaceHeader
                  project={proj}
                  bidRequests={filteredBidRequests}
                  user={user}
                  readOnly={readOnlyWorkspace}
                  onAddScope={readOnlyWorkspace ? null : () => setShowCreateBid(true)}
                  onUploadBid={readOnlyWorkspace ? null : () => {
                    // Pre-fill the Upload Existing Bid dialog with this project
                    // so the user doesn't have to pick from a dropdown that
                    // would just collapse to one option anyway.
                    setUploadBidData(prev => ({ ...prev, project_id: proj.id }));
                    setShowUploadBid(true);
                  }}
                  onSendReminders={readOnlyWorkspace ? null : async () => {
                    if (!user?.id) { toast.error('Please log in to send reminders'); return; }
                    if (awaitingBids.length === 0) { toast.info('No awaiting bid requests on this project'); return; }
                    let sent = 0, failed = 0;
                    for (const bid of awaitingBids) {
                      try {
                        await apiService.sendBidRequestReminders(bid.id, user.id);
                        sent++;
                      } catch (e) { failed++; }
                    }
                    if (sent > 0) toast.success(`Reminders sent for ${sent} scope${sent === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
                    else toast.error('No reminders sent — please try again');
                  }}
                  onClearFilter={() => { setBidFilterProject('all'); setActiveTab('pipeline'); }}
                />
                </>
              );
            })()}

            {/* Cross-project filter — hidden inside a single-project workspace for v2.5.5 users */}
            {!(v255 && bidFilterProject !== 'all') && (
            <div className="mb-4 flex items-center gap-3">
              <Label className="text-slate-600 whitespace-nowrap">Filter by Project:</Label>
              <Select value={bidFilterProject} onValueChange={setBidFilterProject}>
                <SelectTrigger className="w-64 bg-white">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bidFilterProject !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setBidFilterProject('all')} className="text-slate-500">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
              <span className="text-sm text-slate-500 ml-auto">
                Showing {filteredBidRequests.length} of {bidRequests.length} bid requests
              </span>
            </div>
            )}
            
            {isLoading ? (
              <div className="text-center py-12"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div></div>
            ) : filteredBidRequests.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-16 text-center">
                  <Gavel className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    {bidRequests.length === 0
                      ? 'No Bid Requests Yet'
                      : bidFilterProject !== 'all'
                        ? 'No bid requests on this project yet'
                        : 'No Matching Bid Requests'}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    {bidRequests.length === 0
                      ? 'Create your first bid request or upload an existing bid'
                      : bidFilterProject !== 'all'
                        ? 'Send your first scope out to start collecting bids for this project.'
                        : 'Try changing the project filter'}
                  </p>
                  <div className="flex justify-center gap-3">
                    {bidRequests.length === 0 ? (
                      <>
                        <Button onClick={() => setShowCreateBid(true)}><Plus className="w-4 h-4 mr-2" /> Create Bid Request</Button>
                        <Button variant="outline" onClick={() => setShowUploadBid(true)}><Upload className="w-4 h-4 mr-2" /> Upload Existing</Button>
                      </>
                    ) : bidFilterProject !== 'all' ? (
                      <>
                        <Button onClick={() => setShowCreateBid(true)} data-testid="empty-add-scope-btn"><Plus className="w-4 h-4 mr-2" /> Add a scope to this project</Button>
                        <Button variant="outline" onClick={() => setBidFilterProject('all')}>See all projects</Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={() => setBidFilterProject('all')}>Clear Filter</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredBidRequests.map(bid => (
                  <Card key={bid.id} className="bg-white hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            bid.trade_type === 'Electrical' ? 'bg-yellow-100' : 
                            bid.trade_type === 'Plumbing' ? 'bg-blue-100' :
                            bid.trade_type === 'HVAC' ? 'bg-cyan-100' :
                            bid.trade_type === 'Drywall' ? 'bg-orange-100' :
                            bid.trade_type === 'Framing' ? 'bg-amber-100' :
                            'bg-purple-100'
                          }`}>
                            <Gavel className={`w-6 h-6 ${
                              bid.trade_type === 'Electrical' ? 'text-yellow-600' : 
                              bid.trade_type === 'Plumbing' ? 'text-blue-600' :
                              bid.trade_type === 'HVAC' ? 'text-cyan-600' :
                              bid.trade_type === 'Drywall' ? 'text-orange-600' :
                              bid.trade_type === 'Framing' ? 'text-amber-600' :
                              'text-purple-600'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-1 flex-wrap">
                              <h3 className="font-semibold text-lg">{bid.title}</h3>
                              {/* Trade Type Badge - Prominent */}
                              {bid.trade_type && (
                                <Badge className={`text-sm px-3 py-1 ${
                                  bid.trade_type === 'Electrical' ? 'bg-yellow-500 text-white' : 
                                  bid.trade_type === 'Plumbing' ? 'bg-blue-500 text-white' :
                                  bid.trade_type === 'HVAC' ? 'bg-cyan-500 text-white' :
                                  bid.trade_type === 'Drywall' ? 'bg-orange-500 text-white' :
                                  bid.trade_type === 'Framing' ? 'bg-amber-500 text-white' :
                                  bid.trade_type === 'Excavation' ? 'bg-stone-500 text-white' :
                                  bid.trade_type === 'Water & Sewer' ? 'bg-teal-500 text-white' :
                                  'bg-purple-500 text-white'
                                }`}>
                                  {bid.trade_type}
                                </Badge>
                              )}
                              {!bid.trade_type && (
                                <Badge variant="outline" className="text-slate-400 border-slate-300">No Trade Set</Badge>
                              )}
                              <Badge className="bg-blue-100 text-blue-700">{bid.response_count || 0} BIDS</Badge>
                              {(bid.response_count || 0) > 0 && <Badge className="bg-green-100 text-green-700">Ready for Import</Badge>}
                              {bid.status === 'awarded' && <Badge className="bg-purple-100 text-purple-700"><Award className="w-3 h-3 mr-1" /> Awarded</Badge>}
                            </div>
                            <p className="text-slate-500 text-sm mb-2">{bid.project?.name} - {bid.project?.address}</p>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span><Calendar className="w-4 h-4 inline mr-1" /> Due: {formatDate(bid.due_date)}</span>
                              <span><Users className="w-4 h-4 inline mr-1" /> {bid.contractor_ids?.length || 0} contractors</span>
                            </div>
                            {bid.lowest_bid && (
                              <div className="flex items-center gap-3 mt-3">
                                <span className="text-sm text-slate-500">Lowest:</span>
                                <span className="text-lg font-bold text-green-600">${bid.lowest_bid.toLocaleString()}</span>
                                {bid.highest_bid && bid.highest_bid !== bid.lowest_bid && (
                                  <span className="text-sm text-slate-400">Range: ${(bid.lowest_bid / 1000).toFixed(1)}K - ${(bid.highest_bid / 1000).toFixed(1)}K</span>
                                )}
                              </div>
                            )}
                            {!bid.lowest_bid && bid.total_amount > 0 && (
                              <div className="flex items-center gap-3 mt-3">
                                <span className="text-sm text-slate-500">Bid Amount:</span>
                                <span className="text-lg font-bold text-blue-600">${bid.total_amount.toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Remind button for 0 BIDS */}
                          {!readOnlyWorkspace && (bid.response_count || 0) === 0 && (bid.contractor_ids?.length || 0) > 0 && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                              onClick={async () => {
                                if (!user?.id) {
                                  toast.error('Please log in to send reminders');
                                  return;
                                }
                                try {
                                  // Send reminder to all contractors who haven't responded
                                  const response = await apiService.sendBidRequestReminders(bid.id, user.id);
                                  toast.success(response.data?.message || `Reminders sent to ${bid.contractor_ids?.length || 0} contractor(s)!`);
                                } catch (error) {
                                  console.error('Failed to send reminders:', error);
                                  toast.error(error.response?.data?.detail || 'Failed to send reminders. Please try again.');
                                }
                              }}
                              data-testid={`remind-all-btn-${bid.id}`}
                            >
                              <Bell className="w-4 h-4 mr-2" /> Remind
                            </Button>
                          )}
                          {!readOnlyWorkspace && (bid.response_count || 0) > 0 && bid.status !== 'awarded' && (
                            <>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleImportToBidSheet(bid)}>
                                <FileSpreadsheet className="w-4 h-4 mr-2" /> Import to Bid Sheet
                              </Button>
                              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => handleOpenAwardModal(bid)}>
                                <Award className="w-4 h-4 mr-2" /> Award Bid
                              </Button>
                            </>
                          )}
                          {bid.status === 'awarded' && bid.awarded_response && (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-200">
                                <span className="text-xl">🏆</span>
                                <div className="text-sm">
                                  <div className="font-semibold text-purple-700">{bid.awarded_response.contractor_name || 'Contractor'}</div>
                                  <div className="text-purple-600">${parseFloat(bid.awarded_response.total_amount || 0).toLocaleString()}</div>
                                </div>
                              </div>
                              {!readOnlyWorkspace && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="outline">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenIssueContract(bid)}>
                                    <FileText className="w-4 h-4 mr-2" /> Issue / Reissue Contract
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => {
                                    const contractor = contractors.find(c => c.id === bid.awarded_contractor_id);
                                    if (contractor) handleOpenMessageModal(contractor, bid);
                                  }}>
                                    <Mail className="w-4 h-4 mr-2" /> Message Contractor
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleOpenTransferModal(bid)}>
                                    <ArrowRight className="w-4 h-4 mr-2" /> Transfer Award
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenRevokeModal(bid)} className="text-red-600">
                                    <XCircle className="w-4 h-4 mr-2" /> Revoke Award
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              )}
                            </div>
                          )}
                          {/* Enter bid manually — for contractors who quote offline (won't use the portal) */}
                          {!readOnlyWorkspace && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                              onClick={() => handleOpenManualBid(bid)}
                              data-testid={`enter-manual-bid-btn-${bid.id}`}
                            >
                              <Plus className="w-4 h-4 mr-2" /> Enter Bid
                            </Button>
                          )}
                          {/* Preview — what the contractor sees (always available, even when funded) */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPreviewBidId(bid.id)}
                            data-testid={`preview-bid-btn-${bid.id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" /> Preview
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => {
                            setSelectedComparisonTrade(bid.trade_type || '');
                            loadTradeComparison(bid.project_id || null);
                            setActiveTab('compare');
                          }}>
                            <BarChart3 className="w-4 h-4 mr-2" /> Compare
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/bid-request/${bid.id}`)}><Eye className="w-4 h-4 mr-2" /> View</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setPreviewBidId(bid.id)}><Eye className="w-4 h-4 mr-2" /> Preview as Contractor</DropdownMenuItem>
                              {!readOnlyWorkspace && (
                                <>
                                  <DropdownMenuItem onClick={() => handleEditBidRequest(bid)}><Edit2 className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenManualBid(bid)} data-testid={`enter-manual-bid-${bid.id}`}><Plus className="w-4 h-4 mr-2" /> Enter bid manually</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleOpenSendToMore(bid)}><UserPlus className="w-4 h-4 mr-2" /> Send to More Contractors</DropdownMenuItem>
                                  {bid.status === 'awarded' && bid.awarded_contractor_id && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleOpenIssueContract(bid)} className="text-blue-700 font-semibold" data-testid={`issue-contract-from-list-${bid.id}`}>
                                        <FileText className="w-4 h-4 mr-2" /> Issue / Reissue Contract
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDeleteBidRequest(bid.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Bid Request Form */}
      {activeTab === 'bids' && showCreateBid && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <button onClick={() => setShowCreateBid(false)} className="flex items-center gap-2 text-blue-100 hover:text-white mb-4 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to Bid Requests
              </button>
              <h1 className="text-2xl font-bold flex items-center gap-3"><Gavel className="w-7 h-7" /> Create Bid Request</h1>
              <p className="text-blue-100 mt-1">Send bid requests to contractors</p>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <Card className="bg-white">
                  <CardHeader className="border-b"><CardTitle>Bid Details</CardTitle></CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <Label>Project</Label>
                      <Select value={newBidRequest.project_id} onValueChange={(v) => setNewBidRequest({...newBidRequest, project_id: v})}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent>
                          {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bid Title</Label>
                      <Input className="mt-1.5" placeholder="e.g., Framing Work" value={newBidRequest.title} onChange={(e) => setNewBidRequest({...newBidRequest, title: e.target.value})} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label>Scope of Work</Label>
                        <div className="flex items-center gap-2">
                          {/* Template selector */}
                          {scopeTemplates.length > 0 && (
                            <Select 
                              value="" 
                              onValueChange={(templateId) => {
                                const template = scopeTemplates.find(t => t.id === templateId);
                                if (template) {
                                  setNewBidRequest(prev => ({
                                    ...prev,
                                    scope_of_work: template.scope_text,
                                    trade_type: template.trade_type.toLowerCase()
                                  }));
                                  toast.success(`Loaded template: ${template.name}`);
                                }
                              }}
                            >
                              <SelectTrigger className="w-48 h-8 text-xs">
                                <SelectValue placeholder="Load from template..." />
                              </SelectTrigger>
                              <SelectContent>
                                {scopeTemplates.map(t => (
                                  <SelectItem key={t.id} value={t.id}>
                                    <div className="flex flex-col">
                                      <span>{t.name}</span>
                                      <span className="text-xs text-slate-400">{t.trade_type}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {/* Save as template button */}
                          {newBidRequest.scope_of_work && newBidRequest.scope_of_work.length > 50 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 text-xs text-blue-600 hover:text-blue-700"
                              onClick={() => {
                                setNewScopeTemplate({
                                  name: newBidRequest.title || '',
                                  trade_type: newBidRequest.trade_type || '',
                                  scope_text: newBidRequest.scope_of_work,
                                  description: ''
                                });
                                setShowScopeTemplateManager(true);
                              }}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              Save as Template
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => setShowScopeTemplateManager(true)}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Templates
                          </Button>
                        </div>
                      </div>
                      <Textarea 
                        className="min-h-[120px]" 
                        placeholder="Describe the work in detail, or load from a template..." 
                        value={newBidRequest.scope_of_work} 
                        onChange={(e) => setNewBidRequest({...newBidRequest, scope_of_work: e.target.value})} 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Due Date</Label>
                        <Input type="date" className="mt-1.5" value={newBidRequest.due_date} onChange={(e) => setNewBidRequest({...newBidRequest, due_date: e.target.value})} />
                      </div>
                      <div>
                        <Label>Trade Type</Label>
                        <Select value={newBidRequest.trade_type} onValueChange={(v) => {
                          // Auto-attach the matching built-in scope template when a trade
                          // is picked — only if the scope field is still empty, so we never
                          // overwrite what the user typed.
                          setNewBidRequest(prev => {
                            const next = { ...prev, trade_type: v };
                            if (!prev.scope_of_work || !prev.scope_of_work.trim()) {
                              const match = scopeTemplates.find(
                                t => (t.trade_type || '').toLowerCase() === v && !(t.trade_type || '').toLowerCase().startsWith('supplier')
                              );
                              if (match) {
                                next.scope_of_work = match.scope_text;
                                toast.success(`Scope pre-filled from "${match.name}"`);
                              }
                            }
                            return next;
                          });
                        }}>
                          <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select trade" /></SelectTrigger>
                          <SelectContent>
                            {trades.filter(t => t !== 'All Trades').map(t => <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea className="mt-1.5" placeholder="Additional instructions..." value={newBidRequest.notes} onChange={(e) => setNewBidRequest({...newBidRequest, notes: e.target.value})} rows={2} />
                    </div>
                    
                    {/* Document/Plans Upload Section */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Attach Plans/Documents
                      </Label>
                      
                      {/* Option to select from existing documents */}
                      {newBidRequest.project_id && documents.filter(d => d.project_id === newBidRequest.project_id).length > 0 && (
                        <div className="mt-2 mb-3">
                          <p className="text-xs text-slate-500 mb-2">Select from existing project documents:</p>
                          <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                            {documents
                              .filter(d => d.project_id === newBidRequest.project_id)
                              .map(doc => (
                                <label 
                                  key={doc.id} 
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${
                                    newBidRequest.document_ids?.includes(doc.id) ? 'bg-blue-50 border border-blue-200' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={newBidRequest.document_ids?.includes(doc.id) || false}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setNewBidRequest(prev => ({
                                          ...prev,
                                          document_ids: [...(prev.document_ids || []), doc.id]
                                        }));
                                      } else {
                                        setNewBidRequest(prev => ({
                                          ...prev,
                                          document_ids: (prev.document_ids || []).filter(id => id !== doc.id)
                                        }));
                                      }
                                    }}
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  <span className="text-sm text-slate-700 truncate flex-1">
                                    {doc.name || doc.file_name || doc.filename || 'Unnamed document'}
                                  </span>
                                  <span className="text-xs text-slate-400 flex-shrink-0">{doc.category || 'document'}</span>
                                </label>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Upload new documents */}
                      <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length === 0) return;
                            
                            // Check if project is selected
                            if (!newBidRequest.project_id) {
                              toast.error('Please select a project first before uploading documents');
                              e.target.value = ''; // Reset file input
                              return;
                            }
                            
                            for (const file of files) {
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('project_id', newBidRequest.project_id);
                                formData.append('category', 'plans');
                                
                                const res = await apiService.uploadDocument(formData);
                                if (res.data?.id) {
                                  setNewBidRequest(prev => ({
                                    ...prev,
                                    document_ids: [...(prev.document_ids || []), res.data.id]
                                  }));
                                  // Also add to documents list so it appears in the selector
                                  setDocuments(prev => [...prev, res.data]);
                                  toast.success(`Uploaded: ${file.name}`);
                                }
                              } catch (err) {
                                console.error('Upload error:', err);
                                toast.error(`Failed to upload: ${file.name}`);
                              }
                            }
                          }}
                          className="hidden"
                          id="bid-request-docs"
                        />
                        <label htmlFor="bid-request-docs" className="cursor-pointer">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload new plans, specs, or drawings</p>
                          <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, DWG, DXF</p>
                        </label>
                      </div>
                      {newBidRequest.document_ids?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {newBidRequest.document_ids.map((docId, idx) => {
                            const doc = documents.find(d => d.id === docId);
                            return (
                              <div key={docId} className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                <FileText className="w-3 h-3" />
                                <span>{doc?.name || doc?.file_name || doc?.filename || `Document ${idx + 1}`}</span>
                                <button
                                  onClick={() => setNewBidRequest(prev => ({
                                    ...prev,
                                    document_ids: prev.document_ids.filter(id => id !== docId)
                                  }))}
                                  className="ml-1 text-blue-500 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    {/* AI Material Takeoff - Coming Soon */}
                    <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200 opacity-60">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-slate-400" />
                        <Label className="font-semibold text-slate-500">AI Material Takeoff</Label>
                        <Badge variant="outline" className="text-slate-400 border-slate-300 text-xs">Coming Soon</Badge>
                      </div>
                      <p className="text-xs text-slate-500">
                        AI-powered material estimates from construction plans will be available in a future update.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contractors Selection */}
              <div className="space-y-6">
                <Card className="bg-white">
                  <CardHeader className="border-b">
                    <div className="flex items-center justify-between">
                      <CardTitle>Select Contractors</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => setShowAddContractor(true)}><UserPlus className="w-4 h-4 mr-1" /> Add New</Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="mb-4">
                      <Label className="text-sm mb-2 block">Filter by Trade:</Label>
                      <Select value={selectedTrade === 'all' ? 'All Trades' : selectedTrade} onValueChange={(val) => setSelectedTrade(val === 'All Trades' ? 'all' : val)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a trade" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {contractorTrades.map(trade => (
                            <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedTrade !== 'all' && selectedTrade !== 'All Trades' && (
                        <div className="mt-2 flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700">{selectedTrade}</Badge>
                          <button onClick={() => setSelectedTrade('all')} className="text-xs text-slate-500 hover:text-slate-700">Clear filter</button>
                        </div>
                      )}
                    </div>

                    {contractors.length === 0 ? (
                      <div className="py-8 text-center">
                        <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500 mb-3">No contractors yet</p>
                        <Button size="sm" onClick={() => setShowAddContractor(true)}><Plus className="w-4 h-4 mr-1" /> Add Contractor</Button>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[350px] overflow-y-auto">
                        {matchingContractors.map(c => (
                          <label key={c.id} className="flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-slate-50">
                            <div className="flex items-center gap-3">
                              <input type="checkbox" checked={newBidRequest.contractor_ids.includes(c.id)} onChange={() => {
                                setNewBidRequest(prev => ({
                                  ...prev,
                                  contractor_ids: prev.contractor_ids.includes(c.id) ? prev.contractor_ids.filter(id => id !== c.id) : [...prev.contractor_ids, c.id]
                                }));
                              }} className="w-5 h-5 rounded" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{c.company || c.name}</p>
                                  {c.trade && <Badge className={getTradeBadgeColor(c.trade)}>{c.trade}</Badge>}
                                </div>
                                <p className="text-sm text-slate-500">{c.email}</p>
                              </div>
                            </div>
                            <Badge className={c.w9_received ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{c.w9_received ? 'W-9 ✓' : 'Pending'}</Badge>
                          </label>
                        ))}
                        {otherContractors.length > 0 && (
                          <>
                            <div className="text-sm text-slate-500 mt-4 mb-2"><Filter className="w-4 h-4 inline mr-1" /> Other trades:</div>
                            {otherContractors.map(c => (
                              <label key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-dashed cursor-pointer hover:bg-slate-50 opacity-70">
                                <div className="flex items-center gap-3">
                                  <input type="checkbox" checked={newBidRequest.contractor_ids.includes(c.id)} onChange={() => {
                                    setNewBidRequest(prev => ({
                                      ...prev,
                                      contractor_ids: prev.contractor_ids.includes(c.id) ? prev.contractor_ids.filter(id => id !== c.id) : [...prev.contractor_ids, c.id]
                                    }));
                                  }} className="w-5 h-5 rounded" />
                                  <div>
                                    <p className="font-medium">{c.company || c.name}</p>
                                    {c.trade && <Badge className={getTradeBadgeColor(c.trade)}>{c.trade}</Badge>}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t text-sm"><span className="font-medium text-blue-600">{newBidRequest.contractor_ids.length}</span> selected</div>
                  </CardContent>
                </Card>

                <Card className="bg-blue-600 text-white">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-4"><Send className="w-5 h-5" /> <span className="font-semibold">Ready to Send</span></div>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between"><span className="text-blue-200">Contractors:</span><span>{newBidRequest.contractor_ids.length}</span></div>
                      <div className="flex justify-between"><span className="text-blue-200">Due Date:</span><span>{newBidRequest.due_date ? formatDate(newBidRequest.due_date) : 'Not set'}</span></div>
                      {newBidRequest.document_ids?.length > 0 && (
                        <div className="flex justify-between"><span className="text-blue-200">Attachments:</span><span>{newBidRequest.document_ids.length} file(s)</span></div>
                      )}
                    </div>
                    <Button className="w-full bg-white text-blue-600 hover:bg-blue-50" onClick={handleCreateBidRequest} disabled={isSubmitting || newBidRequest.contractor_ids.length === 0}>
                      {isSubmitting ? 'Sending...' : 'Send Bid Requests'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ==================== BID SHEETS TAB ==================== */}
      {activeTab === 'bidsheets' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><FileSpreadsheet className="w-7 h-7" /> Bid Sheets</h1>
                  <p className="text-blue-100 mt-1">Create bid sheets for bank pre-approval</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={() => setIsAIWizardOpen(true)} 
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white"
                    data-testid="ai-wizard-btn"
                  >
                    <Wand2 className="w-4 h-4 mr-2" /> New Bid Sheet with AI
                  </Button>
                  <Button onClick={() => navigate('/bid-sheet/new')} variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                    <Plus className="w-4 h-4 mr-2" /> Create Manually
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* AI Wizard Component */}
            <BidSheetAIWizard 
              user={user}
              open={isAIWizardOpen}
              onOpenChange={setIsAIWizardOpen}
              onBidSheetCreated={(newSheet) => {
                loadData();
                toast.success('Bid sheet created with AI mapping!');
              }}
            />

            {/* Workflow Banner */}
            <Alert className="bg-blue-50 border-blue-200 mb-6">
              <HelpCircle className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                    <span>Create with AI or manually</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                    <span>Review & finalize</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                    <span>Submit to bank</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</div>
                    <span>Bank approves → Start project</span>
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            {bidSheets.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-16 text-center">
                  <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Bid Sheets Yet</h3>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">Create your first bid sheet using AI to automatically map your costs to bank categories, or create one manually</p>
                  <div className="flex justify-center gap-3">
                    <Button 
                      onClick={() => setIsAIWizardOpen(true)}
                      className="bg-gradient-to-r from-indigo-500 to-purple-500"
                    >
                      <Wand2 className="w-4 h-4 mr-2" /> Start with AI
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/bid-sheet/new')}>
                      <Plus className="w-4 h-4 mr-2" /> Create Manually
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bidSheets.map(sheet => {
                  const getStatusBadge = (status, hasBankTemplate = false) => {
                    const statusConfig = {
                      draft: { label: hasBankTemplate ? 'Pending Bank Approval' : 'Draft', className: hasBankTemplate ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700' },
                      submitted: { label: 'Submitted to Bank', className: 'bg-blue-100 text-blue-700' },
                      approved: { label: 'Bank Approved', className: 'bg-green-100 text-green-700' },
                      converted: { label: 'Converted to Project', className: 'bg-purple-100 text-purple-700' }
                    };
                    return statusConfig[status] || statusConfig.draft;
                  };
                  const status = getStatusBadge(sheet.status, !!sheet.bank_template_id);
                  const isApproved = sheet.status === 'approved';
                  const isSubmitted = sheet.status === 'submitted';
                  const isDraft = sheet.status === 'draft';
                  
                  return (
                    <Card key={sheet.id} className={`bg-white hover:shadow-md transition-shadow ${isApproved ? 'border-l-4 border-green-500' : ''}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{sheet.name}</h3>
                              <Badge className={status.className}>
                                {isApproved && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                {status.label}
                              </Badge>
                              {sheet.bank_template_id && (
                                <Badge className="bg-indigo-100 text-indigo-700">
                                  <Wand2 className="w-3 h-3 mr-1" /> AI Mapped
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-500 text-sm mb-3">{sheet.property_address || 'No address'}</p>
                            <div className="flex items-center gap-6 text-sm">
                              <span className="text-slate-600"><strong>Total Budget:</strong> ${(sheet.total_cost || 0).toLocaleString()}</span>
                              {sheet.bank_submission_date && (
                                <span className="text-slate-600"><strong>Submitted:</strong> {formatDate(sheet.bank_submission_date)}</span>
                              )}
                              {sheet.approved_amount && (
                                <span className="text-green-600"><strong>Approved:</strong> ${sheet.approved_amount.toLocaleString()}</span>
                              )}
                              {sheet.contractors_awarded?.length > 0 && (
                                <span className="text-purple-600"><strong>Contractors Awarded:</strong> {sheet.contractors_awarded.length}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isApproved && !sheet.contractors_awarded?.length && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await apiService.awardContractorsFromBidSheet(sheet.id, user.id);
                                    toast.success('Contractors awarded and notified!');
                                    loadData();
                                  } catch (e) {
                                    toast.error('Failed to award contractors');
                                  }
                                }}
                              >
                                <Award className="w-4 h-4 mr-2" /> Award Contractors
                              </Button>
                            )}
                            {isSubmitted && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await apiService.markBidSheetApproved(sheet.id, user.id);
                                    toast.success('Bid sheet marked as approved!');
                                    loadData();
                                  } catch (e) {
                                    toast.error('Failed to mark as approved');
                                  }
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Approved
                              </Button>
                            )}
                            {isDraft && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await apiService.submitBidSheetToBank(sheet.id, user.id);
                                    toast.success('Bid sheet submitted to bank!');
                                    loadData();
                                  } catch (e) {
                                    toast.error('Failed to submit');
                                  }
                                }}
                              >
                                <Landmark className="w-4 h-4 mr-2" /> Submit to Bank
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => navigate(`/bid-sheet/${sheet.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> View
                            </Button>
                            {/* Bank Format Export for AI-mapped sheets */}
                            {sheet.bank_template_id && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const res = await apiService.exportBidSheetToBankFormat(sheet.id);
                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `${sheet.name.replace(/\s+/g, '_')}_BankFormat.xlsm`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Exported to bank format!');
                                  } catch (e) {
                                    toast.error('Failed to export');
                                  }
                                }}
                              >
                                <Building2 className="w-4 h-4 mr-2" /> Bank Format
                              </Button>
                            )}
                            <Button size="sm" onClick={async () => {
                              try {
                                const res = await apiService.exportBidSheetPDF(sheet.id);
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `BidSheet_${sheet.name.replace(/\s+/g, '_')}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (e) {
                                toast.error('Export failed');
                              }
                            }}>
                              <Download className="w-4 h-4 mr-2" /> Export
                            </Button>
                            {/* v2.5.10: delete bid sheet — was missing from the Project Hub list */}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete bid sheet "${sheet.name}"? This cannot be undone.`)) return;
                                try {
                                  await apiService.deleteBidSheet(sheet.id);
                                  toast.success('Bid sheet deleted');
                                  loadData();
                                } catch (err) {
                                  toast.error('Failed to delete bid sheet');
                                }
                              }}
                              data-testid={`delete-bidsheet-${sheet.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ==================== CONTRACTORS TAB ==================== */}
      {activeTab === 'contractors' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><Users className="w-7 h-7" /> Contractors</h1>
                  <p className="text-blue-100 mt-1">Manage contractors, payments, and cost reporting</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowInviteContractors(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="invite-contractors-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Invite Contractors
                  </Button>
                  <Button variant="outline" onClick={() => setShowContractorPortal(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <Eye className="w-4 h-4 mr-2" /> Preview Contractor Portal
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/contractor-payments')} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="open-payment-report">
                    <FileText className="w-4 h-4 mr-2" /> Payment Report
                  </Button>
                  {can(PERMISSIONS.MANAGE_CONTRACTORS) && (
                    <Button onClick={() => setShowAddContractor(true)} className="bg-white text-blue-600 hover:bg-blue-50">
                      <Plus className="w-4 h-4 mr-2" /> Add Contractor
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Contractors</p>
                  <p className="text-2xl font-bold text-slate-800">{contractors.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">With W-9</p>
                  <p className="text-2xl font-bold text-green-600">{contractors.filter(c => c.w9_received).length}</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">YTD Paid</p>
                  <p className="text-2xl font-bold text-slate-800">${contractorPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Payments Logged</p>
                  <p className="text-2xl font-bold text-slate-800">{contractorPayments.length}</p>
                </CardContent>
              </Card>
            </div>

            {/* Trade Filters */}
            <div className="mb-6">
              <Select value={selectedTrade === 'all' ? 'All Trades' : selectedTrade} onValueChange={(val) => setSelectedTrade(val === 'All Trades' ? 'all' : val)}>
                <SelectTrigger className="w-full max-w-xs bg-white">
                  <SelectValue placeholder="Filter by trade" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {contractorTrades.map(trade => (
                    <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTrade !== 'all' && selectedTrade !== 'All Trades' && (
                <div className="mt-2 flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700">{selectedTrade}</Badge>
                  <button onClick={() => setSelectedTrade('all')} className="text-xs text-slate-500 hover:text-slate-700">Clear filter</button>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search contractors..." className="pl-10 bg-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            {filteredContractors.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-16 text-center">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Contractors</h3>
                  <p className="text-slate-500 mb-6">Add your first contractor</p>
                  <Button onClick={() => setShowAddContractor(true)}><Plus className="w-4 h-4 mr-2" /> Add Contractor</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredContractors.map(c => {
                  const ytdPaid = contractorYTDTotals[c.id] || 0;
                  const payments = getContractorPayments(c.id);
                  const isExpanded = expandedContractors[c.id];
                  
                  return (
                    <Card key={c.id} className="bg-white hover:shadow-md transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-4">
                            {/* Contractor Avatar - Show logo if available */}
                            {c.company_logo ? (
                              <img 
                                src={c.company_logo} 
                                alt={c.company || c.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                              />
                            ) : (
                              <div className={`w-12 h-12 ${getTradeBadgeColor(c.trade).replace('bg-', 'bg-').replace('-100', '-500')} rounded-full flex items-center justify-center text-white font-bold`}>
                                {(c.company || c.name || '??').substring(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{c.company}</h3>
                                {c.trade && <Badge className={getTradeBadgeColor(c.trade)}>{c.trade}</Badge>}
                                {c.specialty && c.specialty !== c.trade && <Badge variant="outline">{c.specialty}</Badge>}
                              </div>
                              <p className="text-sm text-slate-500">{c.name} • {c.email}</p>
                              {/* W9/COI Status Badges - Click to Upload */}
                              <div className="flex gap-2 mt-2">
                                <Badge 
                                  className={`cursor-pointer ${c.w9_received ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                  onClick={() => {
                                    setSelectedContractor(c);
                                    setDocUploadType('w9');
                                    setDocUploadFile(null);
                                    setShowDocUploadDialog(true);
                                  }}
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  W-9 {c.w9_received ? '✓' : '- Upload'}
                                </Badge>
                                <Badge 
                                  className={`cursor-pointer ${c.coi_received && !c.coi_expired ? 'bg-green-100 text-green-700 hover:bg-green-200' : c.coi_expired ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                  onClick={() => {
                                    setSelectedContractor(c);
                                    setDocUploadType('coi');
                                    setDocUploadFile(null);
                                    setDocCoiExpiration('');
                                    setShowDocUploadDialog(true);
                                  }}
                                >
                                  <Shield className="w-3 h-3 mr-1" />
                                  COI {c.coi_received && !c.coi_expired ? '✓' : c.coi_expired ? '- Expired' : '- Upload'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* YTD Paid */}
                            <div className="text-right mr-2">
                              <p className="text-sm text-slate-500">YTD Paid</p>
                              <p className="text-lg font-semibold text-slate-800">${ytdPaid.toLocaleString()}</p>
                            </div>
                            
                            {can(PERMISSIONS.MANAGE_CONTRACTORS) && (
                              <Button variant="outline" size="sm" onClick={() => { setSelectedContractor({...c}); setShowEditContractor(true); }}>
                                <Edit2 className="w-4 h-4 mr-1" /> Edit
                              </Button>
                            )}
                            {can(PERMISSIONS.RECORD_PAYMENTS) && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { setSelectedContractor(c); setShowLogPayment(true); }}>
                                <DollarSign className="w-4 h-4 mr-1" /> Log Payment
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleRequestForms(c)}>
                              <FileCheck className="w-4 h-4 mr-1" /> Request W-9
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleRequestInsurance(c)}>
                              <Shield className="w-4 h-4 mr-1" /> Request COI
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleResendPortalEmail(c.id, c.email)} data-testid={`resend-portal-${c.id}`}>
                              <Mail className="w-4 h-4 mr-1" /> Portal
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {can(PERMISSIONS.CREATE_BIDS) && (
                                  <DropdownMenuItem onClick={() => { setActiveTab('bids'); setShowCreateBid(true); setNewBidRequest(prev => ({...prev, contractor_ids: [c.id]})); }}>
                                    <Send className="w-4 h-4 mr-2" /> Send Bid Request
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleResendPortalEmail(c.id, c.email)}>
                                  <Mail className="w-4 h-4 mr-2" /> Resend Portal Link
                                </DropdownMenuItem>
                                {can(PERMISSIONS.MANAGE_CONTRACTORS) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteContractor(c.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        
                        {/* Payment History Accordion */}
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <button
                            onClick={() => setExpandedContractors(prev => ({...prev, [c.id]: !prev[c.id]}))}
                            className="flex items-center justify-between w-full text-left text-sm font-medium text-slate-700 hover:text-slate-900"
                          >
                            <span className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              Payment History ({payments.length} payments)
                            </span>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          
                          {isExpanded && (
                            <div className="mt-3">
                              {payments.length === 0 ? (
                                <p className="text-sm text-slate-500 italic py-2">No payments recorded yet</p>
                              ) : (
                                <div className="max-h-60 overflow-y-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-50 sticky top-0">
                                      <tr>
                                        <th className="text-left py-2 px-3 font-medium text-slate-600">Date</th>
                                        <th className="text-left py-2 px-3 font-medium text-slate-600">Amount</th>
                                        <th className="text-left py-2 px-3 font-medium text-slate-600">Method</th>
                                        <th className="text-left py-2 px-3 font-medium text-slate-600">Notes</th>
                                        <th className="text-right py-2 px-3 font-medium text-slate-600">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {payments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)).map((payment, idx) => (
                                        <tr key={payment.id || idx} className="hover:bg-slate-50">
                                          <td className="py-2 px-3 text-slate-700">{formatDate(payment.payment_date)}</td>
                                          <td className="py-2 px-3 font-medium text-green-600">${parseFloat(payment.amount).toLocaleString()}</td>
                                          <td className="py-2 px-3 text-slate-600 capitalize">{payment.payment_method || 'manual'}</td>
                                          <td className="py-2 px-3 text-slate-600 truncate max-w-[200px]">{payment.notes || '-'}</td>
                                          <td className="py-2 px-3 text-right">
                                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => handleDeletePayment(payment.id)}>
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ==================== MESSAGES TAB ==================== */}
      {activeTab === 'messages' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><Mail className="w-7 h-7" /> Messages</h1>
                  <p className="text-blue-100 mt-1">Chat with your contractors</p>
                </div>
                <Button 
                  onClick={() => {
                    loadContactableContractors();
                    setShowNewConversationModal(true);
                  }} 
                  className="bg-white text-blue-600 hover:bg-blue-50"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Message
                </Button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
              <div className="flex h-full">
                {/* Conversation List */}
                <div className="w-80 border-r bg-slate-50 flex flex-col">
                  <div className="p-4 border-b bg-white">
                    <h3 className="font-semibold text-slate-900">Conversations</h3>
                    <p className="text-xs text-slate-500 mt-1">Messages auto-delete 24hrs after read</p>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {conversations.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No conversations yet</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-3"
                          onClick={() => {
                            loadContactableContractors();
                            setShowNewConversationModal(true);
                          }}
                        >
                          Start a conversation
                        </Button>
                      </div>
                    ) : (
                      conversations.map((conv) => (
                        <div
                          key={conv.contractor_id}
                          className={`p-4 border-b cursor-pointer transition-colors ${
                            selectedConversation?.contractor_id === conv.contractor_id
                              ? 'bg-blue-50 border-l-4 border-l-blue-500'
                              : 'hover:bg-slate-100'
                          }`}
                          onClick={() => handleSelectConversation(conv)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <span className="text-blue-600 font-semibold text-sm">
                                    {(conv.contractor_name || 'C')[0].toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-slate-900 truncate">{conv.contractor_name}</div>
                                  <div className="text-xs text-slate-500 truncate">{conv.contractor_trade}</div>
                                </div>
                              </div>
                              <p className="text-sm text-slate-600 mt-2 line-clamp-1">{conv.last_message}</p>
                            </div>
                            <div className="flex flex-col items-end ml-2">
                              <span className="text-xs text-slate-400">
                                {conv.last_message_at ? formatDate(conv.last_message_at) : ''}
                              </span>
                              {conv.unread_count > 0 && (
                                <span className="mt-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col">
                  {selectedConversation ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 border-b bg-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-semibold">
                              {(selectedConversation.contractor_name || 'C')[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{selectedConversation.contractor_name}</div>
                            <div className="text-sm text-slate-500">{selectedConversation.contractor_trade || selectedConversation.contractor_email}</div>
                          </div>
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
                        {conversationMessages.length === 0 ? (
                          <div className="h-full flex items-center justify-center text-slate-500">
                            <div className="text-center">
                              <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" />
                              <p>No messages yet. Start the conversation!</p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {conversationMessages.map((msg) => (
                              <div
                                key={msg.id}
                                className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                    msg.sender_type === 'admin'
                                      ? 'bg-blue-500 text-white rounded-br-sm'
                                      : 'bg-white border shadow-sm rounded-bl-sm'
                                  }`}
                                >
                                  <p className="text-sm">{msg.message}</p>
                                  <div className={`flex items-center gap-1 mt-1 text-xs ${
                                    msg.sender_type === 'admin' ? 'text-blue-100' : 'text-slate-400'
                                  }`}>
                                    <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    {msg.sender_type === 'admin' && (
                                      <span className="ml-1">
                                        {msg.recipient_read ? '✓✓' : '✓'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div ref={messagesEndRef} />
                          </div>
                        )}
                      </div>

                      {/* Message Input */}
                      <div className="p-4 border-t bg-white">
                        <div className="flex items-end gap-2">
                          <Textarea
                            placeholder="Type a message..."
                            value={newMessageText}
                            onChange={(e) => setNewMessageText(e.target.value)}
                            className="flex-1 resize-none"
                            rows={1}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendNewMessage();
                              }
                            }}
                          />
                          <Button
                            onClick={handleSendNewMessage}
                            disabled={isSendingNewMessage || !newMessageText.trim()}
                          >
                            {isSendingNewMessage ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Press Enter to send, Shift+Enter for new line</p>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <Mail className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <h3 className="font-semibold text-lg text-slate-700">Select a conversation</h3>
                        <p className="text-sm mt-1">Choose a contractor from the list or start a new conversation</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* New Conversation Modal */}
          <Dialog open={showNewConversationModal} onOpenChange={setShowNewConversationModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Start New Conversation</DialogTitle>
                <DialogDescription>Select a contractor to message</DialogDescription>
              </DialogHeader>
              <div className="max-h-[400px] overflow-y-auto">
                {contactableContractors.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No contractors available to message</p>
                    <p className="text-xs mt-1">Invite contractors to bid requests first</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {contactableContractors.map((contractor) => (
                      <div
                        key={contractor.id}
                        className="p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleStartNewConversation(contractor)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-semibold">
                              {(contractor.company || contractor.name || 'C')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{contractor.company || contractor.name}</div>
                            <div className="text-sm text-slate-500">{contractor.trade}</div>
                          </div>
                          {contractor.unread_count > 0 && (
                            <span className="w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                              {contractor.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* ==================== DOCUMENTS TAB ==================== */}
      {activeTab === 'documents' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><FolderOpen className="w-7 h-7" /> Documents</h1>
                  <p className="text-blue-100 mt-1">Manage project documents</p>
                </div>
                <Button onClick={() => setShowUploadDialog(true)} className="bg-white text-blue-600 hover:bg-blue-50">
                  <Upload className="w-4 h-4 mr-2" /> Upload
                </Button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            <Card className="bg-white mb-6">
              <CardContent className="p-4 flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterDocType} onValueChange={setFilterDocType}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="plans">Plans</SelectItem>
                    <SelectItem value="contract">Contracts</SelectItem>
                    <SelectItem value="w9">W-9</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {filteredDocuments.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-16 text-center">
                  <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Documents</h3>
                  <Button onClick={() => setShowUploadDialog(true)}><Upload className="w-4 h-4 mr-2" /> Upload</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredDocuments.map(doc => (
                  <Card key={doc.id} className="bg-white hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">{getFileIcon(doc.file_name)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2"><h3 className="font-medium truncate">{doc.file_name}</h3><Badge className="bg-slate-100 text-slate-700">{getCategoryLabel(doc.category)}</Badge></div>
                        <p className="text-sm text-slate-500">{doc.project_name} • {formatDate(doc.uploaded_at)}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadDocument(doc.id, doc.file_name)}><Download className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ==================== BANK FORMATS TAB ==================== */}
      {activeTab === 'bankformats' && (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-8">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold flex items-center gap-3"><Landmark className="w-7 h-7" /> Bank Formats</h1>
                  <p className="text-blue-100 mt-1">Configure export formats for different lenders</p>
                </div>
                <Button onClick={() => navigate('/bank-templates')} className="bg-white text-blue-600 hover:bg-blue-50">
                  <Plus className="w-4 h-4 mr-2" /> Add Format
                </Button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 py-6">
            {bankTemplates.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-16 text-center">
                  <Landmark className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Bank Formats</h3>
                  <p className="text-slate-500 mb-6">Add your lender&apos;s preferred format</p>
                  <Button onClick={() => navigate('/bank-templates')}><Plus className="w-4 h-4 mr-2" /> Add Bank Format</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bankTemplates.map(t => (
                  <Card key={t.id} className="bg-white">
                    <CardContent className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Landmark className="w-6 h-6 text-blue-600" /></div>
                        <div><h3 className="font-semibold">{t.name}</h3><p className="text-sm text-slate-500">{t.description || 'Bank export format'}</p></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/bank-templates`)}>Edit</Button>
                        {t.is_default && <Badge className="bg-green-100 text-green-700">Default</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ==================== COMPARE TAB ==================== */}
      {activeTab === 'compare' && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-blue-600" /> Bid Comparison
              </CardTitle>
              <CardDescription>
                Select a trade to compare all bids side-by-side
                {bidFilterProject && bidFilterProject !== 'all' && (
                  <span className="ml-1 font-medium text-blue-600" data-testid="compare-project-scope">
                    · Showing bids for {projects.find(p => p.id === bidFilterProject)?.name || 'this project'} only
                  </span>
                )}
                {(!bidFilterProject || bidFilterProject === 'all') && (
                  <span className="ml-1 text-slate-400">· Showing bids across all projects</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Trade Filter */}
              <div className="flex items-center gap-4">
                <Label className="font-medium">Select Trade:</Label>
                <Select 
                  value={selectedComparisonTrade || ''} 
                  onValueChange={(v) => {
                    setSelectedComparisonTrade(v);
                    loadTradeComparison();
                  }}
                >
                  <SelectTrigger className="w-64"><SelectValue placeholder="Choose a trade to compare" /></SelectTrigger>
                  <SelectContent>
                    {trades.filter(t => t !== 'All Trades').map(trade => (
                      <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => loadTradeComparison()}>
                  <BarChart3 className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
              
              {/* Comparison Results */}
              {selectedComparisonTrade && tradeComparisonData?.comparison_data?.[selectedComparisonTrade] ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Badge variant="outline" className="text-lg px-3 py-1">{selectedComparisonTrade}</Badge>
                      <span className="text-slate-500 font-normal">
                        {tradeComparisonData.comparison_data[selectedComparisonTrade].bid_count} bid(s) to compare
                      </span>
                    </h3>
                    {tradeComparisonData.comparison_data[selectedComparisonTrade].potential_savings > 0 && (
                      <Badge className="bg-green-100 text-green-700 text-base">
                        Potential Savings: ${tradeComparisonData.comparison_data[selectedComparisonTrade].potential_savings.toLocaleString()}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Side-by-side comparison cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tradeComparisonData.comparison_data[selectedComparisonTrade].bids.map((bid, idx) => (
                      <Card key={bid.id} className={`${bid.is_lowest ? 'ring-2 ring-green-500 bg-green-50' : ''}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${bid.is_lowest ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                              #{bid.rank}
                            </div>
                            {bid.is_lowest && <Badge className="bg-green-500">LOWEST</Badge>}
                          </div>
                          <CardTitle className="text-lg mt-2">{bid.contractor_name}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Pricing */}
                          <div className="bg-slate-100 rounded-lg p-3">
                            <p className="text-sm text-slate-500">Scaled Total ({bid.project_units} units)</p>
                            <p className={`text-2xl font-bold ${bid.is_lowest ? 'text-green-600' : 'text-slate-800'}`}>
                              ${bid.scaled_total?.toLocaleString()}
                            </p>
                            {bid.bid_units !== bid.project_units && (
                              <p className="text-xs text-slate-500 mt-1">
                                Original: ${bid.original_total?.toLocaleString()} for {bid.bid_units} unit(s)
                              </p>
                            )}
                          </div>
                          
                          {/* Line Items */}
                          {bid.line_items && bid.line_items.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-slate-700 mb-2">Line Items:</p>
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {bid.line_items.map((item, i) => (
                                  <div key={item.id || i} className="flex justify-between items-center text-sm py-1 border-b border-slate-100 last:border-0">
                                    <span className="text-slate-600 truncate flex-1 mr-2">{item.description}</span>
                                    <span className="text-slate-800 font-medium">${(item.total || 0).toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Source */}
                          <div className="text-xs text-slate-400 pt-2 border-t">
                            Source: {bid.source === 'multi_trade_upload' ? 'AI Extracted' : 'Manual Entry'}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : selectedComparisonTrade ? (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Bids for {selectedComparisonTrade}</h3>
                  <p className="text-slate-500">Upload bids with this trade type to enable comparison</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Scale className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a Trade to Compare</h3>
                  <p className="text-slate-500 mb-4">Choose a trade type above to see all bids for that trade side-by-side</p>
                  <Button variant="outline" onClick={() => {
                    loadTradeComparison();
                    setShowTradeComparison(true);
                  }}>
                    <Scale className="w-4 h-4 mr-2" /> Or View All Trades Summary
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== AI EXTRACT TAB ==================== */}
      {activeTab === 'aiextract' && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Sparkles className="w-6 h-6 text-purple-600" /> AI Data Extraction
              </CardTitle>
              <CardDescription>Upload documents and let AI extract key data for you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="bg-purple-50 border-purple-200">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <AlertDescription className="text-purple-800">
                  Upload bid documents, contracts, or invoices and AI will automatically extract line items, costs, and key details.
                </AlertDescription>
              </Alert>

              {/* Upload Area */}
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-purple-400 transition-colors cursor-pointer">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Upload Document to Extract</h3>
                <p className="text-slate-500 mb-4">Supports PDF, Excel, Word, and image files</p>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Upload className="w-4 h-4 mr-2" /> Choose File
                </Button>
              </div>

              {/* What AI Can Extract */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" /> From Bids & Estimates
                  </h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Line items & descriptions</li>
                    <li>• Unit prices & quantities</li>
                    <li>• Total amounts</li>
                    <li>• Contractor details</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" /> From Invoices
                  </h4>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li>• Invoice number & date</li>
                    <li>• Vendor information</li>
                    <li>• Payment amounts</li>
                    <li>• Project reference</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-slate-800 mb-3">Coming Soon</h4>
                <ul className="space-y-2 text-slate-700">
                  <li><Zap className="w-4 h-4 inline mr-2 text-purple-600" /> Auto-populate bid sheets from uploaded bids</li>
                  <li><Zap className="w-4 h-4 inline mr-2 text-purple-600" /> Compare uploaded bids with AI analysis</li>
                  <li><Zap className="w-4 h-4 inline mr-2 text-purple-600" /> Extract and categorize expenses from receipts</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== REVIEWS TAB ==================== */}
      {activeTab === 'reviews' && (
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-blue-600" />
                Pending Reviews
              </h2>
              <p className="text-slate-500 mt-1">Review and approve invoices and compliance documents from contractors</p>
            </div>
            <Button variant="outline" onClick={loadPendingReviews} disabled={isLoadingReviews}>
              {isLoadingReviews ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {isLoadingReviews ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : pendingReviews.total_pending === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900">All caught up!</h3>
                <p className="text-slate-500">No items pending your review.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Pending Invoices */}
              {pendingReviews.pending_invoices?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      Pending Invoices ({pendingReviews.pending_invoices.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pendingReviews.pending_invoices.map(invoice => (
                        <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg bg-yellow-50 border-yellow-200">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">${invoice.amount?.toLocaleString()}</h4>
                              <span className="text-slate-400">•</span>
                              <span className="text-slate-600">{invoice.contractor_name}</span>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{invoice.description}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {invoice.project_name} • {invoice.phase_name} • Submitted {formatDate(invoice.submitted_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {invoice.file_url && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={invoice.file_url} target="_blank" rel="noopener noreferrer">
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </a>
                              </Button>
                            )}
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => openRejectModal(invoice, 'invoice')}
                              data-testid={`reject-invoice-${invoice.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveInvoice(invoice.id)}
                              data-testid={`approve-invoice-${invoice.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending W-9s */}
              {pendingReviews.pending_w9s?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Pending W-9 Documents ({pendingReviews.pending_w9s.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pendingReviews.pending_w9s.map(w9 => (
                        <div key={w9.contractor_id} className="flex items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200">
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">{w9.contractor_name}</h4>
                            <p className="text-sm text-slate-500">{w9.project_name}</p>
                            {w9.uploaded_at && (
                              <p className="text-xs text-slate-400 mt-1">
                                Uploaded {formatDate(w9.uploaded_at)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {w9.file_id && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${w9.file_id}`} target="_blank" rel="noopener noreferrer">
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </a>
                              </Button>
                            )}
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => openRejectModal(w9, 'w9')}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveW9(w9.contractor_id)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending COIs */}
              {pendingReviews.pending_cois?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      Pending Certificates of Insurance ({pendingReviews.pending_cois.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {pendingReviews.pending_cois.map(coi => (
                        <div key={coi.contractor_id} className="flex items-center justify-between p-4 border rounded-lg bg-purple-50 border-purple-200">
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">{coi.contractor_name}</h4>
                            <p className="text-sm text-slate-500">{coi.project_name}</p>
                            {coi.expiration_date && (
                              <p className="text-xs text-slate-400 mt-1">
                                Expires: {formatDate(coi.expiration_date)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {coi.file_id && (
                              <Button variant="outline" size="sm" asChild>
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${coi.file_id}`} target="_blank" rel="noopener noreferrer">
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </a>
                              </Button>
                            )}
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={() => openRejectModal(coi, 'coi')}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveCOI(coi.contractor_id)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== HOW IT WORKS TAB ==================== */}
      {activeTab === 'howto' && (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-white">
            <CardHeader><CardTitle className="flex items-center gap-2 text-2xl"><HelpCircle className="w-6 h-6 text-blue-600" /> How the Bid Management System Works</CardTitle></CardHeader>
            <CardContent className="space-y-8">
              {[
                { step: 1, title: 'Create Bid Requests', desc: 'Send bid requests to contractors for specific scopes of work. Attach plans and specs.', color: 'blue' },
                { step: 2, title: 'Receive & Compare Bids', desc: 'Contractors submit itemized bids. Compare them side-by-side to find the best value.', color: 'blue' },
                { step: 3, title: 'Import to Bid Sheet', desc: 'Take the costs from received bids and import them into a bid sheet for bank submission.', color: 'blue' },
                { step: 4, title: 'Submit to Bank', desc: 'Export and submit your bid sheet to the bank for funding approval.', color: 'blue' },
                { step: 5, title: 'Bank Approves → Award Contractors', desc: 'Once the bank approves your budget, award the contractors. They receive an email notification.', color: 'green' }
              ].map(item => (
                <div key={item.step} className="flex gap-4">
                  <div className={`w-10 h-10 bg-${item.color}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-${item.color}-600 font-bold`}>{item.step}</span>
                  </div>
                  <div><h3 className="font-semibold text-lg mb-1">{item.title}</h3><p className="text-slate-600">{item.desc}</p></div>
                </div>
              ))}

              <div className="bg-blue-50 rounded-lg p-6 mt-8">
                <h4 className="font-semibold text-blue-900 mb-3">Pro Tips</h4>
                <ul className="space-y-2 text-blue-800">
                  <li><CheckSquare className="w-4 h-4 inline mr-2" /> Add trades to contractors for easy filtering</li>
                  <li><CheckSquare className="w-4 h-4 inline mr-2" /> Upload existing bids to compare everything in one place</li>
                  <li><CheckSquare className="w-4 h-4 inline mr-2" /> Attach plans to bid requests so contractors have full info</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== DIALOGS ==================== */}
      
      {/* Rejection Reason Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Provide Rejection Reason
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Please provide a reason for rejecting this {rejectingItem?.type === 'invoice' ? 'invoice' : rejectingItem?.type === 'w9' ? 'W-9' : 'COI'}. 
              This will be sent to the contractor.
            </p>
            <Label>Rejection Reason *</Label>
            <Textarea
              className="mt-2"
              placeholder="e.g., Amount doesn't match the agreed contract, Document is incomplete..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              data-testid="rejection-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (rejectingItem?.type === 'invoice') handleRejectInvoice();
                else if (rejectingItem?.type === 'w9') handleRejectW9();
                else if (rejectingItem?.type === 'coi') handleRejectCOI();
              }}
              disabled={!rejectionReason.trim()}
              data-testid="confirm-rejection-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contractor Dialog */}
      <Dialog open={showAddContractor} onOpenChange={setShowAddContractor}>
        <DialogContent>
          <DialogHeader><DialogTitle><UserPlus className="w-5 h-5 inline mr-2 text-blue-600" /> Add Contractor</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Company Name *</Label><Input className="mt-1.5" value={newContractor.company} onChange={(e) => setNewContractor({...newContractor, company: e.target.value})} /></div>
            <div><Label>Contact Name</Label><Input className="mt-1.5" value={newContractor.name} onChange={(e) => setNewContractor({...newContractor, name: e.target.value})} /></div>
            <div><Label>Email *</Label><Input type="email" className="mt-1.5" value={newContractor.email} onChange={(e) => setNewContractor({...newContractor, email: e.target.value})} /></div>
            <div><Label>Phone</Label><Input className="mt-1.5" value={newContractor.phone} onChange={(e) => setNewContractor({...newContractor, phone: e.target.value})} /></div>
            <div>
              <Label>Trade</Label>
              <Select value={newContractor.trade} onValueChange={(v) => setNewContractor({...newContractor, trade: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select trade" /></SelectTrigger>
                <SelectContent>{trades.filter(t => t !== 'All Trades').map(t => <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Custom Specialty</Label><Input className="mt-1.5" placeholder="e.g., Tile, Cabinetry, Finish Carpentry" value={newContractor.specialty} onChange={(e) => setNewContractor({...newContractor, specialty: e.target.value})} /><p className="text-xs text-slate-500 mt-1">Add if not in dropdown above</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContractor(false)}>Cancel</Button>
            <Button onClick={handleAddContractor}>Add Contractor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enter Bid Manually Dialog — record an offline bid on a contractor's behalf */}
      <Dialog open={showManualBidModal} onOpenChange={setShowManualBidModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle><Plus className="w-5 h-5 inline mr-2 text-blue-600" /> Enter Bid Manually</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto">
            {manualBidRequest && (
              <p className="text-sm text-slate-500">
                For <span className="font-semibold text-slate-700">{manualBidRequest.title}</span>. Use this for contractors who give you their price offline instead of using the portal.
              </p>
            )}
            <div>
              <Label>Contractor *</Label>
              <Select
                value={manualBidForm.contractor_id}
                onValueChange={(v) => setManualBidForm({ ...manualBidForm, contractor_id: v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="manual-bid-contractor-select"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ New contractor (enter below)</SelectItem>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name || c.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {manualBidForm.contractor_id === 'new' && (
              <div className="grid grid-cols-1 gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div><Label>Company Name *</Label><Input className="mt-1.5" placeholder="e.g., Rivera Concrete" value={manualBidForm.contractor_company} onChange={(e) => setManualBidForm({ ...manualBidForm, contractor_company: e.target.value })} data-testid="manual-bid-company" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Contact Name</Label><Input className="mt-1.5" value={manualBidForm.contractor_name} onChange={(e) => setManualBidForm({ ...manualBidForm, contractor_name: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input className="mt-1.5" value={manualBidForm.contractor_phone} onChange={(e) => setManualBidForm({ ...manualBidForm, contractor_phone: e.target.value })} /></div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bid Amount *</Label>
                <Input type="number" min="0" step="0.01" className="mt-1.5" placeholder="0.00" value={manualBidForm.total_amount} onChange={(e) => setManualBidForm({ ...manualBidForm, total_amount: e.target.value })} data-testid="manual-bid-amount" />
              </div>
              <div>
                <Label>Estimated Days</Label>
                <Input type="number" min="0" step="1" className="mt-1.5" placeholder="Optional" value={manualBidForm.estimated_days} onChange={(e) => setManualBidForm({ ...manualBidForm, estimated_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea className="mt-1.5" rows={3} placeholder="Any scope notes, exclusions, or how you received this bid" value={manualBidForm.notes} onChange={(e) => setManualBidForm({ ...manualBidForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualBidModal(false)}>Cancel</Button>
            <Button onClick={handleSubmitManualBid} disabled={manualBidSaving} data-testid="manual-bid-save">
              {manualBidSaving ? 'Saving...' : 'Record Bid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Edit Contractor Dialog */}
      <Dialog open={showEditContractor} onOpenChange={setShowEditContractor}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle><Edit2 className="w-5 h-5 inline mr-2 text-blue-600" /> Edit Contractor</DialogTitle></DialogHeader>
          {selectedContractor && (
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              <div><Label>Company Name</Label><Input className="mt-1.5" value={selectedContractor.company || ''} onChange={(e) => setSelectedContractor({...selectedContractor, company: e.target.value})} /></div>
              <div><Label>Contact Name</Label><Input className="mt-1.5" value={selectedContractor.name || ''} onChange={(e) => setSelectedContractor({...selectedContractor, name: e.target.value})} /></div>
              <div><Label>Email</Label><Input className="mt-1.5" value={selectedContractor.email || ''} onChange={(e) => setSelectedContractor({...selectedContractor, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input className="mt-1.5" value={selectedContractor.phone || ''} onChange={(e) => setSelectedContractor({...selectedContractor, phone: e.target.value})} /></div>
              <div><Label>Tax ID (SSN/EIN)</Label><Input className="mt-1.5" placeholder="XX-XXXXXXX" value={selectedContractor.ein_ssn || ''} onChange={(e) => setSelectedContractor({...selectedContractor, ein_ssn: e.target.value})} /></div>
              
              {/* Multiple Trades Selection */}
              <div>
                <Label>Trades (select all that apply)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {trades.filter(t => t !== 'All Trades').map(t => {
                    const isSelected = selectedContractor.trades?.includes(t.toLowerCase()) || selectedContractor.trade?.toLowerCase() === t.toLowerCase();
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          const currentTrades = selectedContractor.trades || [];
                          const tradeLower = t.toLowerCase();
                          if (currentTrades.includes(tradeLower)) {
                            setSelectedContractor({...selectedContractor, trades: currentTrades.filter(tr => tr !== tradeLower)});
                          } else {
                            setSelectedContractor({...selectedContractor, trades: [...currentTrades, tradeLower]});
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                          isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
                {/* Add Custom Trade */}
                <div className="flex gap-2 mt-3">
                  <Input 
                    placeholder="Add custom trade..." 
                    value={newTradeName} 
                    onChange={(e) => setNewTradeName(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={handleAddCustomTrade}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Address Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Street Address</Label><Input className="mt-1.5" value={selectedContractor.street_address || ''} onChange={(e) => setSelectedContractor({...selectedContractor, street_address: e.target.value})} /></div>
                <div><Label>City</Label><Input className="mt-1.5" value={selectedContractor.city || ''} onChange={(e) => setSelectedContractor({...selectedContractor, city: e.target.value})} /></div>
                <div><Label>State</Label><Input className="mt-1.5" value={selectedContractor.state || ''} onChange={(e) => setSelectedContractor({...selectedContractor, state: e.target.value})} /></div>
                <div><Label>ZIP</Label><Input className="mt-1.5" value={selectedContractor.zip || ''} onChange={(e) => setSelectedContractor({...selectedContractor, zip: e.target.value})} /></div>
              </div>

              {/* W-9 Upload */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4" /> W-9 Form
                  </Label>
                  <Badge className={selectedContractor.w9_received || selectedContractor.w9_file_url ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {selectedContractor.w9_received || selectedContractor.w9_file_url ? 'On File' : 'Pending'}
                  </Badge>
                </div>
                <input 
                  type="file" 
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="text-sm"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleUploadW9(selectedContractor.id, e.target.files[0]);
                    }
                  }}
                />
              </div>

              {/* COI Upload */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4" /> Certificate of Insurance (COI)
                  </Label>
                  <Badge className={selectedContractor.coi_received || selectedContractor.coi_file_url ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {selectedContractor.coi_received || selectedContractor.coi_file_url ? 'On File' : 'Pending'}
                  </Badge>
                </div>
                <input 
                  type="file" 
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="text-sm"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleUploadCOI(selectedContractor.id, e.target.files[0]);
                    }
                  }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditContractor(false)}>Cancel</Button>
            <Button onClick={handleUpdateContractor}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 1099 Year Selection Dialog */}
      <Dialog open={show1099Dialog} onOpenChange={setShow1099Dialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><FileText className="w-5 h-5 inline mr-2 text-orange-600" /> 
              {selectedContractor ? `1099 for ${selectedContractor.company || selectedContractor.name}` : '1099 Report'}
            </DialogTitle>
            <DialogDescription>Select the tax year for your 1099 report</DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <Label>Tax Year</Label>
            <Select value={String(selected1099Year)} onValueChange={(v) => setSelected1099Year(parseInt(v))}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {[2026, 2025, 2024, 2023, 2022].map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 mt-3">
              1099-NEC forms are required for any contractor paid $600 or more during the tax year.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow1099Dialog(false)}>Cancel</Button>
            <Button onClick={selectedContractor ? handleDownload1099 : handleDownloadBulk1099Report} className="bg-orange-600 hover:bg-orange-700">
              <Download className="w-4 h-4 mr-2" /> Generate 1099
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Existing Bid Dialog */}
      <Dialog open={showUploadBid} onOpenChange={(open) => {
        setShowUploadBid(open);
        if (!open) {
          resetUploadBidState();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" /> Upload Existing Bid
            </DialogTitle>
            <DialogDescription>
              Upload bid document(s) and AI will extract all details for comparison
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Upload with AI Extraction */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <Label className="font-semibold text-purple-900">AI Bid Extraction</Label>
              </div>
              <p className="text-xs text-purple-700 mb-3">
                Upload bid document(s) - select multiple files to combine into one bid (e.g., 4plex + 3plex bids)
              </p>
              
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.docx,.doc"
                  multiple
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 1) {
                      // Multiple files - use combine mode
                      handleMultiFileUpload(files);
                    } else if (files && files.length === 1) {
                      // Single file - use normal mode
                      setUploadBidData(prev => ({ ...prev, file: files[0] }));
                      handleExtractBidData(files[0]);
                    }
                  }}
                  className="hidden"
                  id="bid-file-upload"
                />
                <label htmlFor="bid-file-upload" className="flex-1">
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    uploadBidData.file || uploadedFiles.length > 0 ? 'border-green-300 bg-green-50' : 'border-purple-300 hover:border-purple-400'
                  }`}>
                    {isExtractingBid ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                        <span className="text-purple-700">Extracting bid data...</span>
                      </div>
                    ) : uploadedFiles.length > 1 ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="text-green-700">{uploadedFiles.length} files selected</span>
                      </div>
                    ) : uploadBidData.file ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="text-green-700">{uploadBidData.file.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <p className="text-sm text-purple-600">Click to upload bid document(s)</p>
                        <p className="text-xs text-purple-500">Select multiple files to combine (e.g., 4plex + 3plex)</p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
            
            {/* Per-file extraction progress (only while running) */}
            {isExtractingBid && extractionProgress.total > 1 && (
              <div className="bg-white border border-purple-200 rounded-lg p-3 flex items-center gap-3" data-testid="extraction-progress">
                <Loader2 className="w-4 h-4 text-purple-600 animate-spin flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-purple-900">
                    Extracting file {extractionProgress.current} of {extractionProgress.total}…
                  </div>
                  <div className="h-1.5 bg-purple-100 rounded-full mt-1.5 overflow-hidden">
                    <div
                      className="h-full bg-purple-500 transition-all"
                      style={{ width: `${(extractionProgress.current / extractionProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-purple-500 mt-1">
                    Each PDF takes 10–30 seconds. We'll show each file's result as it lands below.
                  </div>
                </div>
              </div>
            )}

            {/* Multi-File Combined Preview */}
            {multiFileMode && extractedFilesData.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">
                    Combining {extractedFilesData.length} File{extractedFilesData.length === 1 ? '' : 's'}
                  </span>
                  {extractedBidData?.contractor?.company_name && (
                    <Badge className="ml-auto bg-blue-100 text-blue-700">
                      {extractedBidData.contractor.company_name}
                    </Badge>
                  )}
                </div>
                
                {/* File Breakdown */}
                <div className="space-y-2">
                  {extractedFilesData.map((fileData, idx) => {
                    const mult = fileData.multiplier || 1;
                    const lineTotal = (fileData.total || 0) * mult;
                    const updateMultiplier = (nextMult) => {
                      setExtractedFilesData(prev => prev.map((f, i) => i === idx ? { ...f, multiplier: nextMult } : f));
                    };
                    const retryFile = async () => {
                      const fileObj = uploadedFiles[idx];
                      if (!fileObj) return;
                      setExtractedFilesData(prev => prev.map((f, i) => i === idx ? { ...f, extraction_status: 'retrying' } : f));
                      try {
                        const formData = new FormData();
                        formData.append('file', fileObj);
                        const res = await apiService.extractBidData(formData);
                        if (res.data?.success && res.data?.extracted_data) {
                          const data = res.data.extracted_data;
                          const detectedUnits = data.bid_details?.detected_units || 1;
                          const pricingBasis = (data.pricing?.pricing_basis || '').toLowerCase();
                          const isPerUnit = pricingBasis === 'per_unit' || pricingBasis === 'per-unit';
                          setExtractedFilesData(prev => prev.map((f, i) => i === idx ? {
                            ...f,
                            data,
                            units: detectedUnits,
                            total: data.pricing?.total_amount || 0,
                            trade_groups: data.trade_groups || [],
                            line_items: data.line_items || [],
                            pricing_basis: isPerUnit ? 'per_unit' : 'total',
                            multiplier: isPerUnit && detectedUnits > 1 ? detectedUnits : 1,
                            extraction_status: 'ok',
                          } : f));
                          toast.success(`Re-extracted ${fileObj.name}`);
                        } else {
                          setExtractedFilesData(prev => prev.map((f, i) => i === idx ? { ...f, extraction_status: 'failed', error: res.data?.error || 'AI still could not read this file.' } : f));
                          toast.error('Re-extract failed');
                        }
                      } catch (e) {
                        setExtractedFilesData(prev => prev.map((f, i) => i === idx ? { ...f, extraction_status: 'failed', error: 'Re-extract failed' } : f));
                        toast.error('Re-extract failed');
                      }
                    };
                    return (
                      <div key={idx} className="bg-white rounded-lg p-3 border" data-testid={`extracted-file-${idx}`}>
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <File className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            <span className="font-medium text-slate-800 text-sm truncate">{fileData.filename}</span>
                          </div>
                          {fileData.extraction_status === 'ok' ? (
                            <div className="text-right">
                              <span className="font-bold text-slate-800">${lineTotal.toLocaleString()}</span>
                              {mult > 1 && (
                                <span className="text-[10px] text-slate-500 ml-1">
                                  (${(fileData.total || 0).toLocaleString()} × {mult})
                                </span>
                              )}
                              <span className="text-xs text-slate-500 ml-2">({fileData.units} unit{fileData.units > 1 ? 's' : ''})</span>
                            </div>
                          ) : fileData.extraction_status === 'retrying' ? (
                            <span className="text-xs text-purple-600 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-red-500 border-red-300">Failed</Badge>
                              <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={retryFile} data-testid={`retry-extract-${idx}`}>
                                Retry
                              </Button>
                            </div>
                          )}
                        </div>
                        {fileData.extraction_status === 'failed' && fileData.error && (
                          <p className="text-[11px] text-red-500 pl-6 mb-2">{fileData.error}</p>
                        )}
                        {/* Per-unit / total override — only when extraction succeeded */}
                        {fileData.extraction_status === 'ok' && (
                          <div className="pl-6 mb-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                              How should we read this price?
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => updateMultiplier(1)}
                                data-testid={`basis-total-${idx}`}
                                className={`text-[11px] px-2 py-1 rounded-md border ${mult === 1 ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                              >
                                Total for whole job
                              </button>
                              <button
                                type="button"
                                onClick={() => updateMultiplier(Math.max(2, fileData.units || 2))}
                                data-testid={`basis-perunit-${idx}`}
                                className={`text-[11px] px-2 py-1 rounded-md border ${mult > 1 ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                              >
                                Per unit × {mult > 1 ? mult : (fileData.units || 1)}
                              </button>
                              {mult > 1 && (
                                <div className="inline-flex items-center gap-1 ml-1">
                                  <span className="text-[10px] text-slate-500">×</span>
                                  <input
                                    type="number"
                                    min="1"
                                    value={mult === '' ? '' : mult}
                                    onChange={(e) => {
                                      // Allow empty string while user is editing so backspace works.
                                      // We re-validate to >=1 on blur.
                                      const v = e.target.value;
                                      if (v === '') { updateMultiplier(''); return; }
                                      const n = parseInt(v, 10);
                                      if (!isNaN(n)) updateMultiplier(n);
                                    }}
                                    onBlur={() => { if (mult === '' || mult < 1) updateMultiplier(1); }}
                                    className="w-12 h-6 text-[11px] text-center border border-slate-200 rounded"
                                    data-testid={`multiplier-input-${idx}`}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {fileData.line_items && fileData.line_items.length > 0 && (
                          <div className="text-xs text-slate-500 pl-6">
                            {fileData.line_items.slice(0, 3).map((item, i) => (
                              <div key={i} className="flex justify-between py-0.5">
                                <span className="truncate flex-1 mr-2">{item.description}</span>
                                <span>${(item.total || 0).toLocaleString()}</span>
                              </div>
                            ))}
                            {fileData.line_items.length > 3 && (
                              <span className="text-slate-400">+{fileData.line_items.length - 3} more items</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Combined Total — sum of (file.total × file.multiplier) so per-unit pricing rolls up correctly */}
                <div className="bg-green-100 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-green-800">Combined Total</span>
                    <span className="text-sm text-green-600 ml-2" data-testid="combined-units-label">
                      ({extractedFilesData.reduce((s, f) => s + ((f.units || 1) * (f.multiplier || 1) / Math.max(1, f.multiplier || 1)), 0)} units total · {extractedFilesData.filter(f => f.extraction_status === 'ok').length}/{extractedFilesData.length} extracted)
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-green-700" data-testid="combined-total-amount">
                    ${extractedFilesData.reduce((sum, f) => sum + ((f.total || 0) * (f.multiplier || 1)), 0).toLocaleString()}
                  </span>
                </div>
                
                {/* Combine Option */}
                <div className="flex items-center gap-3 p-3 bg-white rounded border">
                  <input 
                    type="checkbox" 
                    checked={combineFiles}
                    onChange={(e) => setCombineFiles(e.target.checked)}
                    className="w-5 h-5 rounded text-blue-600"
                    id="combine-files-checkbox"
                  />
                  <label htmlFor="combine-files-checkbox" className="flex-1">
                    <span className="font-medium text-slate-800">Combine into one bid entry</span>
                    <p className="text-xs text-slate-500">Creates a single bid with breakdown preserved for comparison</p>
                  </label>
                </div>
              </div>
            )}
            
            {/* Extracted Data Preview */}
            {extractedBidData && !multiTradeMode && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">Data Extracted Successfully</span>
                  <Badge className={`ml-auto ${
                    extractedBidData.confidence === 'high' ? 'bg-green-100 text-green-700' :
                    extractedBidData.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {extractedBidData.confidence} confidence
                  </Badge>
                </div>
                
                {/* Contractor Info */}
                {extractedBidData.contractor?.company_name && (
                  <div className="bg-white rounded p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Contractor</p>
                    <p className="font-medium text-slate-800">{extractedBidData.contractor.company_name}</p>
                    {extractedBidData.contractor.contact_name && (
                      <p className="text-sm text-slate-600">{extractedBidData.contractor.contact_name}</p>
                    )}
                    {extractedBidData.contractor.phone && (
                      <p className="text-sm text-slate-500">{extractedBidData.contractor.phone}</p>
                    )}
                  </div>
                )}
                
                {/* Line Items */}
                {extractedBidData.line_items?.length > 0 && (
                  <div className="bg-white rounded p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Line Items ({extractedBidData.line_items.length})</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {extractedBidData.line_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                          <span className="text-slate-700 truncate flex-1 mr-2">{item.description}</span>
                          <span className="font-medium text-slate-800">${(item.total || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Total */}
                {extractedBidData.pricing?.total_amount && (
                  <div className="bg-blue-100 rounded p-3 flex justify-between items-center">
                    <span className="font-medium text-blue-800">Total Amount</span>
                    <span className="text-xl font-bold text-blue-900">${extractedBidData.pricing.total_amount.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Multi-Trade Detection UI */}
            {multiTradeMode && selectedTradeGroups.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">Multi-Trade Bid Detected!</span>
                  <Badge className="ml-auto bg-purple-100 text-purple-700">
                    {selectedTradeGroups.length} trades found
                  </Badge>
                </div>
                
                {/* Contractor Info */}
                {extractedBidData?.contractor?.company_name && (
                  <div className="bg-white rounded p-3 border">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Contractor</p>
                    <p className="font-semibold text-slate-800">{extractedBidData.contractor.company_name}</p>
                  </div>
                )}
                
                {/* Unit Scaling */}
                <div className="bg-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Scale className="w-4 h-4 text-purple-700" />
                    <Label className="font-medium text-purple-800">Unit Scaling for Comparison</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-purple-700">Bid is for:</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input 
                          type="number" 
                          min="1"
                          value={bidUnits} 
                          onChange={(e) => setBidUnits(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                          onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setBidUnits(1); }}
                          className="w-20 text-center font-bold"
                        />
                        <span className="text-purple-700">units</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-purple-700">Your project has:</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input 
                          type="number" 
                          min="1"
                          value={projectUnits} 
                          onChange={(e) => setProjectUnits(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                          onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setProjectUnits(1); }}
                          className="w-20 text-center font-bold"
                        />
                        <span className="text-purple-700">units</span>
                      </div>
                    </div>
                  </div>
                  {bidUnits !== projectUnits && bidUnits && projectUnits && (
                    <p className="text-sm text-purple-600 mt-2">
                      Costs will be scaled by {(projectUnits / bidUnits).toFixed(2)}x for comparison
                    </p>
                  )}
                </div>
                
                {/* Trade Groups */}
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">Select Trades to Import:</Label>
                  {selectedTradeGroups.map((trade, idx) => {
                    const scaledTotal = (trade.subtotal || 0) * (projectUnits / bidUnits);
                    return (
                      <div key={idx} className="border rounded-lg overflow-hidden bg-white">
                        <div className={`p-3 flex items-center justify-between ${trade.selected ? 'bg-blue-50' : 'bg-slate-50'}`}>
                          <div className="flex items-center gap-3">
                            <input 
                              type="checkbox" 
                              checked={trade.selected}
                              onChange={(e) => {
                                const updated = [...selectedTradeGroups];
                                updated[idx].selected = e.target.checked;
                                setSelectedTradeGroups(updated);
                              }}
                              className="w-5 h-5 rounded text-blue-600"
                            />
                            <div>
                              <p className="font-semibold text-slate-800">{trade.trade_type}</p>
                              <p className="text-sm text-slate-500">{trade.line_items?.length || 0} line items</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-slate-500 font-bold">$</span>
                              <Input
                                type="number"
                                value={trade.subtotal || 0}
                                data-testid={`trade-subtotal-input-${idx}`}
                                onChange={(e) => {
                                  const updated = [...selectedTradeGroups];
                                  updated[idx] = { ...updated[idx], subtotal: parseFloat(e.target.value) || 0 };
                                  setSelectedTradeGroups(updated);
                                }}
                                className="w-28 text-right font-bold text-lg h-9"
                              />
                            </div>
                            {bidUnits !== projectUnits && (
                              <p className="text-sm text-green-600 font-medium">
                                → ${scaledTotal.toLocaleString()} for {projectUnits} units
                              </p>
                            )}
                          </div>
                        </div>
                        {trade.line_items?.length > 0 && (
                          <div className="p-2 border-t bg-slate-50 max-h-32 overflow-y-auto">
                            {trade.line_items.map((item, i) => (
                              <div key={i} className="flex justify-between py-1 text-sm">
                                <span className="text-slate-600 truncate flex-1 mr-2">{item.description}</span>
                                <span className="text-slate-800">${(item.total || 0).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Manual Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project *</Label>
                <Select value={uploadBidData.project_id} onValueChange={(v) => setUploadBidData({...uploadBidData, project_id: v})}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contractor * {extractedBidData?.contractor?.company_name && <span className="text-green-600 text-xs">(AI suggested)</span>}</Label>
                <div className="flex gap-2 mt-1.5">
                  <Select value={uploadBidData.contractor_id} onValueChange={(v) => setUploadBidData({...uploadBidData, contractor_id: v})}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                    <SelectContent>{contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      // Pre-fill from extracted data if available
                      if (extractedBidData?.contractor) {
                        setQuickAddContractor({
                          company: extractedBidData.contractor.company_name || '',
                          name: extractedBidData.contractor.contact_name || '',
                          email: extractedBidData.contractor.email || '',
                          phone: extractedBidData.contractor.phone || '',
                          trade: uploadBidData.trade_type || ''
                        });
                      }
                      setShowQuickAddContractor(!showQuickAddContractor);
                    }}
                    title="Quick add new contractor"
                    data-testid="quick-add-contractor-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Quick Add Contractor Form */}
                {showQuickAddContractor && (
                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-slate-700">Quick Add Contractor</Label>
                      <Button variant="ghost" size="sm" onClick={() => setShowQuickAddContractor(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Company Name *</Label>
                        <Input 
                          value={quickAddContractor.company}
                          onChange={(e) => setQuickAddContractor(prev => ({...prev, company: e.target.value}))}
                          placeholder="ABC Contracting"
                          className="mt-1 h-9"
                          data-testid="quick-add-company-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Contact Name</Label>
                        <Input 
                          value={quickAddContractor.name}
                          onChange={(e) => setQuickAddContractor(prev => ({...prev, name: e.target.value}))}
                          placeholder="John Smith"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input 
                          type="email"
                          value={quickAddContractor.email}
                          onChange={(e) => setQuickAddContractor(prev => ({...prev, email: e.target.value}))}
                          placeholder="john@abc.com"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Phone</Label>
                        <Input 
                          value={quickAddContractor.phone}
                          onChange={(e) => setQuickAddContractor(prev => ({...prev, phone: e.target.value}))}
                          placeholder="(555) 123-4567"
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowQuickAddContractor(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleQuickAddContractor}
                        disabled={!quickAddContractor.company.trim() || isAddingContractor}
                        data-testid="quick-add-contractor-submit"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {isAddingContractor ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Adding...</>
                        ) : (
                          <><Plus className="w-3 h-3 mr-1" /> Add & Select</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Attach to a prepared scope (so uploaded email bids land under the
                existing scope instead of creating a separate standalone bid) */}
            {uploadBidData.project_id && !multiTradeMode && (() => {
              const projectScopes = bidRequests.filter(
                b => b.project_id === uploadBidData.project_id && b.status !== 'awarded' && b.status !== 'closed'
              );
              if (projectScopes.length === 0) return null;
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="attach-scope-block">
                  <Label className="flex items-center gap-2 text-blue-800 font-medium text-sm mb-1.5">
                    <Gavel className="w-4 h-4" /> File this bid under a prepared scope
                  </Label>
                  <Select
                    value={uploadTargetBidRequestId || '__new__'}
                    onValueChange={(v) => {
                      setUploadTargetBidRequestId(v);
                      if (v && v !== '__new__') {
                        const scope = projectScopes.find(s => s.id === v);
                        if (scope?.trade_type) {
                          setUploadBidData(prev => ({ ...prev, trade_type: scope.trade_type }));
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="bg-white" data-testid="attach-scope-select">
                      <SelectValue placeholder="Choose a scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__new__">Create a new bid instead</SelectItem>
                      {projectScopes.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {(s.trade_type || s.title || 'Scope')} — {s.response_count || 0} bid(s)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-700 mt-1.5">
                    {uploadTargetBidRequestId && uploadTargetBidRequestId !== '__new__'
                      ? 'This bid will be added to the selected scope and appear in its comparison.'
                      : 'Tip: attach emailed bids to their prepared scope so all bids for a trade compare together.'}
                  </p>
                </div>
              );
            })()}

            {/* Trade Type - show for single trade mode and multi-file mode */}
            {(!multiTradeMode || multiFileMode) && (
              <>
                <div>
                  <Label>Trade Type *</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Select value={uploadBidData.trade_type} onValueChange={(v) => setUploadBidData({...uploadBidData, trade_type: v})}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select trade" /></SelectTrigger>
                      <SelectContent>
                        {trades.filter(t => t !== 'All Trades').map(trade => (
                          <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      onClick={() => setShowQuickAddTrade(!showQuickAddTrade)}
                      title="Add new trade type"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Quick Add Trade Form */}
                  {showQuickAddTrade && (
                    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold text-slate-700">Add New Trade</Label>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowQuickAddTrade(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Input 
                          value={quickAddTradeName}
                          onChange={(e) => setQuickAddTradeName(e.target.value)}
                          placeholder="e.g., Solar Installation"
                          className="flex-1 h-9"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleQuickAddTrade();
                            }
                          }}
                        />
                        <Button 
                          size="sm"
                          onClick={() => {
                            handleQuickAddTrade();
                            // Also update uploadBidData trade_type
                            if (quickAddTradeName.trim()) {
                              setUploadBidData(prev => ({ ...prev, trade_type: quickAddTradeName.trim() }));
                            }
                          }}
                          disabled={!quickAddTradeName.trim() || isAddingTrade}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {isAddingTrade ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><Plus className="w-3 h-3 mr-1" /> Add</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Unit Scaling - for single trade or multi-file mode */}
                {!multiFileMode && (
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <Label className="flex items-center gap-2 text-purple-800 font-medium text-sm mb-2">
                      <Scale className="w-4 h-4" />
                      Unit Scaling (for comparison)
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-purple-700">Bid is for:</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input 
                            type="number" 
                            min="1"
                            value={bidUnits} 
                            onChange={(e) => setBidUnits(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                            onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setBidUnits(1); }}
                            className="w-20 text-center"
                          />
                          <span className="text-sm text-purple-700">units</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-purple-700">Your project has:</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input 
                            type="number" 
                            min="1"
                            value={projectUnits} 
                            onChange={(e) => setProjectUnits(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                            onBlur={(e) => { if (e.target.value === '' || isNaN(parseInt(e.target.value))) setProjectUnits(1); }}
                            className="w-20 text-center"
                          />
                          <span className="text-sm text-purple-700">units</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            
            <div>
              <Label>Total Amount {extractedBidData?.pricing?.total_amount && <span className="text-green-600 text-xs">(AI extracted)</span>}</Label>
              <Input 
                type="number" 
                className="mt-1.5" 
                value={uploadBidData.total_amount} 
                onChange={(e) => setUploadBidData({...uploadBidData, total_amount: e.target.value})} 
                placeholder="50000" 
              />
            </div>
            
            <div>
              <Label>Notes / Scope {extractedBidData?.notes?.length > 0 && <span className="text-green-600 text-xs">(AI extracted)</span>}</Label>
              <Textarea 
                className="mt-1.5" 
                value={uploadBidData.notes} 
                onChange={(e) => setUploadBidData({...uploadBidData, notes: e.target.value})} 
                rows={3}
                placeholder="Scope of work, exclusions, special conditions..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadBid(false)}>Cancel</Button>
            {multiFileMode && combineFiles ? (
              <Button 
                onClick={handleUploadCombinedBid}
                disabled={!uploadBidData.project_id || !uploadBidData.contractor_id || !uploadBidData.trade_type || extractedFilesData.length === 0}
                className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
              >
                <FileText className="w-4 h-4 mr-2" />
                Combine & Upload ({extractedFilesData.length} files)
              </Button>
            ) : multiTradeMode ? (
              <>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setMultiTradeMode(false);
                    setSelectedTradeGroups([]);
                  }}
                >
                  Keep as Single Bid
                </Button>
                <Button 
                  onClick={handleCreateMultiTradeBids}
                  disabled={!uploadBidData.project_id || !uploadBidData.contractor_id || selectedTradeGroups.filter(t => t.selected).length === 0}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create {selectedTradeGroups.filter(t => t.selected).length} Trade Bids
                </Button>
              </>
            ) : (
              <Button 
                onClick={handleUploadExistingBid}
                disabled={!uploadBidData.project_id || !uploadBidData.contractor_id || !uploadBidData.trade_type}
              >
                <Upload className="w-4 h-4 mr-2" />
                {extractedBidData ? 'Upload Extracted Bid' : 'Upload Bid'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trade Comparison Dialog */}
      <Dialog open={showTradeComparison} onOpenChange={setShowTradeComparison}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-green-600" /> Compare Bids by Trade
            </DialogTitle>
            <DialogDescription>
              Compare contractor bids across different trades with scaled pricing
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {tradeComparisonData?.comparison_data && Object.keys(tradeComparisonData.comparison_data).length > 0 ? (
              <>
                {/* Trade Groups */}
                {Object.entries(tradeComparisonData.comparison_data).map(([trade, data]) => (
                  <div key={trade} className="border rounded-xl overflow-hidden">
                    <div className="bg-slate-100 px-4 py-3 border-b flex items-center justify-between">
                      <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <Badge variant="outline" className="text-base px-3 py-1">{trade}</Badge>
                        <span className="text-sm font-normal text-slate-500">
                          {data.bid_count} bid{data.bid_count > 1 ? 's' : ''} received
                        </span>
                      </h3>
                      {data.potential_savings > 0 && (
                        <Badge className="bg-green-100 text-green-700">
                          Save ${data.potential_savings.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="divide-y">
                      {data.bids.map((bid, idx) => (
                        <div 
                          key={bid.id} 
                          className={`p-4 flex items-center justify-between ${bid.is_lowest ? 'bg-green-50' : 'bg-white'}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${bid.is_lowest ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                              {bid.rank}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800 flex items-center gap-2">
                                {bid.contractor_name}
                                {bid.is_lowest && (
                                  <Badge className="bg-green-500 text-xs">LOWEST</Badge>
                                )}
                              </p>
                              <p className="text-sm text-slate-500">
                                Original: ${bid.original_total?.toLocaleString()} for {bid.bid_units} unit{bid.bid_units > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className={`text-2xl font-bold ${bid.is_lowest ? 'text-green-600' : 'text-slate-700'}`}>
                                ${bid.scaled_total?.toLocaleString()}
                              </p>
                              <p className="text-sm text-slate-500">
                                {bid.bid_units !== bid.project_units ? `Scaled to ${bid.project_units} units` : `${bid.project_units} units`}
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteTradeBid(bid.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Summary Card */}
                <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-green-400" />
                      Lowest Bid Summary
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {Object.entries(tradeComparisonData.comparison_data).map(([trade, data]) => (
                        data.lowest_bid && (
                          <div key={trade} className="bg-white/10 rounded-lg p-4">
                            <p className="text-slate-300 text-sm">{trade}</p>
                            <p className="text-2xl font-bold text-green-400">
                              ${data.lowest_bid.scaled_total?.toLocaleString()}
                            </p>
                            <p className="text-sm text-slate-400">{data.lowest_bid.contractor_name}</p>
                          </div>
                        )
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/20 flex justify-between items-center">
                      <span className="text-lg">Total (Lowest Bids Combined)</span>
                      <span className="text-3xl font-bold text-green-400">
                        ${tradeComparisonData.total_lowest_bids?.toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12">
                <Scale className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Trade Bids Yet</h3>
                <p className="text-slate-500 mb-4">
                  Upload bid documents with multiple trades to enable comparison
                </p>
                <Button onClick={() => {
                  setShowTradeComparison(false);
                  setShowUploadBid(true);
                }}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Existing Bid
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTradeComparison(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Bid Request Dialog */}
      <Dialog open={showEditBid} onOpenChange={setShowEditBid}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" /> Edit Bid
            </DialogTitle>
            <DialogDescription>
              Update bid details and line item pricing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input 
                className="mt-1.5"
                value={editBidData.title}
                onChange={(e) => setEditBidData({...editBidData, title: e.target.value})}
                placeholder="Bid request title"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Trade Type *</Label>
                <div className="flex gap-2 mt-1.5">
                  <Select value={editBidData.trade_type} onValueChange={(v) => setEditBidData({...editBidData, trade_type: v})}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select trade" /></SelectTrigger>
                    <SelectContent>
                      {trades.filter(t => t !== 'All Trades').map(trade => (
                        <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowQuickAddTrade(!showQuickAddTrade)}
                    title="Add new trade type"
                    data-testid="quick-add-trade-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Quick Add Trade Form */}
                {showQuickAddTrade && (
                  <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-slate-700">Add New Trade</Label>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowQuickAddTrade(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        value={quickAddTradeName}
                        onChange={(e) => setQuickAddTradeName(e.target.value)}
                        placeholder="e.g., Solar Installation"
                        className="flex-1 h-9"
                        data-testid="quick-add-trade-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleQuickAddTrade();
                          }
                        }}
                      />
                      <Button 
                        size="sm"
                        onClick={handleQuickAddTrade}
                        disabled={!quickAddTradeName.trim() || isAddingTrade}
                        data-testid="quick-add-trade-submit"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {isAddingTrade ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Plus className="w-3 h-3 mr-1" /> Add</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <Label>Total Amount ($)</Label>
                <Input 
                  className="mt-1.5"
                  type="number"
                  step="0.01"
                  value={editBidData.total_amount}
                  onChange={(e) => setEditBidData({...editBidData, total_amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contractor</Label>
                <Select value={editBidData.contractor_id} onValueChange={(v) => setEditBidData({...editBidData, contractor_id: v})}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Project</Label>
                <Select value={editBidData.project_id} onValueChange={(v) => setEditBidData({...editBidData, project_id: v})}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Pricing Breakdown Section - Always visible */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Pricing Breakdown</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditBidData({
                    ...editBidData,
                    line_items: [...(editBidData.line_items || []), {
                      id: null,
                      description: '',
                      quantity: '',
                      unit: 'sqft',
                      unit_price: '',
                      total: 0
                    }]
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Line Item
                </Button>
              </div>
              
              {(!editBidData.line_items || editBidData.line_items.length === 0) ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                  <p className="text-slate-500 text-sm mb-2">No line items yet</p>
                  <p className="text-slate-400 text-xs">Click "Add Line Item" to break down pricing by sqft, unit, or other measurements</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-72 overflow-y-auto">
                  {editBidData.line_items.map((item, index) => (
                    <div key={item.id || index} className="p-3 bg-slate-50 rounded-lg border relative group">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-red-100 text-red-600 opacity-0 group-hover:opacity-100"
                        onClick={() => {
                          const newItems = editBidData.line_items.filter((_, i) => i !== index);
                          const grandTotal = newItems.reduce((sum, itm) => sum + (parseFloat(itm.total) || 0), 0);
                          setEditBidData({
                            ...editBidData,
                            line_items: newItems,
                            total_amount: grandTotal.toFixed(2)
                          });
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                      <div className="mb-2">
                        <Input 
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Item description (e.g., Labor, Materials, Installation)"
                          className="text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="text-xs text-slate-500">Quantity</label>
                          <Input 
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            placeholder="0"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Unit Type</label>
                          <Select 
                            value={item.unit || 'ea'} 
                            onValueChange={(v) => updateLineItem(index, 'unit', v)}
                          >
                            <SelectTrigger className="text-sm h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sqft">per sqft</SelectItem>
                              <SelectItem value="lf">per linear ft</SelectItem>
                              <SelectItem value="ea">each</SelectItem>
                              <SelectItem value="hr">per hour</SelectItem>
                              <SelectItem value="day">per day</SelectItem>
                              <SelectItem value="lot">lump sum</SelectItem>
                              <SelectItem value="unit">per unit</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Price / {item.unit || 'unit'}</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                            <Input 
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                              placeholder="0.00"
                              className="text-sm pl-6"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Line Total</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                            <Input 
                              type="number"
                              step="0.01"
                              value={item.total}
                              onChange={(e) => updateLineItem(index, 'total', e.target.value)}
                              placeholder="0.00"
                              className="text-sm pl-6 font-medium bg-slate-100"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Grand Total */}
                  <div className="flex justify-end items-center pt-2 border-t mt-2">
                    <span className="text-sm font-medium text-slate-600 mr-3">Grand Total:</span>
                    <span className="text-lg font-bold text-slate-900">
                      ${parseFloat(editBidData.total_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea 
                className="mt-1.5"
                value={editBidData.notes}
                onChange={(e) => setEditBidData({...editBidData, notes: e.target.value})}
                rows={2}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBid(false)}>Cancel</Button>
            <Button onClick={handleSaveEditBid}>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Project *</Label><Select value={uploadProject} onValueChange={setUploadProject}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Type</Label><Select value={uploadCategory} onValueChange={setUploadCategory}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="plans">Plans</SelectItem><SelectItem value="contract">Contract</SelectItem><SelectItem value="w9">W-9</SelectItem></SelectContent></Select></div>
            <div><Label>File *</Label><div className="mt-1.5 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-500" onClick={() => docInputRef.current?.click()}><input ref={docInputRef} type="file" className="hidden" onChange={(e) => setUploadFile(e.target.files?.[0])} />{uploadFile ? <p className="text-blue-600">{uploadFile.name}</p> : <p className="text-slate-500">Click to select</p>}</div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contractor Portal Preview Dialog */}
      <Dialog open={showContractorPortal} onOpenChange={setShowContractorPortal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 -m-6 mb-4 p-6 rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center"><Gavel className="w-5 h-5 text-white" /></div>
                <div><h2 className="text-xl font-bold text-white">DrawReady</h2><Badge className="bg-orange-500/20 text-orange-200 border-0">Contractor Portal</Badge></div>
              </div>
            </div>
          </DialogHeader>
          
          <Alert className="bg-green-50 border-green-200 mb-4">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-green-700">This is what contractors see. They only see bid requests sent to them.</AlertDescription>
          </Alert>
          
          <h3 className="font-semibold mb-3">Sample Bid Request</h3>
          <Card className="border">
            <CardContent className="p-4">
              <Badge className="bg-blue-100 text-blue-700 mb-2">BID REQUEST</Badge>
              <h4 className="font-semibold text-lg">Framing & Structural Work</h4>
              <p className="text-sm text-slate-500 mb-2">123 Main St • Due: Jan 30, 2025</p>
              <p className="text-sm text-slate-600 mb-4">Complete framing for 2,400 sq ft home including floor, wall, and roof framing...</p>
              <div className="flex gap-2">
                <Button size="sm"><Send className="w-4 h-4 mr-2" /> Submit Bid</Button>
                <Button size="sm" variant="outline"><Download className="w-4 h-4 mr-2" /> Download Plans</Button>
              </div>
            </CardContent>
          </Card>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowContractorPortal(false)}>Close</Button>
            <Button onClick={() => window.open('/contractor-portal', '_blank')}><ExternalLink className="w-4 h-4 mr-2" /> Open Full Portal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Contractors Dialog */}
      <InviteContractors 
        userId={user?.id}
        companyName={user?.business_name || user?.name}
        isOpen={showInviteContractors}
        onClose={() => setShowInviteContractors(false)}
      />

      {/* Log Payment Dialog */}
      <Dialog open={showLogPayment} onOpenChange={setShowLogPayment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" /> Log Payment</DialogTitle>
            <DialogDescription>Record a payment to {selectedContractor?.company || selectedContractor?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  type="number" 
                  placeholder="0.00" 
                  className="pl-10"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date" 
                value={newPayment.payment_date}
                onChange={(e) => setNewPayment({...newPayment, payment_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={newPayment.payment_method} onValueChange={(v) => setNewPayment({...newPayment, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="ach">ACH/Bank Transfer</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Invoice #, project phase, etc."
                value={newPayment.notes}
                onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogPayment(false)}>Cancel</Button>
            <Button onClick={handleLogPayment} className="bg-green-600 hover:bg-green-700">
              <DollarSign className="w-4 h-4 mr-2" /> Log Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to More Contractors Modal */}
      <Dialog open={showSendToMoreModal} onOpenChange={setShowSendToMoreModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" /> Send to More Contractors
            </DialogTitle>
            <DialogDescription>
              {selectedBidRequestForSend?.title && (
                <span>Add contractors to: <strong>{selectedBidRequestForSend.title}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedBidRequestForSend && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span>Already sent to {selectedBidRequestForSend.contractor_ids?.length || 0} contractor(s)</span>
                </div>
              </div>
            )}
            
            <Label className="mb-2 block">Select additional contractors to send this bid request to:</Label>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
              {getAvailableContractorsForBid(selectedBidRequestForSend).length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>All your contractors have already received this bid request.</p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => { setShowSendToMoreModal(false); setActiveTab('contractors'); }}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add New Contractor
                  </Button>
                </div>
              ) : (
                getAvailableContractorsForBid(selectedBidRequestForSend).map(contractor => (
                  <label 
                    key={contractor.id} 
                    className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedContractorsToSend.includes(contractor.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContractorsToSend([...selectedContractorsToSend, contractor.id]);
                        } else {
                          setSelectedContractorsToSend(selectedContractorsToSend.filter(id => id !== contractor.id));
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{contractor.company || contractor.name}</p>
                      <p className="text-xs text-slate-500 truncate">{contractor.email}</p>
                    </div>
                    {contractor.trade && (
                      <Badge variant="outline" className="text-xs shrink-0">{contractor.trade}</Badge>
                    )}
                  </label>
                ))
              )}
            </div>
            
            {selectedContractorsToSend.length > 0 && (
              <div className="mt-3 text-sm text-blue-600 font-medium">
                {selectedContractorsToSend.length} contractor(s) selected
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendToMoreModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSendToMoreContractors} 
              disabled={selectedContractorsToSend.length === 0 || isSendingToMore}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSendingToMore ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send to {selectedContractorsToSend.length} Contractor(s)</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Bid Invite Dialog */}
      <Dialog open={showQuickBidInvite} onOpenChange={setShowQuickBidInvite}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Quick Bid Invite
            </DialogTitle>
            <DialogDescription>
              Invite a contractor to upload their existing bid document. They'll receive an email with a direct link to upload.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quick-project">Project *</Label>
              <Select 
                value={quickInviteData.project_id} 
                onValueChange={(value) => setQuickInviteData({...quickInviteData, project_id: value})}
              >
                <SelectTrigger id="quick-project" data-testid="quick-invite-project-select">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-contractor">Contractor *</Label>
              <Select 
                value={quickInviteData.contractor_id} 
                onValueChange={(value) => setQuickInviteData({...quickInviteData, contractor_id: value})}
              >
                <SelectTrigger id="quick-contractor" data-testid="quick-invite-contractor-select">
                  <SelectValue placeholder="Select a contractor" />
                </SelectTrigger>
                <SelectContent>
                  {contractors.filter(c => c.email).map(contractor => (
                    <SelectItem key={contractor.id} value={contractor.id}>
                      <div className="flex items-center gap-2">
                        <span>{contractor.company || contractor.name}</span>
                        <span className="text-xs text-slate-500">({contractor.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contractors.filter(c => !c.email).length > 0 && (
                <p className="text-xs text-orange-600">
                  Note: {contractors.filter(c => !c.email).length} contractor(s) without email are hidden
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-trade">Trade (Optional)</Label>
              <Select 
                value={quickInviteData.trade_type} 
                onValueChange={(value) => setQuickInviteData({...quickInviteData, trade_type: value === 'none' ? '' : value})}
              >
                <SelectTrigger id="quick-trade" data-testid="quick-invite-trade-select">
                  <SelectValue placeholder="Select trade type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific trade</SelectItem>
                  {trades.filter(t => t !== 'All Trades').map(trade => (
                    <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quick-message">Custom Message (Optional)</Label>
              <Textarea
                id="quick-message"
                data-testid="quick-invite-message"
                placeholder="Add a personal note to the contractor..."
                value={quickInviteData.message}
                onChange={(e) => setQuickInviteData({...quickInviteData, message: e.target.value})}
                rows={3}
              />
              <p className="text-xs text-slate-500">
                This message will be included in the invite email
              </p>
              
              {/* Quick Message Templates */}
              <div className="pt-2">
                <Label className="text-xs text-slate-500">Quick Templates</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setQuickInviteData({...quickInviteData, message: 'Please provide your best price for this work. We need both a labor-only quote and a quote with materials included.'})}
                  >
                    Price Options
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setQuickInviteData({...quickInviteData, message: 'This is time-sensitive. Please submit your bid within the next 48 hours if possible.'})}
                  >
                    Urgent Request
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setQuickInviteData({...quickInviteData, message: 'Please include a detailed breakdown of line items in your bid so we can compare accurately.'})}
                  >
                    Detailed Breakdown
                  </Button>
                </div>
              </div>
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <AlertDescription className="text-sm">
                The contractor will receive an email with a link to upload their bid document (PDF, Excel, or Image). 
                Our AI will automatically extract the bid details for easy review.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickBidInvite(false)}>Cancel</Button>
            <Button 
              onClick={handleSendQuickBidInvite}
              disabled={!quickInviteData.project_id || !quickInviteData.contractor_id || isSendingQuickInvite}
              data-testid="quick-invite-send-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSendingQuickInvite ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scope Template Manager Modal */}
      <Dialog open={showScopeTemplateManager} onOpenChange={setShowScopeTemplateManager}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Scope of Work Templates
            </DialogTitle>
            <DialogDescription>
              Save and manage reusable scope of work templates for your bid requests
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            {/* Left: Template List */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-700 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Templates ({scopeTemplates.length})
              </h3>
              
              {scopeTemplates.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No templates yet</p>
                  <p className="text-slate-400 text-xs mt-1">Create your first scope template</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {scopeTemplates.map(template => (
                    <div 
                      key={template.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        editingScopeTemplate?.id === template.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        // Built-in templates can't be edited in place — load into the
                        // form as a NEW template so "Save" creates a personal copy.
                        setEditingScopeTemplate(template.system ? null : template);
                        setNewScopeTemplate({
                          name: template.system ? `${template.name} (Copy)` : template.name,
                          trade_type: template.trade_type,
                          scope_text: template.scope_text,
                          description: template.description || ''
                        });
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800 truncate">{template.name}</p>
                            {template.system && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-600 flex-shrink-0">
                                Built-in
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-blue-600 mt-0.5">{template.trade_type}</p>
                          {template.description && (
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>
                          )}
                        </div>
                        {!template.system && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                            data-testid={`delete-scope-template-${template.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete template "${template.name}"?`)) {
                                try {
                                  await apiService.deleteScopeTemplate(template.id, user.id);
                                  setScopeTemplates(prev => prev.filter(t => t.id !== template.id));
                                  if (editingScopeTemplate?.id === template.id) {
                                    setEditingScopeTemplate(null);
                                    setNewScopeTemplate({ name: '', trade_type: '', scope_text: '', description: '' });
                                  }
                                  toast.success('Template deleted');
                                } catch (error) {
                                  toast.error('Failed to delete template');
                                }
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setEditingScopeTemplate(null);
                  setNewScopeTemplate({ name: '', trade_type: '', scope_text: '', description: '' });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>
            
            {/* Right: Edit/Create Form */}
            <div className="space-y-4 border-l pl-6">
              <h3 className="font-medium text-slate-700">
                {editingScopeTemplate ? 'Edit Template' : 'Create New Template'}
              </h3>
              
              <div className="space-y-3">
                <div>
                  <Label>Template Name *</Label>
                  <Input
                    className="mt-1"
                    placeholder="e.g., Gutter Installation - Standard"
                    value={newScopeTemplate.name}
                    onChange={(e) => setNewScopeTemplate(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label>Trade Type *</Label>
                  <Select 
                    value={newScopeTemplate.trade_type} 
                    onValueChange={(v) => setNewScopeTemplate(prev => ({ ...prev, trade_type: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select trade type" />
                    </SelectTrigger>
                    <SelectContent>
                      {trades.filter(t => t !== 'All Trades').map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Brief Description</Label>
                  <Input
                    className="mt-1"
                    placeholder="Quick reference note (optional)"
                    value={newScopeTemplate.description}
                    onChange={(e) => setNewScopeTemplate(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label>Scope of Work Text *</Label>
                  <Textarea
                    className="mt-1 min-h-[200px] font-mono text-sm"
                    placeholder="Enter the full scope of work text..."
                    value={newScopeTemplate.scope_text}
                    onChange={(e) => setNewScopeTemplate(prev => ({ ...prev, scope_text: e.target.value }))}
                  />
                </div>
                
                <Button
                  className="w-full"
                  disabled={!newScopeTemplate.name || !newScopeTemplate.trade_type || !newScopeTemplate.scope_text || isSavingScopeTemplate}
                  onClick={async () => {
                    try {
                      setIsSavingScopeTemplate(true);
                      
                      if (editingScopeTemplate) {
                        // Update existing
                        const res = await apiService.updateScopeTemplate(editingScopeTemplate.id, user.id, newScopeTemplate);
                        setScopeTemplates(prev => prev.map(t => 
                          t.id === editingScopeTemplate.id ? res.data.template : t
                        ));
                        toast.success('Template updated');
                      } else {
                        // Create new
                        const res = await apiService.createScopeTemplate(user.id, newScopeTemplate);
                        setScopeTemplates(prev => [...prev, res.data.template]);
                        toast.success('Template created');
                      }
                      
                      setEditingScopeTemplate(null);
                      setNewScopeTemplate({ name: '', trade_type: '', scope_text: '', description: '' });
                    } catch (error) {
                      console.error('Failed to save template:', error);
                      toast.error('Failed to save template');
                    } finally {
                      setIsSavingScopeTemplate(false);
                    }
                  }}
                >
                  {isSavingScopeTemplate ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {editingScopeTemplate ? 'Update Template' : 'Save Template'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contractor Compliance Document Upload (W-9 / COI) */}
      <Dialog open={showDocUploadDialog} onOpenChange={(open) => { setShowDocUploadDialog(open); if (!open) { setDocUploadFile(null); setDocCoiExpiration(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {docUploadType === 'coi' ? <Shield className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
              Upload {docUploadType === 'coi' ? 'Certificate of Insurance (COI)' : 'W-9'}
            </DialogTitle>
            <DialogDescription>
              {selectedContractor ? `For ${selectedContractor.company || selectedContractor.name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Document File *</Label>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="mt-1"
                data-testid="doc-upload-file-input"
                onChange={(e) => setDocUploadFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-slate-400 mt-1">PDF or image, max 5MB (W-9) / 10MB (COI).</p>
            </div>
            {docUploadType === 'coi' && (
              <div>
                <Label>Expiration Date *</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={docCoiExpiration}
                  data-testid="doc-upload-coi-expiration"
                  onChange={(e) => setDocCoiExpiration(e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-1">Required so we can warn you before insurance lapses.</p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDocUploadDialog(false)} disabled={isUploadingDoc}>
              Cancel
            </Button>
            <Button
              onClick={handleDocUpload}
              disabled={!docUploadFile || (docUploadType === 'coi' && !docCoiExpiration) || isUploadingDoc}
              data-testid="doc-upload-submit-button"
            >
              {isUploadingDoc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Import to Bid Sheet Modal */}
      <Dialog open={showImportBidSheetModal} onOpenChange={setShowImportBidSheetModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import to Bid Sheet
            </DialogTitle>
            <DialogDescription>
              Choose how to import this bid
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Import Destination */}
            <div className="space-y-2">
              <Label>Import Destination</Label>
              <Select value={selectedBidSheetId} onValueChange={setSelectedBidSheetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Create New Bid Sheet
                    </div>
                  </SelectItem>
                  {existingBidSheets.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs text-slate-500 border-t mt-1 pt-1">
                        Existing Bid Sheets
                      </div>
                      {existingBidSheets.map(sheet => (
                        <SelectItem key={sheet.id} value={sheet.id}>
                          {sheet.name || 'Untitled Bid Sheet'}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Category Selection */}
            <div className="space-y-2">
              <Label>Category</Label>
              {showCustomCategoryInput ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter custom category name"
                    value={customCategory}
                    onChange={(e) => {
                      setCustomCategory(e.target.value);
                      setImportCategory(e.target.value);
                    }}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowCustomCategoryInput(false);
                      setCustomCategory('');
                      setImportCategory('Site Work');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Select value={importCategory} onValueChange={(val) => {
                  if (val === 'custom') {
                    setShowCustomCategoryInput(true);
                    setCustomCategory(importBidRequest?.trade_type || '');
                    setImportCategory(importBidRequest?.trade_type || '');
                    setImportSubcategory('');
                  } else {
                    setImportCategory(val);
                    // Reset subcategory when category changes, but try to preserve if it exists in new category
                    const newSubcats = categorySubcategories[val] || [];
                    if (!newSubcats.includes(importSubcategory)) {
                      setImportSubcategory(newSubcats[0] || '');
                    }
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Site Work">Site Work</SelectItem>
                    <SelectItem value="Foundation">Foundation</SelectItem>
                    <SelectItem value="Concrete & Slab">Concrete & Slab</SelectItem>
                    <SelectItem value="Framing">Framing</SelectItem>
                    <SelectItem value="Rough-Ins">Rough-Ins (Electrical, Plumbing, HVAC)</SelectItem>
                    <SelectItem value="Insulation & Drywall">Insulation & Drywall</SelectItem>
                    <SelectItem value="Exterior">Exterior</SelectItem>
                    <SelectItem value="Interior Finishes">Interior Finishes</SelectItem>
                    <SelectItem value="Final & Cleanup">Final & Cleanup</SelectItem>
                    <SelectItem value="Flooring">Flooring</SelectItem>
                    <SelectItem value="Painting">Painting</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <div className="border-t my-1" />
                    <SelectItem value="custom">
                      <div className="flex items-center gap-2 text-blue-600">
                        <Plus className="w-4 h-4" />
                        Add Custom Category
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Subcategory Selection - Show subcategories based on selected category */}
            {importCategory && categorySubcategories[importCategory] && (
              <div className="space-y-2">
                <Label>Subcategory</Label>
                <Select value={importSubcategory} onValueChange={setImportSubcategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {categorySubcategories[importCategory].map((subcat) => (
                      <SelectItem key={subcat} value={subcat}>{subcat}</SelectItem>
                    ))}
                    <div className="border-t my-1" />
                    <SelectItem value={importBidRequest?.trade_type || 'Custom'}>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Plus className="w-4 h-4" />
                        Use "{importBidRequest?.trade_type || 'Custom'}" as new subcategory
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Choose where to place this item within the category</p>
              </div>
            )}
            
            {/* Import Method Selection - Show only if there are line items */}
            {importLineItems.length > 0 && (
              <div className="space-y-2">
                <Label>Import Method</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setImportMethod('combined')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      importMethod === 'combined'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold text-sm">Combined</div>
                    <div className="text-xs text-slate-500">Single line item</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportMethod('separate')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      importMethod === 'separate'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold text-sm">Separate</div>
                    <div className="text-xs text-slate-500">{importLineItems.length} line items</div>
                  </button>
                </div>
              </div>
            )}
            
            {/* Cost Type - For Combined import */}
            {importMethod === 'combined' && (
              <div className="space-y-2">
                <Label>Cost Type</Label>
                <Select value={importItemType} onValueChange={setImportItemType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labor">Labor</SelectItem>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="labor_and_materials">Labor & Materials</SelectItem>
                    <SelectItem value="soft_costs">Soft Costs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Line Items Preview - For Separate import */}
            {importMethod === 'separate' && importLineItems.length > 0 && (
              <div className="space-y-2">
                <Label>Line Items (click to change type)</Label>
                <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                  {importLineItems.map((item, idx) => (
                    <div key={item.id} className="p-2 flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{item.description}</div>
                        <div className="text-xs text-slate-500">${parseFloat(item.amount).toLocaleString()}</div>
                      </div>
                      <Select 
                        value={item.type} 
                        onValueChange={(val) => {
                          const updated = [...importLineItems];
                          updated[idx].type = val;
                          setImportLineItems(updated);
                        }}
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="labor">Labor</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="soft_costs">Soft Costs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Bid Info Summary */}
            {importBidRequest && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <div className="font-medium">{importBidRequest.title}</div>
                <div className="text-slate-500">Trade: {importBidRequest.trade_type}</div>
                {importBidRequest.responses?.[0]?.total_amount && (
                  <div className="text-green-600 font-semibold mt-1">
                    Amount: ${parseFloat(importBidRequest.responses[0].total_amount).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowImportBidSheetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmImport}>
              {selectedBidSheetId === 'new' ? 'Create New Bid Sheet' : 'Import to Selected'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Bid Modal */}
      <Dialog open={showAwardModal} onOpenChange={setShowAwardModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Award Bid
            </DialogTitle>
            <DialogDescription>
              Select the winning bid and upload the contract for e-signature
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Bid Request Info */}
            {awardingBidRequest && (
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <div className="font-semibold text-purple-700">{awardingBidRequest.title}</div>
                <div className="text-sm text-purple-600">{awardingBidRequest.trade_type}</div>
              </div>
            )}
            
            {/* Select Winning Bid */}
            <div className="space-y-2">
              <Label>Select Winning Contractor</Label>
              {awardingBidRequest?.responses?.length > 0 ? (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {awardingBidRequest.responses.map(response => (
                    <div
                      key={response.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedWinningResponse?.id === response.id
                          ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                      onClick={() => setSelectedWinningResponse(response)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{response.contractor_name || 'Unknown Contractor'}</div>
                          <div className="text-sm text-slate-500">{response.contractor_email}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            ${parseFloat(response.total_amount || 0).toLocaleString()}
                          </div>
                          {selectedWinningResponse?.id === response.id && (
                            <span className="text-xl">🏆</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-slate-500">
                  No bids received yet
                </div>
              )}
            </div>
            
            {/* Upload Contract */}
            <div className="space-y-2">
              <Label>Upload Contract (for e-signature)</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  id="contract-upload"
                  onChange={(e) => setAwardContract(e.target.files?.[0] || null)}
                />
                <label htmlFor="contract-upload" className="cursor-pointer">
                  {awardContract ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <FileText className="w-5 h-5" />
                      <span>{awardContract.name}</span>
                      <button 
                        onClick={(e) => { e.preventDefault(); setAwardContract(null); }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p>Click to upload contract (PDF, DOC)</p>
                      <p className="text-xs text-slate-400 mt-1">Contractor will sign electronically</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            {/* What happens next */}
            <div className="bg-blue-50 p-3 rounded-lg text-sm">
              <div className="font-medium text-blue-700 mb-2">What happens next:</div>
              <ul className="text-blue-600 space-y-1">
                <li>• Contractor receives award notification email</li>
                <li>• Contractor signs contract electronically</li>
                <li>• Contractor uploads W9 and COI</li>
                <li>• All documents saved to project Documents tab</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAwardModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAwardBid} 
              disabled={!selectedWinningResponse || isAwarding}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isAwarding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Awarding...
                </>
              ) : (
                <>
                  <Award className="w-4 h-4 mr-2" />
                  Award Bid & Notify
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue / Reissue Contract Modal */}
      <Dialog open={!!issueContractBid} onOpenChange={(open) => !open && setIssueContractBid(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="issue-contract-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {projectContracts.length > 0 ? 'Reissue Contract' : 'Issue Contract'}
            </DialogTitle>
            <DialogDescription>
              {issueContractBid?.awarded_response?.contractor_name
                ? <>Generate a fresh Contract Summary PDF for <strong>{issueContractBid.awarded_response.contractor_name}</strong> on <strong>{issueContractBid?.title}</strong>.</>
                : 'Generate a fresh Contract Summary PDF for this awarded bid.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {projectContracts.length > 0 && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs" data-testid="contracts-history">
                <p className="font-semibold text-slate-800 mb-2">
                  {projectContracts.length} contract{projectContracts.length === 1 ? '' : 's'} for this contractor
                </p>
                <ul className="space-y-1.5">
                  {projectContracts.slice(0, 5).map((c) => {
                    const status = c.status || 'issued';
                    const badge = (() => {
                      if (status === 'fully_executed') return { label: 'Fully executed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
                      if (status === 'signed_by_contractor') return { label: 'Awaiting countersign', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
                      return { label: 'Issued · awaiting contractor', cls: 'bg-blue-100 text-blue-800 border-blue-200' };
                    })();
                    return (
                      <li key={c.id} className="flex items-center justify-between gap-2 bg-white px-2.5 py-1.5 rounded border border-slate-200">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 truncate">
                            ${parseFloat(c.contract_amount || 0).toLocaleString()} · {formatDate(c.created_at)}
                          </p>
                          <span className={`inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${badge.cls}`} data-testid={`history-status-${c.id}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {status === 'signed_by_contractor' && (
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => setCountersignContract(c)}
                              data-testid={`countersign-${c.id}`}
                            >
                              Countersign
                            </Button>
                          )}
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/contracts/${c.id}/pdf?user_id=${encodeURIComponent(user?.id || '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 px-2 py-1 rounded bg-blue-50 hover:bg-blue-100"
                            data-testid={`history-pdf-${c.id}`}
                          >
                            PDF
                          </a>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 text-slate-500">A new version will be added — older versions stay accessible in the portal.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contract Amount</Label>
                <Input
                  type="number"
                  value={issueContractForm.contract_amount}
                  onChange={(e) => setIssueContractForm({...issueContractForm, contract_amount: e.target.value})}
                  placeholder="20400"
                  data-testid="issue-contract-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={issueContractForm.start_date}
                  onChange={(e) => setIssueContractForm({...issueContractForm, start_date: e.target.value})}
                  data-testid="issue-contract-start"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={issueContractForm.end_date}
                onChange={(e) => setIssueContractForm({...issueContractForm, end_date: e.target.value})}
                data-testid="issue-contract-end"
              />
            </div>

            <div>
              <Label className="text-xs">Scope of Work</Label>
              <Textarea
                rows={3}
                value={issueContractForm.scope_of_work}
                onChange={(e) => setIssueContractForm({...issueContractForm, scope_of_work: e.target.value})}
                placeholder="Describe the awarded scope..."
                data-testid="issue-contract-scope"
              />
            </div>

            <div className="space-y-2 pt-2 border-t border-slate-200">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={issueContractForm.include_rules}
                  onChange={(e) => setIssueContractForm({...issueContractForm, include_rules: e.target.checked})}
                  className="rounded"
                  data-testid="issue-contract-include-rules"
                />
                <span className="text-slate-700">Include Job Site Rules as page 2 of the PDF</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={issueContractForm.send_email}
                  onChange={(e) => setIssueContractForm({...issueContractForm, send_email: e.target.checked})}
                  className="rounded"
                  data-testid="issue-contract-send-email"
                />
                <span className="text-slate-700">Email PDF to contractor (otherwise: just save to their portal)</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueContractBid(null)} disabled={issueContractLoading}>
              Cancel
            </Button>
            <Button onClick={handleIssueContract} disabled={issueContractLoading} data-testid="issue-contract-submit">
              {issueContractLoading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                : <><FileText className="w-4 h-4 mr-2" />{projectContracts.length > 0 ? 'Reissue' : 'Issue'} Contract</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner Countersign Dialog */}
      <SignContractDialog
        open={!!countersignContract}
        contract={countersignContract}
        mode="owner"
        userId={user?.id}
        defaultName={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.name || ''}
        onClose={() => setCountersignContract(null)}
        onSigned={() => {
          setCountersignContract(null);
          // Refresh the contract list for the open issue modal
          if (issueContractBid) {
            api.get(`/projects/${issueContractBid.project_id}/contracts?contractor_id=${issueContractBid.awarded_contractor_id}`)
              .then(r => setProjectContracts(r.data || []))
              .catch(() => {});
          }
        }}
      />

      {/* v2.5: New 2-step bid project modal (Project Info → Scopes → Publish).
          Feature-flagged to owner email until rollout. */}
      <NewBidProjectModal
        isOpen={showNewBidProjectModal}
        onClose={() => setShowNewBidProjectModal(false)}
        userId={user?.id}
        contractors={contractors || []}
        onPublished={() => {
          setShowNewBidProjectModal(false);
          // Refresh bid requests so the published scopes show up immediately
          if (typeof loadData === 'function') loadData();
        }}
      />

      {/* v2.5.5 — Contractor bid preview ("what your contractor sees") */}
      <ContractorBidPreviewModal
        open={!!previewBidId}
        bidRequestId={previewBidId}
        userId={user?.id}
        onClose={() => setPreviewBidId(null)}
      />

      {/* 9-step "How getting funded works" popup — shown when a non-funded user
          is routed here from the dashboard (?intro=bidding). */}
      {showBiddingModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
          onClick={() => setShowBiddingModal(false)}
          data-testid="bidding-steps-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="bidding-steps-modal"
          >
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                <Gavel className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-bold text-slate-900 flex-1">How getting funded works</h3>
              <button onClick={() => setShowBiddingModal(false)} className="text-slate-400 hover:text-slate-700" data-testid="bidding-steps-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">Your project isn&apos;t funded yet — here&apos;s the path from bid to build:</p>
              <ol className="space-y-2">
                {[
                  'Create project for bid',
                  'Add scopes for bid',
                  'Send to contractors',
                  'Import bids to bid sheet',
                  'Send to bank',
                  'Get funded / project approval',
                  'Award contractors',
                  'Build schedule',
                  'Start project',
                ].map((label, i) => (
                  <li key={i} className="flex items-center gap-3" data-testid={`bidding-step-${i + 1}`}>
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-extrabold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="text-sm text-slate-700">{label}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => { setShowBiddingModal(false); openCreateBidFlow(); }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl"
                data-testid="bidding-steps-cta"
              >
                Got it — create project for bid <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Award Modal */}
      <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Revoke Award
            </DialogTitle>
            <DialogDescription>
              This will remove the award from {revokingBidRequest?.awarded_response?.contractor_name || 'the contractor'} and reopen the bid request.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Note:</strong> The contractor will be notified via email that the award has been revoked.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                placeholder="Explain why the award is being revoked..."
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRevokeModal(false)}>Cancel</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleRevokeAward}
              disabled={isRevoking}
            >
              {isRevoking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Revoking...</> : 'Revoke Award'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Award Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              Transfer Award
            </DialogTitle>
            <DialogDescription>
              Select a different contractor to award this bid to. Both contractors will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Current Award:</strong> {revokingBidRequest?.awarded_response?.contractor_name} - ${parseFloat(revokingBidRequest?.awarded_response?.total_amount || 0).toLocaleString()}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Select New Contractor</Label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {revokingBidRequest?.responses?.map((response) => (
                  <div 
                    key={response.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      transferTargetResponse?.id === response.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setTransferTargetResponse(response)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold">{response.contractor_name || response.contractor?.company || 'Contractor'}</div>
                        <div className="text-sm text-slate-500">{response.contractor?.trade || 'Unknown trade'}</div>
                      </div>
                      <div className="text-lg font-semibold text-green-600">
                        ${parseFloat(response.total_amount || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            <Button 
              onClick={handleTransferAward}
              disabled={isTransferring || !transferTargetResponse}
            >
              {isTransferring ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transferring...</> : 'Transfer Award'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Contractor Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Message {messagingContractor?.company || messagingContractor?.name || 'Contractor'}
            </DialogTitle>
            {messagingBidRequest && (
              <DialogDescription>
                Regarding: {messagingBidRequest.title}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 min-h-[200px] max-h-[300px] p-2">
            {messageThread.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messageThread.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`p-3 rounded-lg max-w-[85%] ${
                    msg.sender_type === 'admin' 
                      ? 'bg-blue-100 ml-auto' 
                      : 'bg-slate-100'
                  }`}
                >
                  <div className="text-xs text-slate-500 mb-1">
                    {msg.sender_name} • {new Date(msg.created_at).toLocaleString()}
                  </div>
                  <p className="text-sm">{msg.message}</p>
                </div>
              ))
            )}
          </div>
          
          <div className="border-t pt-4 space-y-2">
            <Textarea
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={3}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-slate-500">Press Enter to send, Shift+Enter for new line</p>
              <Button 
                onClick={handleSendMessage}
                disabled={isSendingMessage || !messageText.trim()}
              >
                {isSendingMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </div>{/* End of main content area */}
    </div>
  );
}

export default ProjectHub;
